// PhotoGallery3D: 3D mixed-media gallery (images, local videos, YouTube)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchJSON, assetUrl } from '../../utils/api';
import AnimatedTerminalShell from '../common/AnimatedTerminalShell';
import MerchandiseCartModal from './MerchandiseCartModal';
import GalleryObjectViewer from './GalleryObjectViewer';
import { addItemToCart, cartCount, loadMerchCart, saveMerchCart } from '../../utils/merchCart';
import {
  getDebotConfigs,
  getDefaulterTerminalFaqs,
  subscribeDebotConfigs,
  subscribeDefaulterTerminalFaqs,
} from '../../utils/debots';

const TEXT_CARD_FONT_REDUCTION = 0.7;
const TEXT_CARD_SIDE_PADDING = 10;
const TEXT_CARD_TEXT_GUTTER = TEXT_CARD_SIDE_PADDING * 2;
const DISCOVER_TEXT_CARD_PADDING_LEFT = 60;
const DISCOVER_TEXT_CARD_PADDING_RIGHT = 60;
const SQUARE_CONTENT_FONT_FAMILY_MAP = {
  system: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  sans: '"Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: '"Courier New", "Lucida Console", monospace',
  rounded: '"Trebuchet MS", Verdana, sans-serif',
  classic: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
  display: 'Impact, "Arial Black", sans-serif',
  script: '"Comic Sans MS", "Segoe Print", cursive',
  philosopher: 'Philosopher, "Palatino Linotype", "Book Antiqua", Palatino, serif',
};

const initialOrderForm = {
  customerFirstName: '',
  customerLastName: '',
  customerEmail: '',
  customerPhone: '',
  deliveryAddress: '',
  deliveryPostcode: '',
  deliveryCity: '',
  deliveryCountry: '',
  requestedSize: '',
  quantity: '1',
  message: '',
};

const MAX_GALLERY_TERMINAL_LINES = 220;
const THRUSTER_TRANSITION_MS = 2000;
const TERMINAL_TYPEWRITER_CHARS_PER_SEC = 72;
const FOCUSED_INFO_HIDE_TRANSITION_MS = 2000;
const FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS = 200;
const FOCUSED_INFO_SHOW_WIDTH_GROW_MS = 200;
const FOCUSED_INFO_SHOW_HEIGHT_UNFOLD_MS = 500;
const FOCUSED_INFO_SHOW_PRE_TYPE_MS = FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS
  + FOCUSED_INFO_SHOW_WIDTH_GROW_MS
  + FOCUSED_INFO_SHOW_HEIGHT_UNFOLD_MS;
const FOCUSED_INFO_TERMINAL_FLASH_MS = FOCUSED_INFO_SHOW_PRE_TYPE_MS;
const FOCUSED_INFO_INTRO_GLOW_COLOR = new THREE.Color('#ffffff');
const GALLERY_TERMINAL_BUTTON_LEFT = 28;
const GALLERY_TERMINAL_BUTTON_BOTTOM = 22;
const GALLERY_TERMINAL_BUTTON_HEIGHT = 62;
const GALLERY_TERMINAL_PANEL_LEFT = 28;
const GALLERY_TERMINAL_PANEL_WIDTH = 560;
const GALLERY_TERMINAL_PANEL_HEIGHT = 500;

function formatTerminalTimestamp(ts) {
  return new Date(ts).toLocaleString('nb-NO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function createGalleryTerminalFallbackAnswer(question, bot, faqOptions = []) {
  if (!bot) return 'No bot selected yet. Open DEBOTS admin in Discover and configure bots.';

  if (!bot.online) {
    return `${bot.name} is OFFLINE now. Turn ONLINE on to receive FAQ answers.`;
  }

  const normalized = String(question || '').trim().toLowerCase();
  const sourceFaqs = Array.isArray(faqOptions) && faqOptions.length
    ? faqOptions
    : (Array.isArray(bot.faqs) ? bot.faqs : []);
  const matchedFaq = sourceFaqs.find((faq) => {
    const faqQuestion = String(faq?.question || '').trim().toLowerCase();
    if (!faqQuestion) return false;
    return normalized === faqQuestion || normalized.includes(faqQuestion) || faqQuestion.includes(normalized);
  });

  if (matchedFaq?.answer) return matchedFaq.answer;

  const shortDescription = bot.description || 'This bot is configured for gallery guidance.';
  return `${bot.name}: ${shortDescription} No direct FAQ hit found, but this is a strong next step.`;
}

function formatNok(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${n.toLocaleString('nb-NO')} NOK`;
}

function primaryImageUrl(item) {
  return item.optimizedUrl || item.url || item.originalUrl || item.thumbUrl || undefined;
}

function galleryImageStatusKey(item, index = 0) {
  const raw = item?._id
    || item?.id
    || item?.storageKey
    || item?.storageUrl
    || item?.thumbUrl
    || item?.optimizedUrl
    || item?.url
    || item?.originalUrl;
  if (raw) return String(raw);
  return `image-${Math.max(0, Math.round(Number(index) || 0))}`;
}

function isLineBreakItem(item) {
  if (item?.kind === 'linebreak') return true;
  const hasImageSource = !!(item?.thumbUrl || item?.optimizedUrl || item?.url || item?.originalUrl);
  const hasBoxPayload = !!item?.box;
  return !hasImageSource && !hasBoxPayload;
}

function isTextBoxGalleryItem(entry) {
  const isSquareCanvas = !!entry?.box?.square;
  return !!entry
    && (entry.kind === 'box' || (entry.kind === 'image' && !entry.url && entry.box))
    && !isSquareCanvas;
}

function isFogClearableGalleryItem(item) {
  if (!item) return false;
  if (item.kind === 'video') return true;
  if (item.kind === 'box') return true;
  if (item.kind === 'image') return true;
  return false;
}

function normalizeVariantGroups(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map((group) => ({
      title: String(group?.title || '').trim(),
      options: Array.isArray(group?.options)
        ? group.options.map((value) => String(value || '').trim()).filter(Boolean)
        : [],
    }))
    .filter((group) => group.title && group.options.length);
}

function deriveRequestedSizeFromVariants(selectedVariants) {
  const rows = Array.isArray(selectedVariants) ? selectedVariants : [];
  const sizeRow = rows.find((entry) => /size|stoerrelse|størrelse/i.test(String(entry?.title || '')));
  return sizeRow ? String(sizeRow.value || '').trim().toUpperCase() : '';
}

function toSafeNonNegativeInteger(value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return Math.max(0, Math.round(Number(fallback) || 0));
  return Math.max(0, Math.round(numeric));
}

function normalizeHexColor(value, fallback = '#000000') {
  const raw = String(value || '').trim();
  const match = raw.match(/^#([0-9a-f]{6})$/i);
  return match ? `#${match[1].toLowerCase()}` : fallback;
}

function clampRange(value, fallback, min, max) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
}

function clampOpacity(value, fallback = 1) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeGalleryItemZOffset(value, fallback = 0) {
  return clampRange(value, fallback, -20, 20);
}

function normalizeFogVelocity(value, fallback = 35) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return clampRange(fallback, 35, 0, 1000);
  const scaled = numeric > 0 && numeric <= 1 ? numeric * 1000 : numeric;
  return clampRange(scaled, 35, 0, 1000);
}

function hexToRgbTriplet(value, fallback = '0,0,0') {
  const hex = normalizeHexColor(value, '#000000');
  if (!hex) return fallback;
  const stripped = hex.slice(1);
  const r = parseInt(stripped.slice(0, 2), 16);
  const g = parseInt(stripped.slice(2, 4), 16);
  const b = parseInt(stripped.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

function getHexRelativeLuminance(value, fallback = 0.5) {
  const hex = normalizeHexColor(value, '');
  if (!hex) return fallback;
  const stripped = hex.slice(1);
  const toLinear = (channel) => {
    const normalized = channel / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  const r = toLinear(parseInt(stripped.slice(0, 2), 16));
  const g = toLinear(parseInt(stripped.slice(2, 4), 16));
  const b = toLinear(parseInt(stripped.slice(4, 6), 16));
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
}

function normalizeFogConfig(source = {}) {
  return {
    fogEnabled: !!source.fogEnabled,
    fogOpacity: clampOpacity(source.fogOpacity, 0.42),
    fogVelocity: normalizeFogVelocity(source.fogVelocity, 35),
    fogDensity: clampRange(source.fogDensity, 1, 0, 2),
    fogColorPrimary: normalizeHexColor(source.fogColorPrimary, '#9bc4de'),
    fogColorSecondary: normalizeHexColor(source.fogColorSecondary, '#8aaec7'),
  };
}

function normalizeCategoryLightType(value, fallback = 'directional') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'directional' || raw === 'point' || raw === 'spot' || raw === 'hemisphere'
    ? raw
    : fallback;
}

function normalizeCategoryLightSettings(value = {}, fallback = {}) {
  const source = value && typeof value === 'object' ? value : {};
  const fallbackSource = fallback && typeof fallback === 'object' ? fallback : {};
  const fallbackType = normalizeCategoryLightType(fallbackSource.type, 'directional');

  return {
    type: normalizeCategoryLightType(source.type, fallbackType),
    color: normalizeHexColor(source.color, normalizeHexColor(fallbackSource.color, '#ffffff')),
    intensity: clampRange(source.intensity, clampRange(fallbackSource.intensity, 1, 0, 3), 0, 3),
    directionalAzimuth: clampRange(source.directionalAzimuth, clampRange(fallbackSource.directionalAzimuth, 35, 0, 360), 0, 360),
    directionalElevation: clampRange(source.directionalElevation, clampRange(fallbackSource.directionalElevation, 45, -20, 80), -20, 80),
    pointDistance: clampRange(source.pointDistance, clampRange(fallbackSource.pointDistance, 30, 1, 80), 1, 80),
    pointDecay: clampRange(source.pointDecay, clampRange(fallbackSource.pointDecay, 1.2, 0, 4), 0, 4),
    spotAngle: clampRange(source.spotAngle, clampRange(fallbackSource.spotAngle, 35, 8, 85), 8, 85),
    spotPenumbra: clampRange(source.spotPenumbra, clampRange(fallbackSource.spotPenumbra, 0.35, 0, 1), 0, 1),
    hemisphereGroundMix: clampRange(source.hemisphereGroundMix, clampRange(fallbackSource.hemisphereGroundMix, 0.65, 0, 1), 0, 1),
    hemisphereHeight: clampRange(source.hemisphereHeight, clampRange(fallbackSource.hemisphereHeight, 0.25, -1, 1), -1, 1),
  };
}

function normalizeCategoryGradientType(value, fallback = 'linear') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'linear' || raw === 'radial' || raw === 'conic' ? raw : fallback;
}

function normalizeCategoryBgColors(value, fallback = ['#000000', '#111111']) {
  const fallbackSource = Array.isArray(fallback) ? fallback : ['#000000', '#111111'];
  const fallbackColors = fallbackSource
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean);
  const safeFallback = fallbackColors.length >= 2
    ? fallbackColors
    : ['#000000', '#111111'];
  const source = Array.isArray(value) ? value : [];
  const normalized = source
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  return normalized.length >= 2 ? normalized : safeFallback;
}

function buildCategoryBackgroundCss(source = {}) {
  const bgColors = normalizeCategoryBgColors(source.bgColors, [source.bg1, source.bg2]);
  const blended = bgColors.map((entry) => applyBgBlendMode(entry, source.bgBlendMode));
  const gradientType = normalizeCategoryGradientType(source.bgGradientType, 'linear');
  if (gradientType === 'radial') {
    return `radial-gradient(circle at 50% 50%, ${blended.join(', ')})`;
  }
  if (gradientType === 'conic') {
    return `conic-gradient(from 180deg at 50% 50%, ${blended.join(', ')})`;
  }
  return `linear-gradient(180deg, ${blended.join(', ')})`;
}

function normalizeSquareGradientShape(value, fallback = 'linear') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'radial' || raw === 'conic' || raw === 'linear' ? raw : fallback;
}

function normalizeSquareGeometryType(value, fallback = 'plane') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'plane' || raw === 'sphere' || raw === 'box' || raw === 'triangle' || raw === 'other'
    ? raw
    : fallback;
}

function normalizeSquareEdgeSmoothness(value, fallback = 24) {
  return clampRange(value, fallback, 0, 100);
}

function normalizeSquarePlaneResolution(value, fallback = 24) {
  return Math.round(clampRange(value, fallback, 2, 80));
}

function normalizeSquareFoldDegrees(value, fallback = 0) {
  return clampRange(value, fallback, -360, 360);
}

function normalizeSquareGeometryRotateY(value, fallback = 0) {
  return clampRange(value, fallback, -180, 180);
}

function normalizeCanvasSpanValue(value, fallback = undefined) {
  const allowed = [0.25, 0.5, 0.75, ...Array.from({ length: 10 }, (_, index) => index + 1)];
  const numeric = Number(value);
  const fallbackNumeric = Number(fallback);
  const fallbackValue = Number.isFinite(fallbackNumeric)
    ? allowed.find((entry) => Math.abs(entry - fallbackNumeric) < 0.0001)
    : undefined;
  if (!Number.isFinite(numeric)) return fallbackValue;
  const matched = allowed.find((entry) => Math.abs(entry - numeric) < 0.0001);
  return matched !== undefined ? matched : fallbackValue;
}

function normalizeSquareGeometryDepth(value, fallback = 1) {
  return clampRange(value, fallback, 0.25, 100);
}

function normalizeSquareTexturePattern(value, fallback = 'none') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'none' || raw === 'noise' || raw === 'grid' || raw === 'dots' || raw === 'stripes' || raw === 'waves'
    ? raw
    : fallback;
}

function normalizeSquareTextureVelocity(value, fallback = 0) {
  return clampRange(value, fallback, -2, 2);
}

function normalizeSquareTextureDisplacement(value, fallback = 0) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeSquareGradientColorAlphas(input, colorCount = 0, fallback = []) {
  const desiredCount = Math.max(0, Math.min(8, Math.round(Number(colorCount) || 0)));
  if (!desiredCount) return [];
  const source = Array.isArray(input) ? input : [];
  const fallbackSource = Array.isArray(fallback) ? fallback : [];
  const normalized = [];
  for (let index = 0; index < desiredCount; index += 1) {
    const fallbackAlpha = clampOpacity(fallbackSource[index], 1);
    normalized.push(clampOpacity(source[index], fallbackAlpha));
  }
  return normalized;
}

function buildGradientRgbaStops(colors = [], alphas = []) {
  const safeColors = Array.isArray(colors) ? colors : [];
  const safeAlphas = Array.isArray(alphas) ? alphas : [];
  return safeColors.map((color, index) => {
    const hex = normalizeHexColor(color, '#000000');
    const alpha = clampOpacity(safeAlphas[index], 1);
    const rgb = hexToRgbTriplet(hex, '0,0,0');
    return `rgba(${rgb}, ${alpha})`;
  });
}

function normalizeSquareMaterialType(value, fallback = 'standard') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'standard' || raw === 'physical' || raw === 'phong' || raw === 'lambert' || raw === 'basic'
    ? raw
    : fallback;
}

function normalizeSquareMaterialRoughness(value, fallback = 0.78) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeSquareMaterialMetalness(value, fallback = 0.08) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeSquareMaterialShininess(value, fallback = 35) {
  return clampRange(value, fallback, 0, 160);
}

function normalizeSquareMaterialEmissiveIntensity(value, fallback = 0) {
  return clampRange(value, fallback, 0, 4);
}

function normalizeSquareMaterialOpacity(value, fallback = 1) {
  return clampOpacity(value, fallback);
}

function normalizeSquareMaterialClearcoat(value, fallback = 0) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeSquareMaterialTransmission(value, fallback = 0) {
  return clampRange(value, fallback, 0, 1);
}

function normalizeSquareBorderWidth(value, fallback = 0) {
  return clampRange(value, fallback, 0, 0.35);
}

function normalizeSquareBorderDepth(value, fallback = 0.12) {
  return clampRange(value, fallback, 0, 200);
}

function normalizeSquareBorderFollowFold(value, fallback = true) {
  if (value === undefined || value === null) return !!fallback;
  return !!value;
}

function normalizeSquareBorderDepthOffset(value, fallback = 0) {
  return clampRange(value, fallback, -1000, 1000);
}

function normalizeSquareBorderOpacity(value, fallback = 1) {
  return clampOpacity(value, fallback);
}

function normalizeSquareBorderResolution(value, fallback = 24) {
  return Math.round(clampRange(value, fallback, 1, 100));
}

function normalizeSquareExtrusionSideMode(value, fallback = 'repeat-image') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'repeat-image' || raw === 'color' ? raw : fallback;
}

function normalizeSquareExtrusionSideColor(value, fallback = '#1a1a1a') {
  return normalizeHexColor(value, fallback);
}

function normalizeSquareExtrusionSideOpacity(value, fallback = 1) {
  return clampOpacity(value, fallback);
}

function normalizeSquareIconShape(value, fallback = 'none') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'circle' || raw === 'square' || raw === 'diamond' || raw === 'star' || raw === 'none'
    ? raw
    : fallback;
}

function normalizeSquareIconSourceType(value, fallback = 'symbol') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'symbol' || raw === 'image' ? raw : fallback;
}

function normalizeSquareIconImageVariant(value, fallback = 'thumbnail') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'thumbnail' || raw === 'optimized' || raw === 'original' ? raw : fallback;
}

function normalizeSquareContentAlign(value, fallback = 'center') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'left' || raw === 'center' || raw === 'right' ? raw : fallback;
}

function normalizeSquareContentFontFamily(value, fallback = 'system') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'system' || raw === 'sans' || raw === 'serif' || raw === 'mono' || raw === 'rounded' || raw === 'classic' || raw === 'display' || raw === 'script' || raw === 'philosopher'
    ? raw
    : fallback;
}

function resolveSquareContentFontFamily(value, fallback = 'system') {
  const key = normalizeSquareContentFontFamily(value, fallback);
  return SQUARE_CONTENT_FONT_FAMILY_MAP[key] || SQUARE_CONTENT_FONT_FAMILY_MAP.system;
}

function normalizeInfoTextFontType(value, fallback = 'system') {
  return normalizeSquareContentFontFamily(value, fallback);
}

function normalizeInfoTextHorizontalAlign(value, fallback = 'left') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'left' || raw === 'middle' || raw === 'right' ? raw : fallback;
}

function normalizeInfoTextVerticalAlign(value, fallback = 'top') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'top' || raw === 'middle' || raw === 'bottom' ? raw : fallback;
}

function normalizeInfoTextVisibilityToggle(value, fallback = true) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const raw = value.trim().toLowerCase();
    if (raw === 'true' || raw === '1' || raw === 'yes' || raw === 'on') return true;
    if (raw === 'false' || raw === '0' || raw === 'no' || raw === 'off') return false;
  }
  return fallback;
}

function normalizeInfoTextSettings(input = {}, fallback = {}) {
  const source = (input && typeof input === 'object') ? input : {};
  const baseline = (fallback && typeof fallback === 'object') ? fallback : {};
  const normalizedLegacyShowFromLibrary = normalizeInfoTextVisibilityToggle(
    source.showFromLibrary,
    normalizeInfoTextVisibilityToggle(baseline.showFromLibrary, true)
  );
  return {
    fontSize: clampRange(source.fontSize, clampRange(baseline.fontSize, 28, 12, 96), 12, 96),
    lineHeight: clampRange(source.lineHeight, clampRange(baseline.lineHeight, 1.18, 0.8, 2.6), 0.8, 2.6),
    fontType: normalizeInfoTextFontType(source.fontType, normalizeInfoTextFontType(baseline.fontType, 'system')),
    letterSpacing: clampRange(source.letterSpacing, clampRange(baseline.letterSpacing, 0, -4, 24), -4, 24),
    showFromLibrary: normalizedLegacyShowFromLibrary,
    showGalleryTitle: normalizeInfoTextVisibilityToggle(source.showGalleryTitle, normalizeInfoTextVisibilityToggle(baseline.showGalleryTitle, true)),
    showGalleryDescription: normalizeInfoTextVisibilityToggle(source.showGalleryDescription, normalizeInfoTextVisibilityToggle(baseline.showGalleryDescription, true)),
    showLibraryTitle: normalizeInfoTextVisibilityToggle(source.showLibraryTitle, normalizeInfoTextVisibilityToggle(baseline.showLibraryTitle, true)),
    showLibraryDescription: normalizeInfoTextVisibilityToggle(source.showLibraryDescription, normalizeInfoTextVisibilityToggle(baseline.showLibraryDescription, false)),
    showLibraryContributors: normalizeInfoTextVisibilityToggle(source.showLibraryContributors, normalizeInfoTextVisibilityToggle(baseline.showLibraryContributors, false)),
    showLibraryCopyright: normalizeInfoTextVisibilityToggle(source.showLibraryCopyright, normalizeInfoTextVisibilityToggle(baseline.showLibraryCopyright, false)),
    showLibraryCreatedYear: normalizeInfoTextVisibilityToggle(source.showLibraryCreatedYear, normalizeInfoTextVisibilityToggle(baseline.showLibraryCreatedYear, false)),
    showLibraryOtherInfo: normalizeInfoTextVisibilityToggle(source.showLibraryOtherInfo, normalizeInfoTextVisibilityToggle(baseline.showLibraryOtherInfo, false)),
    horizontalAlign: normalizeInfoTextHorizontalAlign(source.horizontalAlign, normalizeInfoTextHorizontalAlign(baseline.horizontalAlign, 'left')),
    verticalAlign: normalizeInfoTextVerticalAlign(source.verticalAlign, normalizeInfoTextVerticalAlign(baseline.verticalAlign, 'top')),
    textColor: normalizeHexColor(source.textColor, normalizeHexColor(baseline.textColor, '')),
    textExtrusion: clampRange(source.textExtrusion, clampRange(baseline.textExtrusion, 0, 0, 8), 0, 8),
    textZIndex: clampRange(source.textZIndex, clampRange(baseline.textZIndex, 0, 0, 2), 0, 2),
    backgroundColor: normalizeHexColor(source.backgroundColor, normalizeHexColor(baseline.backgroundColor, '#050505')),
    backgroundOpacity: clampOpacity(source.backgroundOpacity, clampOpacity(baseline.backgroundOpacity, 0.65)),
    backgroundSizeOffset: clampRange(source.backgroundSizeOffset, clampRange(baseline.backgroundSizeOffset, 0, 0, 240), 0, 240),
    backgroundBorderWidth: clampRange(source.backgroundBorderWidth, clampRange(baseline.backgroundBorderWidth, 0, 0, 240), 0, 240),
    backgroundBorderDepth: clampRange(source.backgroundBorderDepth, clampRange(baseline.backgroundBorderDepth, 0, 0, 200), 0, 200),
    backgroundBorderZOffset: clampRange(source.backgroundBorderZOffset, clampRange(baseline.backgroundBorderZOffset, 0, -2, 2), -2, 2),
    backgroundBorderColor: normalizeHexColor(source.backgroundBorderColor, normalizeHexColor(baseline.backgroundBorderColor, '#ffffff')),
    backgroundBorderOpacity: clampOpacity(source.backgroundBorderOpacity, clampOpacity(baseline.backgroundBorderOpacity, 1)),
    backgroundZIndex: clampRange(source.backgroundZIndex, clampRange(baseline.backgroundZIndex, 0.04, 0, 2), 0, 2),
    backgroundCornerRoundness: clampRange(
      source.backgroundCornerRoundness,
      clampRange(baseline.backgroundCornerRoundness, 0, 0, 100),
      0,
      100
    ),
    backgroundExtrusion: clampRange(
      source.backgroundExtrusion,
      clampRange(baseline.backgroundExtrusion, 0, 0, 100),
      0,
      100
    ),
  };
}

function resolveFocusedInfoBackgroundDepth(value, maxRange = 100) {
  const safeMaxRange = Math.max(0, Number(maxRange) || 0);
  const extrusion = clampRange(value, 0, 0, safeMaxRange);
  return extrusion * 0.045;
}

function createRoundedRectShape(width, height, radius) {
  const safeWidth = Math.max(0.02, Number(width) || 0.02);
  const safeHeight = Math.max(0.02, Number(height) || 0.02);
  const halfW = safeWidth / 2;
  const halfH = safeHeight / 2;
  const safeRadius = clampRange(radius, 0, 0, Math.min(halfW, halfH));

  const shape = new THREE.Shape();
  if (safeRadius <= 0.0001) {
    shape.moveTo(-halfW, -halfH);
    shape.lineTo(halfW, -halfH);
    shape.lineTo(halfW, halfH);
    shape.lineTo(-halfW, halfH);
    shape.lineTo(-halfW, -halfH);
    return shape;
  }

  shape.moveTo(-halfW + safeRadius, -halfH);
  shape.lineTo(halfW - safeRadius, -halfH);
  shape.quadraticCurveTo(halfW, -halfH, halfW, -halfH + safeRadius);
  shape.lineTo(halfW, halfH - safeRadius);
  shape.quadraticCurveTo(halfW, halfH, halfW - safeRadius, halfH);
  shape.lineTo(-halfW + safeRadius, halfH);
  shape.quadraticCurveTo(-halfW, halfH, -halfW, halfH - safeRadius);
  shape.lineTo(-halfW, -halfH + safeRadius);
  shape.quadraticCurveTo(-halfW, -halfH, -halfW + safeRadius, -halfH);
  shape.closePath();
  return shape;
}

function createFocusedInfoBackgroundGeometry(width, height, cornerRoundness, extrusionDepth) {
  const safeWidth = Math.max(0.02, Number(width) || 0.02);
  const safeHeight = Math.max(0.02, Number(height) || 0.02);
  const maxRadius = Math.min(safeWidth, safeHeight) * 0.5;
  const radius = maxRadius * (clampRange(cornerRoundness, 0, 0, 100) / 100);
  const shape = createRoundedRectShape(safeWidth, safeHeight, radius);
  const curveSegments = Math.max(6, Math.round(10 + (clampRange(cornerRoundness, 0, 0, 100) * 0.22)));
  const depth = Math.max(0, Number(extrusionDepth) || 0);

  if (depth > 0.0001) {
    return new THREE.ExtrudeGeometry(shape, {
      depth,
      bevelEnabled: false,
      curveSegments,
    });
  }

  return new THREE.ShapeGeometry(shape, curveSegments);
}

function createFocusedInfoBorderGeometry(width, height, borderWidth, cornerRoundness, extrusionDepth) {
  const safeWidth = Math.max(0.02, Number(width) || 0.02);
  const safeHeight = Math.max(0.02, Number(height) || 0.02);
  const safeBorderWidth = Math.max(0, Number(borderWidth) || 0);
  if (safeBorderWidth <= 0.0001) return null;

  const outerWidth = safeWidth + (safeBorderWidth * 2);
  const outerHeight = safeHeight + (safeBorderWidth * 2);
  const cornerRatio = clampRange(cornerRoundness, 0, 0, 100) / 100;
  const outerRadius = Math.min(outerWidth, outerHeight) * 0.5 * cornerRatio;
  const innerRadius = Math.min(safeWidth, safeHeight) * 0.5 * cornerRatio;
  const curveSegments = Math.max(6, Math.round(10 + (clampRange(cornerRoundness, 0, 0, 100) * 0.22)));

  const outerShape = createRoundedRectShape(outerWidth, outerHeight, outerRadius);
  const innerShape = createRoundedRectShape(safeWidth, safeHeight, innerRadius);
  const holePoints = innerShape.getPoints(Math.max(12, curveSegments * 2)).reverse();
  const hole = new THREE.Path(holePoints);
  outerShape.holes.push(hole);

  const depth = Math.max(0, Number(extrusionDepth) || 0);
  if (depth > 0.0001) {
    return new THREE.ExtrudeGeometry(outerShape, {
      depth,
      bevelEnabled: false,
      curveSegments,
    });
  }

  return new THREE.ShapeGeometry(outerShape, curveSegments);
}

function getFocusedInfoTextExtrusionColor(textColor) {
  const color = new THREE.Color(normalizeHexColor(textColor, '#f5f5f5'));
  color.multiplyScalar(0.48);
  return `#${color.getHexString()}`;
}

function formatMetadataContributors(input = []) {
  const rows = Array.isArray(input) ? input : [];
  return rows
    .map((entry) => {
      const name = String(entry?.name || '').trim();
      const role = String(entry?.rolle || '').trim();
      if (!name && !role) return '';
      return [name, role].filter(Boolean).join(' - ');
    })
    .filter(Boolean)
    .join(', ');
}

function buildFocusedInfoTextLines(entry = {}, infoTextSettings = {}) {
  const lines = [];
  const visibility = normalizeInfoTextSettings(infoTextSettings, {});
  const galleryTitle = String(entry?.title || entry?.filename || '').trim();
  const galleryDescription = String(entry?.description || '').trim();
  const libraryTitle = String(entry?.subject || '').trim();
  const libraryDescription = String(entry?.text || '').trim();
  const contributors = formatMetadataContributors([
    ...(Array.isArray(entry?.deltagere) ? entry.deltagere : []),
    ...(Array.isArray(entry?.artCreatedBy) ? entry.artCreatedBy : []),
  ]);
  const createdYear = Number.isFinite(Number(entry?.createdYear))
    ? String(Math.round(Number(entry.createdYear)))
    : '';
  const explicitCopyright = String(entry?.copyright || entry?.copyrightText || '').trim();
  const fallbackCopyright = [contributors ? `© ${contributors}` : '', createdYear].filter(Boolean).join(' ');
  const copyrightText = explicitCopyright || fallbackCopyright;
  const links = Array.isArray(entry?.links)
    ? entry.links
      .map((item) => String(item?.title || item?.url || '').trim())
      .filter(Boolean)
      .join(', ')
    : '';
  const tags = Array.isArray(entry?.tags)
    ? entry.tags.map((item) => String(item || '').trim()).filter(Boolean).join(', ')
    : '';
  const otherInfo = [
    String(entry?.album || '').trim(),
    String(entry?.filename || '').trim(),
    links ? `Links: ${links}` : '',
    tags ? `Tags: ${tags}` : '',
  ].filter(Boolean).join(' | ');

  function pushLines(value) {
    const raw = String(value || '');
    if (!raw.trim()) return;
    raw
      .split(/\r?\n/g)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => lines.push(line.slice(0, 720)));
  }

  if (visibility.showGalleryTitle && galleryTitle) pushLines(galleryTitle);
  if (visibility.showGalleryDescription && galleryDescription) pushLines(galleryDescription);
  if (visibility.showLibraryTitle && libraryTitle && libraryTitle !== galleryTitle) pushLines(libraryTitle);
  if (visibility.showLibraryDescription && libraryDescription && libraryDescription !== galleryDescription) pushLines(libraryDescription);
  if (visibility.showLibraryContributors && contributors) pushLines(contributors);
  if (visibility.showLibraryCopyright && copyrightText) pushLines(copyrightText);
  if (visibility.showLibraryCreatedYear && createdYear) pushLines(createdYear);
  if (visibility.showLibraryOtherInfo && otherInfo) pushLines(otherInfo);

  if (!lines.length) lines.push('Ingen metadata tilgjengelig.');
  return lines.slice(0, 12);
}

function normalizeSquareContentWeight(value, fallback = 600) {
  const allowed = [300, 400, 500, 600, 700, 800, 900];
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  const rounded = Math.round(raw);
  if (allowed.includes(rounded)) return rounded;
  return allowed.reduce((closest, candidate) => (
    Math.abs(candidate - rounded) < Math.abs(closest - rounded) ? candidate : closest
  ), fallback);
}

function normalizeSquareContentBlocks(input, fallbackText = '') {
  const source = Array.isArray(input) ? input : [];
  const blocks = source
    .slice(0, 12)
    .map((entry, index) => {
      const row = (entry && typeof entry === 'object') ? entry : {};
      const fallbackId = `content-${index + 1}`;
      const text = String(row.text || '').slice(0, 6000);
      if (!text.trim()) return null;
      return {
        id: String(row.id || fallbackId).trim().slice(0, 120) || fallbackId,
        text,
        color: normalizeHexColor(row.color, '#ffffff'),
        size: clampRange(row.size, 30, 12, 120),
        weight: normalizeSquareContentWeight(row.weight, 600),
        italic: !!row.italic,
        align: normalizeSquareContentAlign(row.align, 'center'),
        fontFamily: normalizeSquareContentFontFamily(row.fontFamily, 'system'),
        lineHeight: clampRange(row.lineHeight, 1.2, 0.8, 2.4),
        opacity: clampOpacity(row.opacity, 1),
      };
    })
    .filter(Boolean);

  if (blocks.length) return blocks;

  const fallback = String(fallbackText || '').slice(0, 6000);
  if (!fallback.trim()) return [];
  return [{
    id: 'content-1',
    text: fallback,
    color: '#ffffff',
    size: 30,
    weight: 600,
    italic: false,
    align: 'center',
    fontFamily: 'system',
    lineHeight: 1.2,
    opacity: 1,
  }];
}

function selectSquareIconImageUrl(icon = {}) {
  const variant = normalizeSquareIconImageVariant(icon.imageVariant, 'thumbnail');
  const thumb = String(icon.imageThumbUrl || '').trim();
  const optimized = String(icon.imageOptimizedUrl || '').trim();
  const original = String(icon.imageOriginalUrl || '').trim();
  if (variant === 'optimized') return optimized || thumb || original;
  if (variant === 'original') return original || optimized || thumb;
  return thumb || optimized || original;
}

function normalizeSquareAnimationMode(value, fallback = 'linear') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'linear' || raw === 'non-linear' ? raw : fallback;
}

function normalizeSquareAnimationDirection(value, fallback = 'loop') {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'loop' || raw === 'alternate' ? raw : fallback;
}

function normalizeSquareClickAnimationTransitionMs(value, fallback = 650) {
  const fallbackRounded = Math.round(clampRange(fallback, 650, 50, 6000));
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallbackRounded;
  return Math.round(clampRange(numeric, fallbackRounded, 50, 6000));
}

function normalizeSquareClickAnimationFrame(input, fallback = {}) {
  const source = (input && typeof input === 'object') ? input : {};
  const baseline = (fallback && typeof fallback === 'object') ? fallback : {};
  const fallbackColor1 = normalizeHexColor(baseline.color1, '#0b0b0b');
  const fallbackColor2 = normalizeHexColor(baseline.color2, '#202020');
  const fallbackGradientRaw = (Array.isArray(baseline.gradientColors) ? baseline.gradientColors : [])
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  const fallbackGradientColors = fallbackGradientRaw.length >= 2 ? fallbackGradientRaw : [fallbackColor1, fallbackColor2];
  const gradientColorsRaw = (Array.isArray(source.gradientColors) ? source.gradientColors : [])
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  const gradientColors = gradientColorsRaw.length >= 2 ? gradientColorsRaw : fallbackGradientColors;
  const gradientColorAlphas = normalizeSquareGradientColorAlphas(
    source.gradientColorAlphas,
    gradientColors.length,
    normalizeSquareGradientColorAlphas(
      baseline.gradientColorAlphas,
      gradientColors.length,
      Array.from({ length: gradientColors.length }, () => 1)
    )
  );
  const fallbackMaterialType = normalizeSquareMaterialType(baseline.materialType, 'standard');
  const fallbackTransitionMs = normalizeSquareClickAnimationTransitionMs(baseline.transitionMs, 650);
  const name = String(source.name || baseline.name || '').trim().slice(0, 60);

  return {
    name,
    transitionMs: normalizeSquareClickAnimationTransitionMs(source.transitionMs, fallbackTransitionMs),
    gradientColors,
    gradientColorAlphas,
    gradientShape: normalizeSquareGradientShape(source.gradientShape, normalizeSquareGradientShape(baseline.gradientShape, 'linear')),
    gradientAngle: clampRange(source.gradientAngle, clampRange(baseline.gradientAngle, 90, 0, 360), 0, 360),
    backgroundOpacity: clampOpacity(source.backgroundOpacity, clampOpacity(baseline.backgroundOpacity, 1)),
    geometryType: normalizeSquareGeometryType(source.geometryType, normalizeSquareGeometryType(baseline.geometryType, 'plane')),
    edgeSmoothness: normalizeSquareEdgeSmoothness(source.edgeSmoothness, normalizeSquareEdgeSmoothness(baseline.edgeSmoothness, 24)),
    planeResolution: normalizeSquarePlaneResolution(source.planeResolution, normalizeSquarePlaneResolution(baseline.planeResolution, 24)),
    planeFoldX: normalizeSquareFoldDegrees(source.planeFoldX, normalizeSquareFoldDegrees(baseline.planeFoldX, 0)),
    planeFoldY: normalizeSquareFoldDegrees(source.planeFoldY, normalizeSquareFoldDegrees(baseline.planeFoldY, 0)),
    geometryRotateY: normalizeSquareGeometryRotateY(source.geometryRotateY, normalizeSquareGeometryRotateY(baseline.geometryRotateY, 0)),
    geometryDepth: normalizeSquareGeometryDepth(source.geometryDepth, normalizeSquareGeometryDepth(baseline.geometryDepth, 1)),
    texturePattern: normalizeSquareTexturePattern(source.texturePattern, normalizeSquareTexturePattern(baseline.texturePattern, 'none')),
    textureVelocity: normalizeSquareTextureVelocity(source.textureVelocity, normalizeSquareTextureVelocity(baseline.textureVelocity, 0)),
    textureDisplacement: normalizeSquareTextureDisplacement(
      source.textureDisplacement,
      normalizeSquareTextureDisplacement(baseline.textureDisplacement, 0)
    ),
    materialType: normalizeSquareMaterialType(source.materialType, fallbackMaterialType),
    materialRoughness: normalizeSquareMaterialRoughness(
      source.materialRoughness,
      normalizeSquareMaterialRoughness(baseline.materialRoughness, 0.78)
    ),
    materialMetalness: normalizeSquareMaterialMetalness(
      source.materialMetalness,
      normalizeSquareMaterialMetalness(baseline.materialMetalness, 0.08)
    ),
    materialShininess: normalizeSquareMaterialShininess(
      source.materialShininess,
      normalizeSquareMaterialShininess(baseline.materialShininess, 35)
    ),
    materialEmissiveIntensity: normalizeSquareMaterialEmissiveIntensity(
      source.materialEmissiveIntensity,
      normalizeSquareMaterialEmissiveIntensity(baseline.materialEmissiveIntensity, 0)
    ),
    materialOpacity: normalizeSquareMaterialOpacity(
      source.materialOpacity,
      normalizeSquareMaterialOpacity(baseline.materialOpacity, 1)
    ),
    materialClearcoat: normalizeSquareMaterialClearcoat(
      source.materialClearcoat,
      normalizeSquareMaterialClearcoat(baseline.materialClearcoat, 0)
    ),
    materialTransmission: normalizeSquareMaterialTransmission(
      source.materialTransmission,
      normalizeSquareMaterialTransmission(baseline.materialTransmission, 0)
    ),
  };
}

function normalizeSquareClickAnimations(input, fallbackFrame = {}) {
  const source = Array.isArray(input) ? input : [];
  return source
    .slice(0, 8)
    .map((entry, index) => {
      const normalized = normalizeSquareClickAnimationFrame(entry, fallbackFrame);
      const fallbackName = `Klikk ${index + 1}`;
      const safeName = String(normalized.name || fallbackName).trim().slice(0, 60) || fallbackName;
      return { ...normalized, name: safeName };
    });
}

function createSquareVisualFrameFromExtras(extras = {}) {
  return normalizeSquareClickAnimationFrame({
    gradientColors: extras.gradientColors,
    gradientColorAlphas: extras.gradientColorAlphas,
    gradientShape: extras.gradientShape,
    gradientAngle: extras.gradientAngle,
    backgroundOpacity: extras.backgroundOpacity,
    geometryType: extras.geometryType,
    edgeSmoothness: extras.edgeSmoothness,
    planeResolution: extras.planeResolution,
    planeFoldX: extras.planeFoldX,
    planeFoldY: extras.planeFoldY,
    geometryRotateY: extras.geometryRotateY,
    geometryDepth: extras.geometryDepth,
    texturePattern: extras.texturePattern,
    textureVelocity: extras.textureVelocity,
    textureDisplacement: extras.textureDisplacement,
    materialType: extras.materialType,
    materialRoughness: extras.materialRoughness,
    materialMetalness: extras.materialMetalness,
    materialShininess: extras.materialShininess,
    materialEmissiveIntensity: extras.materialEmissiveIntensity,
    materialOpacity: extras.materialOpacity,
    materialClearcoat: extras.materialClearcoat,
    materialTransmission: extras.materialTransmission,
    transitionMs: 650,
  }, extras);
}

function applySquareVisualFrameToExtras(baseExtras = {}, frame = null) {
  if (!frame || typeof frame !== 'object') return baseExtras;
  return {
    ...baseExtras,
    gradientColors: frame.gradientColors,
    gradientColorAlphas: frame.gradientColorAlphas,
    gradientShape: frame.gradientShape,
    gradientAngle: frame.gradientAngle,
    backgroundOpacity: frame.backgroundOpacity,
    geometryType: frame.geometryType,
    edgeSmoothness: frame.edgeSmoothness,
    planeResolution: frame.planeResolution,
    planeFoldX: frame.planeFoldX,
    planeFoldY: frame.planeFoldY,
    geometryRotateY: frame.geometryRotateY,
    geometryDepth: frame.geometryDepth,
    texturePattern: frame.texturePattern,
    textureVelocity: frame.textureVelocity,
    textureDisplacement: frame.textureDisplacement,
    materialType: frame.materialType,
    materialRoughness: frame.materialRoughness,
    materialMetalness: frame.materialMetalness,
    materialShininess: frame.materialShininess,
    materialEmissiveIntensity: frame.materialEmissiveIntensity,
    materialOpacity: frame.materialOpacity,
    materialClearcoat: frame.materialClearcoat,
    materialTransmission: frame.materialTransmission,
  };
}

function interpolateSquareVisualFrames(fromFrame, toFrame, progress = 1) {
  const from = normalizeSquareClickAnimationFrame(fromFrame, toFrame || {});
  const to = normalizeSquareClickAnimationFrame(toFrame, from);
  const t = clampRange(progress, 1, 0, 1);
  const lerp = (a, b) => a + ((b - a) * t);

  const colorCount = Math.max(from.gradientColors.length, to.gradientColors.length, 2);
  const gradientColors = [];
  const gradientColorAlphas = [];
  for (let index = 0; index < colorCount; index += 1) {
    const fromColor = from.gradientColors[index] || from.gradientColors[from.gradientColors.length - 1] || '#0b0b0b';
    const toColor = to.gradientColors[index] || to.gradientColors[to.gradientColors.length - 1] || fromColor;
    gradientColors.push(mixHex(fromColor, toColor, t));
    const fromAlpha = clampOpacity(from.gradientColorAlphas[index], 1);
    const toAlpha = clampOpacity(to.gradientColorAlphas[index], fromAlpha);
    gradientColorAlphas.push(clampOpacity(lerp(fromAlpha, toAlpha), toAlpha));
  }

  return {
    ...to,
    gradientColors,
    gradientColorAlphas,
    gradientShape: t < 1 ? from.gradientShape : to.gradientShape,
    gradientAngle: lerp(from.gradientAngle, to.gradientAngle),
    backgroundOpacity: clampOpacity(lerp(from.backgroundOpacity, to.backgroundOpacity), to.backgroundOpacity),
    geometryType: t < 1 ? from.geometryType : to.geometryType,
    edgeSmoothness: normalizeSquareEdgeSmoothness(lerp(from.edgeSmoothness, to.edgeSmoothness), to.edgeSmoothness),
    planeResolution: normalizeSquarePlaneResolution(Math.round(lerp(from.planeResolution, to.planeResolution)), to.planeResolution),
    planeFoldX: normalizeSquareFoldDegrees(lerp(from.planeFoldX, to.planeFoldX), to.planeFoldX),
    planeFoldY: normalizeSquareFoldDegrees(lerp(from.planeFoldY, to.planeFoldY), to.planeFoldY),
    geometryRotateY: normalizeSquareGeometryRotateY(lerp(from.geometryRotateY, to.geometryRotateY), to.geometryRotateY),
    geometryDepth: normalizeSquareGeometryDepth(lerp(from.geometryDepth, to.geometryDepth), to.geometryDepth),
    texturePattern: t < 1 ? from.texturePattern : to.texturePattern,
    textureVelocity: lerp(from.textureVelocity, to.textureVelocity),
    textureDisplacement: lerp(from.textureDisplacement, to.textureDisplacement),
    materialType: t < 1 ? from.materialType : to.materialType,
    materialRoughness: lerp(from.materialRoughness, to.materialRoughness),
    materialMetalness: lerp(from.materialMetalness, to.materialMetalness),
    materialShininess: lerp(from.materialShininess, to.materialShininess),
    materialEmissiveIntensity: lerp(from.materialEmissiveIntensity, to.materialEmissiveIntensity),
    materialOpacity: clampOpacity(lerp(from.materialOpacity, to.materialOpacity), to.materialOpacity),
    materialClearcoat: lerp(from.materialClearcoat, to.materialClearcoat),
    materialTransmission: lerp(from.materialTransmission, to.materialTransmission),
  };
}

function getSquareCanvasStateKey(entry = {}, index = 0, galleryKey = '') {
  const stableId = String(entry?._id || entry?.id || entry?.assetId || entry?.youtubeId || '').trim();
  const itemKey = stableId ? `id:${stableId}` : `idx:${index}`;
  return `${String(galleryKey || '').trim() || 'gallery'}:${itemKey}`;
}

function resolveSquareAnimationProgress(progress, direction = 'loop') {
  const t = clampRange(progress, 0, 0, 1);
  if (normalizeSquareAnimationDirection(direction, 'loop') === 'alternate') {
    return t <= 0.5 ? t * 2 : (2 - (t * 2));
  }
  return t;
}

function applyAnimationMode(progress, mode = 'linear') {
  const t = clampRange(progress, 0, 0, 1);
  if (normalizeSquareAnimationMode(mode, 'linear') === 'non-linear') {
    return 0.5 - (Math.cos(Math.PI * t) / 2);
  }
  return t;
}

function normalizeSquareIconKeyframes(input) {
  const source = Array.isArray(input) ? input : [];
  const total = source.length;
  return source
    .slice(0, 5)
    .map((entry, index) => {
      const row = (entry && typeof entry === 'object') ? entry : {};
      const fallbackAt = total <= 1 ? 0 : index / Math.max(1, total - 1);
      const rawAt = Number(row.at);
      return {
        at: Number.isFinite(rawAt) ? clampRange(rawAt, fallbackAt, 0, 1) : fallbackAt,
        offsetX: clampRange(row.offsetX, 0, -60, 60),
        offsetY: clampRange(row.offsetY, 0, -60, 60),
        scale: clampRange(row.scale, 1, 0.2, 4),
        rotate: clampRange(row.rotate, 0, -360, 360),
        opacity: clampOpacity(row.opacity, 1),
        blur: clampRange(row.blur, 0, 0, 24),
      };
    })
    .sort((a, b) => a.at - b.at);
}

function normalizeSquareIconList(input) {
  const source = Array.isArray(input) ? input : [];
  return source.slice(0, 20).map((entry, index) => {
    const icon = (entry && typeof entry === 'object') ? entry : {};
    const fallbackId = `icon-${index + 1}`;
    const hasImageSource = !!String(icon.imageThumbUrl || icon.imageOptimizedUrl || icon.imageOriginalUrl || '').trim();
    const sourceTypeFallback = hasImageSource ? 'image' : 'symbol';
    return {
      id: String(icon.id || fallbackId).trim() || fallbackId,
      sourceType: normalizeSquareIconSourceType(icon.sourceType, sourceTypeFallback),
      symbol: String(icon.symbol || '✦').slice(0, 12) || '✦',
      imageThumbUrl: String(icon.imageThumbUrl || '').trim().slice(0, 2000),
      imageOptimizedUrl: String(icon.imageOptimizedUrl || '').trim().slice(0, 2000),
      imageOriginalUrl: String(icon.imageOriginalUrl || '').trim().slice(0, 2000),
      imageVariant: normalizeSquareIconImageVariant(icon.imageVariant, 'thumbnail'),
      x: clampRange(icon.x, 0, -60, 60),
      y: clampRange(icon.y, 0, -60, 60),
      size: clampRange(icon.size, 52, 6, 260),
      rotation: clampRange(icon.rotation, 0, -360, 360),
      color: normalizeHexColor(icon.color, '#ffffff'),
      opacity: clampOpacity(icon.opacity, 1),
      blur: clampRange(icon.blur, 0, 0, 24),
      keyframes: normalizeSquareIconKeyframes(icon.keyframes || icon.animationKeyframes),
      shape: normalizeSquareIconShape(icon.shape, 'none'),
    };
  });
}

function normalizeSquareKeyframes(input) {
  const source = Array.isArray(input) ? input : [];
  const total = source.length;
  return source
    .slice(0, 10)
    .map((entry, index) => {
      const row = (entry && typeof entry === 'object') ? entry : {};
      const fallbackAt = total <= 1 ? 0 : index / Math.max(1, total - 1);
      const rawAt = Number(row.at);
      return {
        at: Number.isFinite(rawAt) ? clampRange(rawAt, fallbackAt, 0, 1) : fallbackAt,
        offsetX: clampRange(row.offsetX, 0, -60, 60),
        offsetY: clampRange(row.offsetY, 0, -60, 60),
        scale: clampRange(row.scale, 1, 0.2, 4),
        rotate: clampRange(row.rotate, 0, -360, 360),
        opacity: clampOpacity(row.opacity, 1),
      };
    })
    .sort((a, b) => a.at - b.at);
}

function normalizeSquareBoxExtras(box = {}, fallback = {}) {
  const color1 = normalizeHexColor(box.color1, normalizeHexColor(fallback.color1, '#0b0b0b'));
  const color2 = normalizeHexColor(box.color2, normalizeHexColor(fallback.color2, '#202020'));
  const gradientColorsRaw = (Array.isArray(box.gradientColors) ? box.gradientColors : [])
    .map((entry) => normalizeHexColor(entry, ''))
    .filter(Boolean)
    .slice(0, 8);
  const gradientColors = gradientColorsRaw.length >= 2 ? gradientColorsRaw : [color1, color2];
  const gradientColorAlphas = normalizeSquareGradientColorAlphas(
    box.gradientColorAlphas,
    gradientColors.length,
    normalizeSquareGradientColorAlphas(
      fallback.gradientColorAlphas,
      gradientColors.length,
      Array.from({ length: gradientColors.length }, () => 1)
    )
  );
  const materialType = normalizeSquareMaterialType(
    box.materialType,
    normalizeSquareMaterialType(fallback.materialType, 'standard')
  );

  const extras = {
    square: !!box.square,
    textVisible: box.textVisible !== false,
    color1,
    color2,
    gradientColors,
    gradientColorAlphas,
    gradientShape: normalizeSquareGradientShape(box.gradientShape, 'linear'),
    gradientAngle: clampRange(box.gradientAngle, 90, 0, 360),
    backgroundOpacity: clampOpacity(box.backgroundOpacity, clampOpacity(fallback.backgroundOpacity, 1)),
    geometryType: normalizeSquareGeometryType(box.geometryType, normalizeSquareGeometryType(fallback.geometryType, 'plane')),
    edgeSmoothness: normalizeSquareEdgeSmoothness(box.edgeSmoothness, normalizeSquareEdgeSmoothness(fallback.edgeSmoothness, 24)),
    planeResolution: normalizeSquarePlaneResolution(box.planeResolution, normalizeSquarePlaneResolution(fallback.planeResolution, 24)),
    planeFoldX: normalizeSquareFoldDegrees(box.planeFoldX, normalizeSquareFoldDegrees(fallback.planeFoldX, 0)),
    planeFoldY: normalizeSquareFoldDegrees(box.planeFoldY, normalizeSquareFoldDegrees(fallback.planeFoldY, 0)),
    geometryRotateY: normalizeSquareGeometryRotateY(box.geometryRotateY, normalizeSquareGeometryRotateY(fallback.geometryRotateY, 0)),
    geometryDepth: normalizeSquareGeometryDepth(box.geometryDepth, normalizeSquareGeometryDepth(fallback.geometryDepth, 1)),
    texturePattern: normalizeSquareTexturePattern(box.texturePattern, normalizeSquareTexturePattern(fallback.texturePattern, 'none')),
    textureVelocity: normalizeSquareTextureVelocity(box.textureVelocity, normalizeSquareTextureVelocity(fallback.textureVelocity, 0)),
    textureDisplacement: normalizeSquareTextureDisplacement(box.textureDisplacement, normalizeSquareTextureDisplacement(fallback.textureDisplacement, 0)),
    materialType,
    materialRoughness: normalizeSquareMaterialRoughness(
      box.materialRoughness,
      normalizeSquareMaterialRoughness(fallback.materialRoughness, 0.78)
    ),
    materialMetalness: normalizeSquareMaterialMetalness(
      box.materialMetalness,
      normalizeSquareMaterialMetalness(fallback.materialMetalness, 0.08)
    ),
    materialShininess: normalizeSquareMaterialShininess(
      box.materialShininess,
      normalizeSquareMaterialShininess(fallback.materialShininess, 35)
    ),
    materialEmissiveIntensity: normalizeSquareMaterialEmissiveIntensity(
      box.materialEmissiveIntensity,
      normalizeSquareMaterialEmissiveIntensity(fallback.materialEmissiveIntensity, 0)
    ),
    materialOpacity: normalizeSquareMaterialOpacity(
      box.materialOpacity,
      normalizeSquareMaterialOpacity(fallback.materialOpacity, 1)
    ),
    materialClearcoat: normalizeSquareMaterialClearcoat(
      box.materialClearcoat,
      normalizeSquareMaterialClearcoat(fallback.materialClearcoat, 0)
    ),
    materialTransmission: normalizeSquareMaterialTransmission(
      box.materialTransmission,
      normalizeSquareMaterialTransmission(fallback.materialTransmission, 0)
    ),
    borderWidth: normalizeSquareBorderWidth(box.borderWidth, normalizeSquareBorderWidth(fallback.borderWidth, 0)),
    borderColor: normalizeHexColor(box.borderColor, normalizeHexColor(fallback.borderColor, '#ffffff')),
    borderDepth: normalizeSquareBorderDepth(box.borderDepth, normalizeSquareBorderDepth(fallback.borderDepth, 0.12)),
    borderFollowFold: normalizeSquareBorderFollowFold(box.borderFollowFold, normalizeSquareBorderFollowFold(fallback.borderFollowFold, true)),
    borderDepthOffset: normalizeSquareBorderDepthOffset(box.borderDepthOffset, normalizeSquareBorderDepthOffset(fallback.borderDepthOffset, 0)),
    borderOpacity: normalizeSquareBorderOpacity(box.borderOpacity, normalizeSquareBorderOpacity(fallback.borderOpacity, 1)),
    borderResolution: normalizeSquareBorderResolution(box.borderResolution, normalizeSquareBorderResolution(fallback.borderResolution, 24)),
    extrusionSideMode: normalizeSquareExtrusionSideMode(box.extrusionSideMode, normalizeSquareExtrusionSideMode(fallback.extrusionSideMode, 'repeat-image')),
    extrusionSideColor: normalizeSquareExtrusionSideColor(box.extrusionSideColor, normalizeSquareExtrusionSideColor(fallback.extrusionSideColor, '#1a1a1a')),
    extrusionSideOpacity: normalizeSquareExtrusionSideOpacity(box.extrusionSideOpacity, normalizeSquareExtrusionSideOpacity(fallback.extrusionSideOpacity, 1)),
    infoTextSettings: normalizeInfoTextSettings(box.infoTextSettings, fallback.infoTextSettings),
    contentBlocks: normalizeSquareContentBlocks(box.contentBlocks, box.text2),
    icons: normalizeSquareIconList(box.icons),
    animationDuration: clampRange(box.animationDuration, 12, 1, 90),
    animationMode: normalizeSquareAnimationMode(box.animationMode, 'linear'),
    animationDirection: normalizeSquareAnimationDirection(box.animationDirection, normalizeSquareAnimationDirection(fallback.animationDirection, 'loop')),
    animationKeyframes: normalizeSquareKeyframes(box.animationKeyframes || box.keyframes),
  };

  extras.clickAnimations = normalizeSquareClickAnimations(
    box.clickAnimations,
    createSquareVisualFrameFromExtras(extras)
  );

  return extras;
}

function normalizeMediaCardBoxOptions(box = {}, fallback = {}) {
  const source = (box && typeof box === 'object') ? box : {};
  const baseline = (fallback && typeof fallback === 'object') ? fallback : {};
  return {
    planeFoldX: normalizeSquareFoldDegrees(source.planeFoldX, normalizeSquareFoldDegrees(baseline.planeFoldX, 0)),
    planeFoldY: normalizeSquareFoldDegrees(source.planeFoldY, normalizeSquareFoldDegrees(baseline.planeFoldY, 0)),
    edgeSmoothness: normalizeSquareEdgeSmoothness(source.edgeSmoothness, normalizeSquareEdgeSmoothness(baseline.edgeSmoothness, 24)),
    planeResolution: normalizeSquarePlaneResolution(source.planeResolution, normalizeSquarePlaneResolution(baseline.planeResolution, 24)),
    geometryDepth: normalizeSquareGeometryDepth(source.geometryDepth, normalizeSquareGeometryDepth(baseline.geometryDepth, 1)),
    borderWidth: normalizeSquareBorderWidth(source.borderWidth, normalizeSquareBorderWidth(baseline.borderWidth, 0)),
    borderColor: normalizeHexColor(source.borderColor, normalizeHexColor(baseline.borderColor, '#ffffff')),
    borderDepth: normalizeSquareBorderDepth(source.borderDepth, normalizeSquareBorderDepth(baseline.borderDepth, 0.12)),
    borderFollowFold: normalizeSquareBorderFollowFold(source.borderFollowFold, normalizeSquareBorderFollowFold(baseline.borderFollowFold, true)),
    borderDepthOffset: normalizeSquareBorderDepthOffset(source.borderDepthOffset, normalizeSquareBorderDepthOffset(baseline.borderDepthOffset, 0)),
    borderOpacity: normalizeSquareBorderOpacity(source.borderOpacity, normalizeSquareBorderOpacity(baseline.borderOpacity, 1)),
    borderResolution: normalizeSquareBorderResolution(source.borderResolution, normalizeSquareBorderResolution(baseline.borderResolution, 24)),
    extrusionSideMode: normalizeSquareExtrusionSideMode(source.extrusionSideMode, normalizeSquareExtrusionSideMode(baseline.extrusionSideMode, 'repeat-image')),
    extrusionSideColor: normalizeSquareExtrusionSideColor(source.extrusionSideColor, normalizeSquareExtrusionSideColor(baseline.extrusionSideColor, '#1a1a1a')),
    extrusionSideOpacity: normalizeSquareExtrusionSideOpacity(source.extrusionSideOpacity, normalizeSquareExtrusionSideOpacity(baseline.extrusionSideOpacity, 1)),
    texturePattern: normalizeSquareTexturePattern(source.texturePattern, normalizeSquareTexturePattern(baseline.texturePattern, 'none')),
    textureVelocity: normalizeSquareTextureVelocity(source.textureVelocity, normalizeSquareTextureVelocity(baseline.textureVelocity, 0)),
    textureDisplacement: normalizeSquareTextureDisplacement(source.textureDisplacement, normalizeSquareTextureDisplacement(baseline.textureDisplacement, 0)),
    infoTextSettings: normalizeInfoTextSettings(source.infoTextSettings, baseline.infoTextSettings),
  };
}

function resolveMediaSpanDimensions(aspectRatio, widthSpanValue, baseRowHeight, rowGap) {
  const safeAspectRaw = Number(aspectRatio);
  const safeAspect = Number.isFinite(safeAspectRaw) && safeAspectRaw > 0 ? safeAspectRaw : (16 / 9);
  const widthSpan = normalizeCanvasSpanValue(widthSpanValue, 1) || 1;
  const rowSpan = Math.max(0.25, Math.min(10, widthSpan));
  const height = (baseRowHeight * rowSpan) + (rowGap * Math.max(0, rowSpan - 1));
  const width = Math.max(0.06, height * safeAspect);
  return { width, height };
}

function interpolateSquareAnimation(box = {}, nowMs = Date.now()) {
  const extras = normalizeSquareBoxExtras(box, box);
  const keyframes = extras.animationKeyframes;
  if (!keyframes.length) return { offsetX: 0, offsetY: 0, scale: 1, rotate: 0, opacity: 1 };
  if (keyframes.length === 1) {
    const only = keyframes[0];
    return { offsetX: only.offsetX, offsetY: only.offsetY, scale: only.scale, rotate: only.rotate, opacity: only.opacity };
  }

  const durationMs = Math.max(1000, Math.round(extras.animationDuration * 1000));
  const cycleProgress = ((nowMs % durationMs) / durationMs);
  const direction = normalizeSquareAnimationDirection(extras.animationDirection, 'loop');
  const progress = resolveSquareAnimationProgress(cycleProgress, direction);
  const frames = keyframes.slice().sort((a, b) => a.at - b.at);
  let left = frames[0];
  let right = frames[1] || frames[0];
  let tLinear = 0;

  if (direction === 'alternate') {
    if (progress <= frames[0].at) {
      left = frames[0];
      right = frames[1] || frames[0];
    } else if (progress >= frames[frames.length - 1].at) {
      left = frames[frames.length - 2] || frames[frames.length - 1];
      right = frames[frames.length - 1];
    } else {
      for (let index = 0; index < frames.length - 1; index += 1) {
        const a = frames[index];
        const b = frames[index + 1];
        if (progress >= a.at && progress <= b.at) {
          left = a;
          right = b;
          break;
        }
      }
    }
    const denominator = Math.max(0.0001, right.at - left.at);
    tLinear = clampRange((progress - left.at) / denominator, 0, 0, 1);
  } else {
    const first = frames[0];
    const extended = frames.concat([{ ...first, at: first.at + 1 }]);
    left = frames[0];
    right = extended[1] || frames[0];

    for (let index = 0; index < extended.length - 1; index += 1) {
      const a = extended[index];
      const b = extended[index + 1];
      const p = progress < a.at ? progress + 1 : progress;
      if (p >= a.at && p <= b.at) {
        left = a;
        right = b;
        break;
      }
    }

    const denominator = Math.max(0.0001, right.at - left.at);
    const p = progress < left.at ? progress + 1 : progress;
    tLinear = clampRange((p - left.at) / denominator, 0, 0, 1);
  }

  const t = applyAnimationMode(tLinear, extras.animationMode);
  const lerp = (a, b) => a + ((b - a) * t);
  return {
    offsetX: lerp(left.offsetX, right.offsetX),
    offsetY: lerp(left.offsetY, right.offsetY),
    scale: lerp(left.scale, right.scale),
    rotate: lerp(left.rotate, right.rotate),
    opacity: lerp(left.opacity, right.opacity),
  };
}

function interpolateSquareIconAnimation(icon = {}, nowMs = Date.now(), durationSeconds = 12, animationMode = 'linear', animationDirection = 'loop') {
  const keyframes = normalizeSquareIconKeyframes(icon.keyframes || icon.animationKeyframes);
  if (!keyframes.length) return { offsetX: 0, offsetY: 0, scale: 1, rotate: 0, opacity: 1, blur: 0 };
  if (keyframes.length === 1) {
    const only = keyframes[0];
    return {
      offsetX: only.offsetX,
      offsetY: only.offsetY,
      scale: only.scale,
      rotate: only.rotate,
      opacity: only.opacity,
      blur: only.blur,
    };
  }

  const durationMs = Math.max(1000, Math.round(clampRange(durationSeconds, 12, 1, 90) * 1000));
  const cycleProgress = ((nowMs % durationMs) / durationMs);
  const direction = normalizeSquareAnimationDirection(animationDirection, 'loop');
  const progress = resolveSquareAnimationProgress(cycleProgress, direction);
  const frames = keyframes.slice().sort((a, b) => a.at - b.at);
  let left = frames[0];
  let right = frames[1] || frames[0];
  let tLinear = 0;

  if (direction === 'alternate') {
    if (progress <= frames[0].at) {
      left = frames[0];
      right = frames[1] || frames[0];
    } else if (progress >= frames[frames.length - 1].at) {
      left = frames[frames.length - 2] || frames[frames.length - 1];
      right = frames[frames.length - 1];
    } else {
      for (let index = 0; index < frames.length - 1; index += 1) {
        const a = frames[index];
        const b = frames[index + 1];
        if (progress >= a.at && progress <= b.at) {
          left = a;
          right = b;
          break;
        }
      }
    }
    const denominator = Math.max(0.0001, right.at - left.at);
    tLinear = clampRange((progress - left.at) / denominator, 0, 0, 1);
  } else {
    const first = frames[0];
    const extended = frames.concat([{ ...first, at: first.at + 1 }]);
    left = frames[0];
    right = extended[1] || frames[0];

    for (let index = 0; index < extended.length - 1; index += 1) {
      const a = extended[index];
      const b = extended[index + 1];
      const p = progress < a.at ? progress + 1 : progress;
      if (p >= a.at && p <= b.at) {
        left = a;
        right = b;
        break;
      }
    }

    const denominator = Math.max(0.0001, right.at - left.at);
    const p = progress < left.at ? progress + 1 : progress;
    tLinear = clampRange((p - left.at) / denominator, 0, 0, 1);
  }

  const t = applyAnimationMode(tLinear, animationMode);
  const lerp = (a, b) => a + ((b - a) * t);
  return {
    offsetX: lerp(left.offsetX, right.offsetX),
    offsetY: lerp(left.offsetY, right.offsetY),
    scale: lerp(left.scale, right.scale),
    rotate: lerp(left.rotate, right.rotate),
    opacity: lerp(left.opacity, right.opacity),
    blur: lerp(left.blur, right.blur),
  };
}

function applyTextureColorSpace(texture) {
  if (!texture) return;
  if ('encoding' in texture && THREE.sRGBEncoding) {
    texture.encoding = THREE.sRGBEncoding;
  }
}

function prepareImageTexture(texture, renderer) {
  if (!texture) return texture;
  applyTextureColorSpace(texture);
  const width = Number(texture?.image?.width || 0);
  const height = Number(texture?.image?.height || 0);
  const isPowerOfTwo = width > 0
    && height > 0
    && THREE.MathUtils.isPowerOfTwo(width)
    && THREE.MathUtils.isPowerOfTwo(height);

  texture.generateMipmaps = !!isPowerOfTwo;
  texture.minFilter = isPowerOfTwo ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = Math.max(1, renderer?.capabilities?.getMaxAnisotropy?.() || 8);
  if (texture.image) {
    texture.needsUpdate = true;
  }
  return texture;
}

function prepareVideoTexture(texture) {
  if (!texture) return texture;
  applyTextureColorSpace(texture);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

function applyMediaMapToMaterial(material, texture) {
  if (!material) return;
  if (material.color?.set) material.color.set(0xffffff);
  material.map = texture || null;
  if (material.emissive?.set) {
    material.emissive.set(0x111111);
    material.emissiveIntensity = texture ? 0.18 : 0;
  }
  material.needsUpdate = true;
}

function applyMediaSurfaceMaterials(mesh, texture, options = {}) {
  if (!mesh) return;
  const currentMaterial = mesh.material;
  const baseMaterial = Array.isArray(currentMaterial)
    ? (currentMaterial.find((entry) => entry && !entry?.userData?.isExtrusionSideMaterial) || currentMaterial.find(Boolean))
    : currentMaterial;
  if (!baseMaterial) return;

  applyMediaMapToMaterial(baseMaterial, texture);

  const isExtrudedBox = mesh?.geometry?.type === 'BoxGeometry'
    && normalizeSquareGeometryDepth(options.geometryDepth, 1) > 1.0001;
  const sideMode = normalizeSquareExtrusionSideMode(options.extrusionSideMode, 'repeat-image');
  const sideColor = normalizeSquareExtrusionSideColor(options.extrusionSideColor, '#1a1a1a');
  const sideOpacity = normalizeSquareExtrusionSideOpacity(options.extrusionSideOpacity, 1);

  if (!isExtrudedBox || sideMode === 'repeat-image') {
    if (Array.isArray(currentMaterial)) {
      const uniqueMaterials = new Set(currentMaterial.filter(Boolean));
      uniqueMaterials.forEach((entry) => {
        if (!entry || entry === baseMaterial) return;
        try { entry.dispose?.(); } catch {}
      });
      mesh.material = baseMaterial;
    }
    if (!baseMaterial.userData) baseMaterial.userData = {};
    baseMaterial.userData.isExtrusionSideMaterial = false;
    baseMaterial.userData.isExtrusionFrontMaterial = false;
    return;
  }

  const sideMaterial = baseMaterial.clone();
  sideMaterial.color.set(sideColor);
  sideMaterial.map = null;
  sideMaterial.bumpMap = null;
  sideMaterial.emissive.set(sideColor);
  sideMaterial.emissiveIntensity = 0.08;
  sideMaterial.transparent = sideOpacity < 0.999;
  sideMaterial.opacity = sideOpacity;
  sideMaterial.depthWrite = true;
  if (!sideMaterial.userData) sideMaterial.userData = {};
  sideMaterial.userData.isExtrusionSideMaterial = true;

  if (!baseMaterial.userData) baseMaterial.userData = {};
  baseMaterial.userData.isExtrusionSideMaterial = false;
  baseMaterial.userData.isExtrusionFrontMaterial = true;

  mesh.material = [sideMaterial, sideMaterial, sideMaterial, sideMaterial, baseMaterial, baseMaterial];
}

function applyDistanceBlurToMediaMesh(mesh, blurStrength = 0, nowMs = null) {
  if (!mesh) return;
  const targetStrength = clampRange(blurStrength, 0, 0, 120);
  const now = Number.isFinite(Number(nowMs))
    ? Number(nowMs)
    : (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const transitionDurationMs = 500;
  const maxStepPerMs = 120 / transitionDurationMs;
  const materialList = Array.isArray(mesh.material)
    ? mesh.material.filter(Boolean)
    : [mesh.material].filter(Boolean);

  materialList.forEach((material) => {
    if (!material || material?.userData?.isExtrusionSideMaterial) return;
    if (!material.userData) material.userData = {};
    if (!material.userData.distanceBlurBase) {
      material.userData.distanceBlurBase = {
        roughness: Number.isFinite(Number(material.roughness)) ? Number(material.roughness) : 0.78,
        metalness: Number.isFinite(Number(material.metalness)) ? Number(material.metalness) : 0.08,
        emissiveIntensity: Number.isFinite(Number(material.emissiveIntensity)) ? Number(material.emissiveIntensity) : 0.18,
        opacity: Number.isFinite(Number(material.opacity)) ? Number(material.opacity) : 1,
        transparent: !!material.transparent,
      };
    }

    const previousCurrent = clampRange(material.userData.distanceBlurCurrent, 0, 0, 120);
    const previousStamp = Number.isFinite(Number(material.userData.distanceBlurLastTs))
      ? Number(material.userData.distanceBlurLastTs)
      : now;
    const dtMs = Math.max(0, now - previousStamp);
    const maxStep = maxStepPerMs * dtMs;
    const delta = targetStrength - previousCurrent;
    let strength = targetStrength;
    if (Math.abs(delta) > maxStep) {
      strength = previousCurrent + (Math.sign(delta) * maxStep);
    }
    strength = clampRange(strength, previousCurrent, 0, 120);
    material.userData.distanceBlurCurrent = strength;
    material.userData.distanceBlurLastTs = now;

    const base = material.userData.distanceBlurBase;
    if (strength <= 0.0001) {
      if ('roughness' in material) material.roughness = base.roughness;
      if ('metalness' in material) material.metalness = base.metalness;
      if ('emissiveIntensity' in material) material.emissiveIntensity = base.emissiveIntensity;
      if ('opacity' in material) {
        material.opacity = base.opacity;
        material.transparent = base.transparent;
      }
      material.needsUpdate = true;
      return;
    }

    const ratio = strength / 120;
    if ('roughness' in material) material.roughness = clampRange(base.roughness + (ratio * 0.45), base.roughness, 0, 1);
    if ('metalness' in material) material.metalness = clampRange(base.metalness - (ratio * 0.18), base.metalness, 0, 1);
    if ('emissiveIntensity' in material) material.emissiveIntensity = Math.max(0, base.emissiveIntensity * (1 - (ratio * 0.45)));
    if ('opacity' in material) {
      material.opacity = clampRange(base.opacity - (ratio * 0.28), base.opacity, 0.55, 1);
      material.transparent = material.opacity < 0.999 || base.transparent;
    }
    material.needsUpdate = true;
  });
}

function inferSquareMaterialType(material) {
  if (!material) return 'standard';
  const fromUserData = normalizeSquareMaterialType(material?.userData?.squareMaterialType, '');
  if (fromUserData) return fromUserData;
  const type = String(material?.type || '').trim();
  if (type === 'MeshPhysicalMaterial') return 'physical';
  if (type === 'MeshPhongMaterial') return 'phong';
  if (type === 'MeshLambertMaterial') return 'lambert';
  if (type === 'MeshBasicMaterial') return 'basic';
  return 'standard';
}

function createSquareRuntimeMaterial(materialType = 'standard') {
  const normalizedType = normalizeSquareMaterialType(materialType, 'standard');
  const shared = {
    color: 0xffffff,
    side: THREE.DoubleSide,
    transparent: true,
    toneMapped: true,
  };

  let material;
  if (normalizedType === 'physical') {
    material = new THREE.MeshPhysicalMaterial({
      ...shared,
      roughness: 0.78,
      metalness: 0.08,
      clearcoat: 0,
      transmission: 0,
    });
  } else if (normalizedType === 'phong') {
    material = new THREE.MeshPhongMaterial({
      ...shared,
      shininess: 35,
      specular: new THREE.Color(0x2f2f2f),
    });
  } else if (normalizedType === 'lambert') {
    material = new THREE.MeshLambertMaterial(shared);
  } else if (normalizedType === 'basic') {
    material = new THREE.MeshBasicMaterial(shared);
  } else {
    material = new THREE.MeshStandardMaterial({
      ...shared,
      roughness: 0.78,
      metalness: 0.08,
    });
  }

  if (!material.userData) material.userData = {};
  material.userData.squareMaterialType = normalizedType;
  return material;
}

function applySquareRuntimeMaterialSettings(material, extras = {}) {
  if (!material) return;
  if (material.color?.set) material.color.set(0xffffff);

  const opacity = normalizeSquareMaterialOpacity(extras.materialOpacity, 1);
  material.opacity = opacity;
  material.transparent = opacity < 0.999;
  material.depthWrite = opacity >= 0.2;

  if (material.emissive?.set) {
    material.emissive.set(0x111111);
    material.emissiveIntensity = normalizeSquareMaterialEmissiveIntensity(extras.materialEmissiveIntensity, 0);
  }

  if ('roughness' in material) {
    material.roughness = normalizeSquareMaterialRoughness(extras.materialRoughness, 0.78);
  }
  if ('metalness' in material) {
    material.metalness = normalizeSquareMaterialMetalness(extras.materialMetalness, 0.08);
  }
  if ('shininess' in material) {
    material.shininess = normalizeSquareMaterialShininess(extras.materialShininess, 35);
  }
  if ('clearcoat' in material) {
    material.clearcoat = normalizeSquareMaterialClearcoat(extras.materialClearcoat, 0);
  }
  if ('transmission' in material) {
    material.transmission = normalizeSquareMaterialTransmission(extras.materialTransmission, 0);
  }

  if (!material.userData) material.userData = {};
  material.userData.squareMaterialType = normalizeSquareMaterialType(extras.materialType, inferSquareMaterialType(material));
  material.needsUpdate = true;
}

function ensureSquareRuntimeMaterial(mesh, desiredMaterialType = 'standard') {
  if (!mesh) return null;
  const targetType = normalizeSquareMaterialType(desiredMaterialType, 'standard');
  const current = mesh.material;

  if (Array.isArray(current)) {
    current.forEach((entry) => {
      try { entry?.dispose?.(); } catch {}
    });
    const replacement = createSquareRuntimeMaterial(targetType);
    mesh.material = replacement;
    return replacement;
  }

  const currentType = inferSquareMaterialType(current);
  if (current && currentType === targetType) return current;

  const next = createSquareRuntimeMaterial(targetType);
  if (current) {
    if (current.map) next.map = current.map;
    if (current.displacementMap) next.displacementMap = current.displacementMap;
    try { current.dispose?.(); } catch {}
  }
  mesh.material = next;
  return next;
}

function sampleSquareTextureNoise(x, y, seed = 0) {
  const value = Math.sin((x * 12.9898) + (y * 78.233) + (seed * 37.719)) * 43758.5453;
  return value - Math.floor(value);
}

function paintSquareTexturePatternLayer(ctx, width, height, extras = {}, timeMs = 0) {
  const texturePattern = normalizeSquareTexturePattern(extras.texturePattern, 'none');
  if (texturePattern === 'none') return;

  const textureVelocity = normalizeSquareTextureVelocity(extras.textureVelocity, 0);
  const shift = (Number(timeMs) || 0) * textureVelocity * 0.05;
  const unit = Math.max(8, Math.round(Math.min(width, height) / 40));
  const patternTheme = String(extras.patternTheme || '').trim().toLowerCase();
  const useDarkPattern = patternTheme === 'dark';
  const useLightPattern = patternTheme === 'light';
  const hasCustomTheme = useDarkPattern || useLightPattern;
  const patternStrength = clampRange(
    extras.patternStrength,
    hasCustomTheme ? (useDarkPattern ? 0.52 : 0.4) : 0.26,
    0.08,
    0.9,
  );

  ctx.save();
  ctx.globalCompositeOperation = hasCustomTheme
    ? (useDarkPattern ? 'multiply' : 'screen')
    : 'overlay';
  ctx.globalAlpha = patternStrength;
  ctx.strokeStyle = hasCustomTheme
    ? (useDarkPattern ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.74)')
    : 'rgba(255,255,255,0.5)';
  ctx.fillStyle = hasCustomTheme
    ? (useDarkPattern ? 'rgba(0,0,0,0.76)' : 'rgba(255,255,255,0.66)')
    : 'rgba(255,255,255,0.42)';
  ctx.lineWidth = Math.max(1, Math.round(unit * 0.12));

  if (texturePattern === 'grid') {
    const step = Math.max(10, unit * 2);
    const xShift = (((shift % step) + step) % step);
    const yShift = ((((shift * 0.7) % step) + step) % step);
    for (let x = xShift - step; x <= width + step; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = yShift - step; y <= height + step; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  } else if (texturePattern === 'dots') {
    const gap = Math.max(14, Math.round(unit * 2.2));
    const radius = Math.max(1, Math.round(gap * 0.2));
    const xShift = (((shift % gap) + gap) % gap);
    const yShift = ((((shift * 0.65) % gap) + gap) % gap);
    for (let y = yShift - gap; y <= height + gap; y += gap) {
      const rowOffset = (Math.round(y / gap) % 2 === 0) ? 0 : (gap / 2);
      for (let x = xShift - gap; x <= width + gap; x += gap) {
        ctx.beginPath();
        ctx.arc(x + rowOffset, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (texturePattern === 'stripes') {
    const stride = Math.max(16, Math.round(unit * 2.4));
    const stripeWidth = Math.max(3, Math.round(stride * 0.3));
    const diagonal = Math.sqrt((width * width) + (height * height));
    const stripeShift = (((shift * 1.1) % stride) + stride) % stride;
    ctx.translate(width / 2, height / 2);
    ctx.rotate(Math.PI / 4);
    for (let x = -diagonal - stride + stripeShift; x <= diagonal + stride; x += stride) {
      ctx.fillRect(x, -diagonal, stripeWidth, diagonal * 2);
    }
  } else if (texturePattern === 'waves') {
    const waveGap = Math.max(12, Math.round(unit * 1.5));
    const amplitude = Math.max(3, Math.round(unit * 0.5));
    const stepX = Math.max(4, Math.round(unit * 0.45));
    for (let y = -waveGap; y <= height + waveGap; y += waveGap) {
      ctx.beginPath();
      for (let x = 0; x <= width + stepX; x += stepX) {
        const wave = Math.sin((x * 0.02) + (y * 0.032) + (shift * 0.03)) * amplitude;
        if (x === 0) ctx.moveTo(x, y + wave);
        else ctx.lineTo(x, y + wave);
      }
      ctx.stroke();
    }
  } else {
    const cell = Math.max(4, Math.round(unit * 0.45));
    for (let y = 0; y < height; y += cell) {
      for (let x = 0; x < width; x += cell) {
        const noise = sampleSquareTextureNoise(x / cell, y / cell, shift * 0.03);
        if (noise <= 0.57) continue;
        if (hasCustomTheme) {
          const boosted = (0.14 + ((noise - 0.57) * 0.78)) * patternStrength;
          ctx.globalAlpha = clampRange(boosted, 0.2, 0.05, 0.92);
        } else {
          ctx.globalAlpha = 0.08 + ((noise - 0.57) * 0.42);
        }
        ctx.fillRect(x, y, cell, cell);
      }
    }
  }

  ctx.restore();
}

function buildSquareDisplacementCanvas(size = 256, texturePattern = 'noise') {
  const safeSize = Math.max(32, Math.round(size));
  const canvas = document.createElement('canvas');
  canvas.width = safeSize;
  canvas.height = safeSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const normalizedPattern = normalizeSquareTexturePattern(texturePattern, 'noise');
  const pattern = normalizedPattern === 'none' ? 'noise' : normalizedPattern;
  const image = ctx.createImageData(safeSize, safeSize);
  const pixels = image.data;

  for (let y = 0; y < safeSize; y += 1) {
    for (let x = 0; x < safeSize; x += 1) {
      const index = (y * safeSize + x) * 4;
      const baseNoise = (sampleSquareTextureNoise(x * 0.12, y * 0.12, 0.4) - 0.5) * 0.26;
      let signal = 0;

      if (pattern === 'grid') {
        signal = (Math.sin(x * 0.22) + Math.sin(y * 0.22)) * 0.22;
      } else if (pattern === 'dots') {
        const step = 24;
        const dx = ((x % step) - (step / 2)) / step;
        const dy = ((y % step) - (step / 2)) / step;
        const dist = Math.sqrt((dx * dx) + (dy * dy));
        signal = dist < 0.22 ? 0.52 : -0.08;
      } else if (pattern === 'stripes') {
        signal = Math.sin((x + y) * (Math.PI / 16)) * 0.42;
      } else if (pattern === 'waves') {
        signal = Math.sin((x * 0.11) + (Math.sin(y * 0.075) * 2.1)) * 0.4;
      } else {
        signal = (sampleSquareTextureNoise(x * 0.16, y * 0.16, 1.2) - 0.5) * 0.9;
      }

      const gray = Math.max(0, Math.min(255, Math.round(128 + ((signal + baseNoise) * 125))));
      pixels[index] = gray;
      pixels[index + 1] = gray;
      pixels[index + 2] = gray;
      pixels[index + 3] = 255;
    }
  }

  ctx.putImageData(image, 0, 0);
  return canvas;
}

function normalizeBgBlendMode(value, fallback = 0) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  return Math.max(-1, Math.min(1, raw));
}

function mixHex(colorA, colorB, ratio = 0.5) {
  const t = Math.max(0, Math.min(1, Number(ratio) || 0));
  const a = normalizeHexColor(colorA, '#000000').slice(1);
  const b = normalizeHexColor(colorB, '#000000').slice(1);
  const ar = parseInt(a.slice(0, 2), 16);
  const ag = parseInt(a.slice(2, 4), 16);
  const ab = parseInt(a.slice(4, 6), 16);
  const br = parseInt(b.slice(0, 2), 16);
  const bg = parseInt(b.slice(2, 4), 16);
  const bb = parseInt(b.slice(4, 6), 16);
  const rr = Math.round(ar + ((br - ar) * t));
  const rg = Math.round(ag + ((bg - ag) * t));
  const rb = Math.round(ab + ((bb - ab) * t));
  return `#${[rr, rg, rb].map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('')}`;
}

function applyBgBlendMode(baseColor, blendMode) {
  const safe = normalizeHexColor(baseColor, '#000000');
  const mode = normalizeBgBlendMode(blendMode, 0);
  if (mode < 0) return mixHex(safe, '#000000', Math.abs(mode));
  if (mode > 0) return mixHex(safe, '#ffffff', Math.abs(mode));
  return safe;
}

function normalizeVariantAttributes(input) {
  const source = Array.isArray(input) ? input : [];
  const seen = new Set();
  const normalized = [];

  source.forEach((entry) => {
    const title = String(entry?.title || '').trim().slice(0, 120);
    const value = String(entry?.value || '').trim().slice(0, 120);
    if (!title || !value) return;
    const key = `${title.toLowerCase()}::${value.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push({ title, value });
  });

  normalized.sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'nb'));
  return normalized;
}

function normalizeVariantStockRows(rows, fallbackStockOnHand = 0) {
  const source = Array.isArray(rows) ? rows : [];
  if (!source.length) {
    return [{
      key: 'base',
      label: 'Standard',
      attributes: [],
      stockOnHand: toSafeNonNegativeInteger(fallbackStockOnHand),
      reservedCount: 0,
      soldCount: 0,
    }];
  }

  return source.map((row) => ({
    key: String(row?.key || '').trim() || 'base',
    label: String(row?.label || '').trim() || 'Standard',
    attributes: normalizeVariantAttributes(row?.attributes),
    stockOnHand: toSafeNonNegativeInteger(row?.stockOnHand),
    reservedCount: toSafeNonNegativeInteger(row?.reservedCount),
    soldCount: toSafeNonNegativeInteger(row?.soldCount),
  }));
}

function formatAvailabilityStatus(availableStock) {
  return availableStock > 0 ? 'PÅ LAGER' : 'IKKE PÅ LAGER, LANG LEVERINGSTID';
}

function formatStockTableStatus(availableStock) {
  return availableStock > 0 ? 'PÅ LAGER' : 'IKKE PÅ LAGER';
}

function variantRowAvailableStock(row) {
  return Math.max(0, toSafeNonNegativeInteger(row?.stockOnHand) - toSafeNonNegativeInteger(row?.reservedCount));
}

function rowMatchesSelection(row, selectionRows) {
  const attributesMap = new Map(
    normalizeVariantAttributes(row?.attributes).map((entry) => [String(entry.title || '').trim().toLowerCase(), String(entry.value || '').trim()])
  );
  return (Array.isArray(selectionRows) ? selectionRows : []).every((selection) => {
    const title = String(selection?.title || '').trim();
    const value = String(selection?.value || '').trim();
    if (!title || !value) return false;
    return String(attributesMap.get(title.toLowerCase()) || '') === value;
  });
}

function buildMerchandiseVariantTable(variantGroups, variantStockRows) {
  const groups = normalizeVariantGroups(variantGroups);
  const headers = (groups.length ? groups.map((group) => group.title) : ['Variant']).concat('Status');
  const rows = (Array.isArray(variantStockRows) ? variantStockRows : []).map((row) => {
    const attributeMap = new Map(
      normalizeVariantAttributes(row?.attributes).map((attribute) => [String(attribute.title || '').trim().toLowerCase(), String(attribute.value || '').trim()])
    );

    const variantCells = groups.length
      ? groups.map((group) => String(attributeMap.get(String(group.title || '').trim().toLowerCase()) || '-'))
      : [String(row?.label || 'Standard').trim() || 'Standard'];

    const availableStock = Math.max(0, toSafeNonNegativeInteger(row?.stockOnHand) - toSafeNonNegativeInteger(row?.reservedCount));
    return [...variantCells, formatStockTableStatus(availableStock)];
  });

  return {
    headers,
    rows,
  };
}

function getMeshCardDimensions(mesh, fallbackHeight = 1) {
  const userWidth = Number(mesh?.userData?.cardWidth);
  const userHeight = Number(mesh?.userData?.cardHeight);
  if (Number.isFinite(userWidth) && userWidth > 0 && Number.isFinite(userHeight) && userHeight > 0) {
    return { width: userWidth, height: userHeight };
  }
  const geometry = mesh?.geometry;
  const width = Number(geometry?.parameters?.width || 1);
  const height = Number(geometry?.parameters?.height || fallbackHeight || 1);
  return {
    width: Number.isFinite(width) && width > 0 ? width : 1,
    height: Number.isFinite(height) && height > 0 ? height : (fallbackHeight || 1),
  };
}

function setMeshGeometryWithSize(mesh, geometry, width, height) {
  if (!mesh || !geometry) return;
  if (mesh.geometry) mesh.geometry.dispose();
  mesh.geometry = geometry;
  if (!mesh.userData) mesh.userData = {};
  mesh.userData.cardWidth = width;
  mesh.userData.cardHeight = height;
}

function applyFoldWarpToGeometry(geometry, width, height, foldX = 0, foldY = 0) {
  if (!geometry?.attributes?.position) return geometry;
  const xDegrees = normalizeSquareFoldDegrees(foldX, 0);
  const yDegrees = normalizeSquareFoldDegrees(foldY, 0);
  const thetaX = THREE.MathUtils.degToRad(xDegrees);
  const thetaY = THREE.MathUtils.degToRad(yDegrees);

  if (Math.abs(thetaX) < 0.0001 && Math.abs(thetaY) < 0.0001) {
    return geometry;
  }

  const pos = geometry.attributes.position;
  const halfW = Math.max(0.0001, width / 2);
  const halfH = Math.max(0.0001, height / 2);

  for (let i = 0; i < pos.count; i += 1) {
    let x = pos.getX(i);
    let y = pos.getY(i);
    let z = pos.getZ(i);

    if (Math.abs(thetaX) >= 0.0001) {
      const radiusX = height / thetaX;
      const phiX = (y / height) * thetaX;
      y = Math.sin(phiX) * radiusX;
      z += radiusX - (Math.cos(phiX) * radiusX);
    }

    if (Math.abs(thetaY) >= 0.0001) {
      const radiusY = width / thetaY;
      const phiY = (x / width) * thetaY;
      x = Math.sin(phiY) * radiusY;
      z += radiusY - (Math.cos(phiY) * radiusY);
    }

    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
      pos.setXYZ(i, x, y, z);
    } else {
      pos.setXYZ(i, THREE.MathUtils.clamp(pos.getX(i), -halfW, halfW), THREE.MathUtils.clamp(pos.getY(i), -halfH, halfH), 0);
    }
  }

  pos.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

function buildFoldedPlaneGeometry(width, height, edgeSmoothness = 24, foldX = 0, foldY = 0, planeResolution = undefined) {
  const explicitResolution = Number(planeResolution);
  const segments = Number.isFinite(explicitResolution)
    ? normalizeSquarePlaneResolution(explicitResolution, 24)
    : Math.max(2, Math.round(4 + ((normalizeSquareEdgeSmoothness(edgeSmoothness, 24) / 100) * 60)));
  const geometry = new THREE.PlaneGeometry(width, height, segments, segments);
  return applyFoldWarpToGeometry(geometry, width, height, foldX, foldY);
}

function applySpherifyWarpToGeometry(geometry, width, height, depth, amount = 0) {
  const t = clampRange(amount, 0, 0, 1);
  if (!geometry?.attributes?.position || t <= 0.0001) return geometry;

  const pos = geometry.attributes.position;
  const halfW = Math.max(0.0001, width / 2);
  const halfH = Math.max(0.0001, height / 2);
  const halfD = Math.max(0.0001, depth / 2);

  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);

    const nx = x / halfW;
    const ny = y / halfH;
    const nz = z / halfD;
    const invLen = 1 / Math.max(0.0001, Math.hypot(nx, ny, nz));

    const sx = (nx * invLen) * halfW;
    const sy = (ny * invLen) * halfH;
    const sz = (nz * invLen) * halfD;

    pos.setXYZ(
      i,
      THREE.MathUtils.lerp(x, sx, t),
      THREE.MathUtils.lerp(y, sy, t),
      THREE.MathUtils.lerp(z, sz, t)
    );
  }

  pos.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

function buildSquareCardGeometry(width, height, options = {}) {
  const geometryType = normalizeSquareGeometryType(options.geometryType, 'plane');
  const smoothness = normalizeSquareEdgeSmoothness(options.edgeSmoothness, 24);
  const planeResolutionRaw = Number(options.planeResolution);
  const planeResolution = Number.isFinite(planeResolutionRaw)
    ? normalizeSquarePlaneResolution(planeResolutionRaw, 24)
    : undefined;
  const foldX = normalizeSquareFoldDegrees(options.planeFoldX, 0);
  const foldY = normalizeSquareFoldDegrees(options.planeFoldY, 0);
  const rotateYRad = THREE.MathUtils.degToRad(normalizeSquareGeometryRotateY(options.geometryRotateY, 0));
  const depthScale = normalizeSquareGeometryDepth(options.geometryDepth, 1);
  const detail = Math.max(3, Math.round(6 + (smoothness / 100) * 56));

  if (geometryType === 'sphere') {
    const widthSegments = Math.max(8, detail);
    const heightSegments = Math.max(6, Math.round(widthSegments * 0.72));
    const sphere = new THREE.SphereGeometry(0.5, widthSegments, heightSegments);
    sphere.scale(width, height, Math.max(0.08, Math.min(width, height) * depthScale));
    sphere.rotateY((-Math.PI * 0.5) + rotateYRad);
    return sphere;
  }

  if (geometryType === 'box') {
    const depth = Math.max(0.08, Math.min(width, height) * 0.26 * depthScale);
    const seg = Math.max(1, Math.round(1 + (smoothness / 100) * 14));
    const geometry = new THREE.BoxGeometry(width, height, depth, seg, seg, seg);
    applySpherifyWarpToGeometry(geometry, width, height, depth, smoothness / 100);
    if (Math.abs(rotateYRad) > 0.00001) geometry.rotateY(rotateYRad);
    return geometry;
  }

  if (geometryType === 'triangle') {
    const depth = Math.max(0.08, Math.min(width, height) * 0.2 * depthScale);
    const hSeg = Math.max(1, Math.round(1 + (smoothness / 100) * 8));
    const tri = new THREE.CylinderGeometry(0.5, 0.5, depth, 3, hSeg);
    tri.rotateX(Math.PI / 2);
    tri.rotateZ(Math.PI / 2);
    tri.scale(width, height, 1);
    if (Math.abs(rotateYRad) > 0.00001) tri.rotateY(rotateYRad);
    return tri;
  }

  if (geometryType === 'other') {
    const detailLevel = Math.max(0, Math.min(3, Math.round((smoothness / 100) * 3)));
    const other = new THREE.IcosahedronGeometry(0.5, detailLevel);
    other.scale(width, height, Math.max(0.08, Math.min(width, height) * 0.7 * depthScale));
    if (Math.abs(rotateYRad) > 0.00001) other.rotateY(rotateYRad);
    return other;
  }

  if (depthScale > 1.0001) {
    const depth = Math.max(0.02, Math.min(width, height) * 0.14 * (depthScale - 1));
    const seg = Math.max(1, Math.min(80, planeResolution ?? Math.round(1 + (smoothness / 100) * 18)));
    const foldedBox = new THREE.BoxGeometry(width, height, depth, seg, seg, Math.max(1, Math.round(seg * 0.6)));
    applyFoldWarpToGeometry(foldedBox, width, height, foldX, foldY);
    applySpherifyWarpToGeometry(foldedBox, width, height, depth, smoothness / 100);
    foldedBox.computeBoundingBox();
    const maxZ = foldedBox.boundingBox?.max?.z;
    if (Number.isFinite(maxZ) && Math.abs(maxZ) > 0.00001) {
      // Keep the original plane face anchored while extra thickness grows backward.
      foldedBox.translate(0, 0, -maxZ);
    }
    if (Math.abs(rotateYRad) > 0.00001) foldedBox.rotateY(rotateYRad);
    return foldedBox;
  }

  const plane = buildFoldedPlaneGeometry(width, height, smoothness, foldX, foldY, planeResolution);
  if (Math.abs(rotateYRad) > 0.00001) plane.rotateY(rotateYRad);
  return plane;
}

function applyMediaBorderFrame(mesh, width, height, options = {}, animatedPainters = []) {
  if (!mesh?.userData) return;

  const previousPainter = mesh.userData.mediaBorderPainter;
  if (previousPainter && Array.isArray(animatedPainters)) {
    const painterIndex = animatedPainters.indexOf(previousPainter);
    if (painterIndex >= 0) animatedPainters.splice(painterIndex, 1);
  }
  mesh.userData.mediaBorderPainter = null;

  const previousGroup = mesh.userData.mediaBorderGroup;
  if (previousGroup) {
    mesh.remove(previousGroup);
    previousGroup.traverse((node) => {
      if (node?.geometry?.dispose) {
        try { node.geometry.dispose(); } catch {}
      }
      const materialList = Array.isArray(node?.material) ? node.material : [node?.material];
      materialList.forEach((material) => {
        if (!material) return;
        const textureSet = new Set([material.map, material.bumpMap].filter(Boolean));
        textureSet.forEach((texture) => {
          try { texture.dispose?.(); } catch {}
        });
        try { material.dispose?.(); } catch {}
      });
    });
  }
  mesh.userData.mediaBorderGroup = null;

  const borderWidthRatio = normalizeSquareBorderWidth(options.borderWidth, 0);
  if (borderWidthRatio <= 0.0001) return;

  const borderWidth = Math.max(0.002, Math.min(width, height) * borderWidthRatio);
  const outerWidth = width + (borderWidth * 2);
  const borderDepth = normalizeSquareBorderDepth(options.borderDepth, 0.12);
  const depth = Math.max(0.004, Math.min(width, height) * borderDepth * 0.2);
  const borderFollowFold = normalizeSquareBorderFollowFold(options.borderFollowFold, true);
  const borderDepthOffset = normalizeSquareBorderDepthOffset(options.borderDepthOffset, 0);
  const borderOpacity = normalizeSquareBorderOpacity(options.borderOpacity, 1);
  const borderResolution = normalizeSquareBorderResolution(options.borderResolution, 24);
  const foldX = normalizeSquareFoldDegrees(options.planeFoldX, 0);
  const foldY = normalizeSquareFoldDegrees(options.planeFoldY, 0);
  const foldActive = borderFollowFold && (Math.abs(foldX) >= 0.0001 || Math.abs(foldY) >= 0.0001);
  const borderColor = normalizeHexColor(options.borderColor, '#ffffff');
  const borderPatternTheme = getHexRelativeLuminance(borderColor, 0.5) >= 0.56 ? 'dark' : 'light';
  const borderPatternStrength = borderPatternTheme === 'dark' ? 0.56 : 0.44;
  const pattern = normalizeSquareTexturePattern(options.texturePattern, 'none');
  const textureVelocity = normalizeSquareTextureVelocity(options.textureVelocity, 0);
  const displacement = normalizeSquareTextureDisplacement(options.textureDisplacement, 0);

  let borderTexture = null;
  let borderPainter = null;
  if (pattern !== 'none') {
    const patternCanvas = document.createElement('canvas');
    patternCanvas.width = 256;
    patternCanvas.height = 256;
    const patternCtx = patternCanvas.getContext('2d');
    if (patternCtx) {
      borderTexture = new THREE.CanvasTexture(patternCanvas);
      borderTexture.wrapS = THREE.RepeatWrapping;
      borderTexture.wrapT = THREE.RepeatWrapping;
      borderTexture.repeat.set(2.6, 2.6);
      const drawPattern = (timeMs = 0) => {
        patternCtx.clearRect(0, 0, patternCanvas.width, patternCanvas.height);
        patternCtx.fillStyle = borderColor;
        patternCtx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);
        paintSquareTexturePatternLayer(patternCtx, patternCanvas.width, patternCanvas.height, {
          texturePattern: pattern,
          textureVelocity,
          patternTheme: borderPatternTheme,
          patternStrength: borderPatternStrength,
        }, timeMs);
        borderTexture.needsUpdate = true;
      };
      drawPattern(performance.now());
      if (Math.abs(textureVelocity) > 0.0001) {
        borderPainter = (timestampMs) => drawPattern(timestampMs);
      }
    }
  }

  const borderMaterial = new THREE.MeshStandardMaterial({
    color: borderColor,
    roughness: 0.42,
    metalness: 0.08,
    transparent: borderOpacity < 0.999,
    opacity: borderOpacity,
    map: borderTexture,
    bumpMap: borderTexture,
    bumpScale: displacement * 0.08,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });

  const borderGroup = new THREE.Group();
  const z = 0.002 + (Math.min(width, height) * 0.01 * borderDepthOffset);
  const lengthSegments = Math.max(1, Math.round(1 + ((borderResolution / 100) * 120)));
  const thicknessSegments = Math.max(1, Math.round(1 + ((borderResolution / 100) * 12)));
  const depthSegments = Math.max(1, Math.round(1 + ((borderResolution / 100) * 6)));

  const createBorderSegment = (segmentWidth, segmentHeight, centerX, centerY, isHorizontal) => {
    const segX = foldActive ? (isHorizontal ? lengthSegments : thicknessSegments) : 1;
    const segY = foldActive ? (isHorizontal ? thicknessSegments : lengthSegments) : 1;
    const segmentGeometry = new THREE.BoxGeometry(segmentWidth, segmentHeight, depth, segX, segY, depthSegments);
    segmentGeometry.translate(centerX, centerY, -(depth * 0.5));
    if (foldActive) {
      applyFoldWarpToGeometry(segmentGeometry, width, height, foldX, foldY);
    }
    const segmentMesh = new THREE.Mesh(segmentGeometry, borderMaterial);
    segmentMesh.position.z = z;
    borderGroup.add(segmentMesh);
  };

  // Keep the border outside the media plane so it never overlaps image/video content.
  createBorderSegment(outerWidth, borderWidth, 0, (height * 0.5) + (borderWidth * 0.5), true);
  createBorderSegment(outerWidth, borderWidth, 0, -((height * 0.5) + (borderWidth * 0.5)), true);
  createBorderSegment(borderWidth, height, -((width * 0.5) + (borderWidth * 0.5)), 0, false);
  createBorderSegment(borderWidth, height, (width * 0.5) + (borderWidth * 0.5), 0, false);

  mesh.add(borderGroup);
  mesh.userData.mediaBorderGroup = borderGroup;

  if (borderPainter && Array.isArray(animatedPainters)) {
    animatedPainters.push(borderPainter);
    mesh.userData.mediaBorderPainter = borderPainter;
  }
}

function applyUpgradeFlash(mesh, mat, newTex) {
  if (!mesh || !newTex) return;
  const materialList = Array.isArray(mat) ? mat.filter(Boolean) : [mat].filter(Boolean);
  const uniqueMaterials = Array.from(new Set(materialList));
  const targetMaterials = uniqueMaterials.filter((entry) => !entry?.userData?.isExtrusionSideMaterial);
  if (!targetMaterials.length) return;

  targetMaterials.forEach((entry) => {
    entry.map = newTex;
    entry.needsUpdate = true;
    const base = Number.isFinite(Number(entry.emissiveIntensity)) ? Number(entry.emissiveIntensity) : 0.18;
    entry.userData = { ...(entry.userData || {}), upgradeFlashBaseEmissive: base };
    entry.emissiveIntensity = base;
  });
}

function youtubeIdFromUrl(url) {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      const seg = u.pathname.split('/').filter(Boolean); return seg[0];
    }
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v'); if (v) return v;
      const seg = u.pathname.split('/').filter(Boolean);
      if (seg[0] === 'embed' && seg[1]) return seg[1];
      if (seg[0] === 'shorts' && seg[1]) return seg[1];
    }
  } catch {}
  return undefined;
}

const PhotoGallery3D = ({
  mode = 'discover',
  embedded = false,
  embeddedGalleryId = '',
  embeddedItemId = '',
  onEmbeddedReady = null,
}) => {
  const { galleryId: galleryIdParam } = useParams();
  const galleryId = String((embedded ? embeddedGalleryId : galleryIdParam) || '').trim();

function normalizeBoxTextFormat(box = {}) {
  const textAlignRaw = String(box?.textAlign || '').trim().toLowerCase();
  const verticalAlignRaw = String(box?.verticalAlign || '').trim().toLowerCase();
  const orientationRaw = String(box?.orientation || '').trim().toLowerCase();
  const fontScaleRaw = Number(box?.fontScale);
  const lineSpacingRaw = Number(box?.lineSpacing);
  return {
    textAlign: textAlignRaw === 'left' || textAlignRaw === 'right' ? textAlignRaw : 'center',
    verticalAlign: verticalAlignRaw === 'top' || verticalAlignRaw === 'bottom' ? verticalAlignRaw : 'center',
    orientation: orientationRaw === 'vertical' ? 'vertical' : 'horizontal',
    fontScale: Number.isFinite(fontScaleRaw) ? Math.max(0.6, Math.min(1.8, fontScaleRaw)) : 1,
    lineSpacing: Number.isFinite(lineSpacingRaw) ? Math.max(0.8, Math.min(2.2, lineSpacingRaw)) : 1.2,
  };
}

function wrapTextBlocks(ctx, text, maxWidth) {
  const paragraphs = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const out = [];
  for (const paragraph of paragraphs) {
    const words = String(paragraph || '').split(/\s+/).filter(Boolean);
    if (!words.length) {
      out.push('');
      continue;
    }
    let current = '';
    for (const word of words) {
      const next = current ? `${current} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth) {
        current = next;
        continue;
      }
      if (current) out.push(current);
      if (ctx.measureText(word).width <= maxWidth) {
        current = word;
      } else {
        let chunk = '';
        for (const ch of word) {
          const candidate = `${chunk}${ch}`;
          if (ctx.measureText(candidate).width <= maxWidth) {
            chunk = candidate;
            continue;
          }
          if (chunk) out.push(chunk);
          chunk = ch;
        }
        current = chunk;
      }
    }
    if (current) out.push(current);
  }
  return out;
}

function measureWrappedLineCount(ctx, text, maxWidth) {
  return wrapTextBlocks(ctx, text, maxWidth).length;
}

function estimateBoxRowSpan(entry, width, baseHeight, maxRows = 8, options = {}) {
  if (typeof document === 'undefined') return 1;
  const safeWidth = Math.max(0.1, Number(width) || 0.1);
  const textGutter = Math.max(0, Number(options?.textGutter) || TEXT_CARD_TEXT_GUTTER);
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = Math.max(220, Math.round(1024 * (baseHeight / safeWidth)));
  const ctx = canvas.getContext('2d');
  if (!ctx) return 1;

  const box = entry?.box || {};
  const textFormat = normalizeBoxTextFormat(box);
  const title = String(box?.text1 || entry?.title || '').trim();
  const subtitle = String(box?.text2 || entry?.description || '').trim();
  const lines = Array.isArray(box?.lines)
    ? box.lines.map((line) => String(line || '').trim()).filter(Boolean)
    : [];
  const tableHeaders = Array.isArray(box?.table?.headers)
    ? box.table.headers.map((header) => String(header || '').trim()).filter(Boolean)
    : [];
  const tableRows = Array.isArray(box?.table?.rows)
    ? box.table.rows
      .filter((row) => Array.isArray(row))
      .map((row) => row.map((cell) => String(cell || '').trim()))
      .filter((row) => row.some(Boolean))
    : [];

  const toSpan = (pixels) => {
    const base = Math.max(1, canvas.height);
    const span = Math.ceil(Math.max(0, pixels) / base);
    return Math.max(1, Math.min(maxRows, span));
  };

  if (tableHeaders.length && tableRows.length) {
    const top = title ? 170 : 110;
    const headerRowCount = 1;
    const bodyRowCount = tableRows.length;
    const rowHeight = 36;
    const required = top + ((headerRowCount + bodyRowCount) * rowHeight) + 72;
    return toSpan(required);
  }

  if (lines.length) {
    const maxTextWidth = (textFormat.orientation === 'vertical' ? canvas.height : canvas.width) - textGutter;
    let y = title ? 210 : 130;
    for (const line of lines) {
      ctx.font = `500 ${Math.max(14, Math.round(36 * textFormat.fontScale))}px system-ui, sans-serif`;
      const wrappedCount = measureWrappedLineCount(ctx, line, maxTextWidth);
      y += (Math.max(1, wrappedCount) * Math.round(52 * textFormat.lineSpacing)) + 8;
    }
    return toSpan(y + 70);
  }

  const maxTextWidth = (textFormat.orientation === 'vertical' ? canvas.height : canvas.width) - textGutter;
  let y = 110;
  if (title) {
    ctx.font = `700 ${Math.max(18, Math.round(58 * textFormat.fontScale))}px system-ui, sans-serif`;
    y += Math.max(1, measureWrappedLineCount(ctx, title, maxTextWidth)) * Math.round(68 * textFormat.lineSpacing);
  }
  if (subtitle) {
    ctx.font = `600 ${Math.max(16, Math.round(48 * textFormat.fontScale))}px system-ui, sans-serif`;
    y += 24;
    y += Math.max(1, measureWrappedLineCount(ctx, subtitle, maxTextWidth)) * Math.round(56 * textFormat.lineSpacing);
  }
  return toSpan(y + 80);
}
  const navigate = useNavigate();
  const isMerchandiseMode = mode === 'merchandise';
  const containerRef = useRef(null);
  const overlayRootRef = useRef(null);
  const estimateBoxRowSpanRef = useRef(estimateBoxRowSpan);

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetsPending, setAssetsPending] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [firstPaintReady, setFirstPaintReady] = useState(false);
  const [fadePhase, setFadePhase] = useState('idle');
  const [fogIntroProgress, setFogIntroProgress] = useState(0);
  const [fogFocusZoomProgress, setFogFocusZoomProgress] = useState(0);
  const [fogStormZoomProgress, setFogStormZoomProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(0);
  const [videoInteract, setVideoInteract] = useState(false);
  const [, setVideoUiTick] = useState(0);
  const [infoItemIndex, setInfoItemIndex] = useState(null);
  const [focusedInfoVisible, setFocusedInfoVisible] = useState(false);
  const [focusedInfoHideSignal, setFocusedInfoHideSignal] = useState(0);
  const [focusedInfoTerminalFlashActive, setFocusedInfoTerminalFlashActive] = useState(false);
  const [showTerminalPanel, setShowTerminalPanel] = useState(false);
  const [terminalBots, setTerminalBots] = useState([]);
  const [terminalDefaulterFaqs, setTerminalDefaulterFaqs] = useState(() => getDefaulterTerminalFaqs());
  const [selectedTerminalBotId, setSelectedTerminalBotId] = useState('');
  const [terminalFaqSelection, setTerminalFaqSelection] = useState('');
  const [terminalLines, setTerminalLines] = useState(() => ([
    {
      id: 'gallery-terminal-boot',
      speaker: 'system',
      text: 'DEBOTS terminal ready.',
      createdAt: Date.now(),
      botName: 'DEBOTS',
    }
  ]));
  const [terminalUserLabel, setTerminalUserLabel] = useState(
    typeof window !== 'undefined' && window.localStorage.getItem('sessionToken') ? 'Admin' : 'Bruker'
  );
  const [terminalTypewriterNow, setTerminalTypewriterNow] = useState(() => Date.now());
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [orderForm, setOrderForm] = useState(initialOrderForm);
  const [sendingOrder, setSendingOrder] = useState(false);
  const [orderMessage, setOrderMessage] = useState('');
  const [cartItems, setCartItems] = useState(() => loadMerchCart());
  const [showCartModal, setShowCartModal] = useState(false);
  const [showMerchVariantModal, setShowMerchVariantModal] = useState(false);
  const [cartFeedback, setCartFeedback] = useState('');
  const [merchandiseMeta, setMerchandiseMeta] = useState(null);
  const [selectedVariantValues, setSelectedVariantValues] = useState({});
  const [imageTierUiTick, setImageTierUiTick] = useState(0);
  const [showObjectViewer, setShowObjectViewer] = useState(false);
  const [viewedObjectIndex, setViewedObjectIndex] = useState(null);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : true
  );
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1000 : false
  );
  const centerOnRef = useRef(() => {});
  const scrollFocusedTextBoxRef = useRef(() => false);
  const squareClickStateRef = useRef(new Map());
  const focusedInfoVisibleRef = useRef(false);
  const focusedInfoHideSignalRef = useRef(0);
  const focusedInfoTerminalFlashTimeoutRef = useRef(0);
  const focusedInfoActivityTimeoutsRef = useRef([]);
  const focusedInfoActivitySequenceRef = useRef(0);
  const recenterOnceRef = useRef(null);
  const fogFocusZoomStateRef = useRef({ value: 0, lastEmit: 0 });
  const fogStormZoomStateRef = useRef({ value: 0, lastEmit: 0 });
  const imageTierStatusRef = useRef(new Map());
  const imageTierStatusRafRef = useRef(0);
  const imageTierTerminalProgressRef = useRef({ key: '', tier: '', bucket: -1 });
  const terminalLogsRef = useRef(null);
  const terminalFaqAnswerTimerRef = useRef(0);
  const thrusterZoomOutRef = useRef(() => '');
  const transitionRef = useRef({ active: false, mode: 'pan-then-zoom', panRatio: 0.58, start: 0, duration: 1000, from: new THREE.Vector3(), to: new THREE.Vector3(), fromLook: new THREE.Vector3(), toLook: new THREE.Vector3(), fromZ: 0, toZ: 0 });

  useEffect(() => {
    const updateViewportState = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsLandscape(width > height);
      setIsMobileViewport(width <= 1000);
    };

    updateViewportState();
    window.addEventListener('resize', updateViewportState);
    window.addEventListener('orientationchange', updateViewportState);

    return () => {
      window.removeEventListener('resize', updateViewportState);
      window.removeEventListener('orientationchange', updateViewportState);
    };
  }, []);

  useEffect(() => {
    const syncTerminalUserLabel = () => {
      if (typeof window === 'undefined') return;
      setTerminalUserLabel(window.localStorage.getItem('sessionToken') ? 'Admin' : 'Bruker');
    };

    syncTerminalUserLabel();
    window.addEventListener('storage', syncTerminalUserLabel);
    window.addEventListener('focus', syncTerminalUserLabel);

    return () => {
      window.removeEventListener('storage', syncTerminalUserLabel);
      window.removeEventListener('focus', syncTerminalUserLabel);
    };
  }, []);

  useEffect(() => {
    const syncBots = () => {
      const nextBots = getDebotConfigs();
      const fallbackBotId = (nextBots.find((bot) => bot.online)?.id) || (nextBots[0]?.id || '');
      setTerminalBots(nextBots);
      setSelectedTerminalBotId((prev) => (nextBots.some((bot) => bot.id === prev && bot.online) ? prev : fallbackBotId));
    };

    syncBots();
    return subscribeDebotConfigs(syncBots);
  }, []);

  useEffect(() => {
    saveMerchCart(cartItems);
  }, [cartItems]);

  useEffect(() => {
    if (!cartFeedback) return;
    const t = setTimeout(() => setCartFeedback(''), 1800);
    return () => clearTimeout(t);
  }, [cartFeedback]);

  useEffect(() => {
    if (embedded) return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [embedded]);

  useEffect(() => {
    if (!galleryId) return;
    let active = true;
    (async () => {
      setLoading(true); setError(null); setDataLoaded(false);
      try {
        const id = decodeURIComponent(galleryId);
        if (isMerchandiseMode) {
          const merchData = await fetchJSON(`/api/merchandise/products/${encodeURIComponent(id)}/public-gallery`, { cache: 'no-store' });
          if (!active) return;
          const layout = merchData?.layout || {};
          const cols = typeof layout.cols === 'number' ? Math.max(1, Math.min(12, layout.cols)) : 4;
          const spacingX = typeof layout.spacingX === 'number' ? Math.max(0.5, Math.min(40, layout.spacingX)) : 2.1;
          const initialZoom = typeof layout.initialZoom === 'number' ? Math.max(0, Math.min(1, layout.initialZoom)) : 0.5;
          const initialDistance = typeof layout.initialDistance === 'number' ? Math.max(0.5, Math.min(40, layout.initialDistance)) : 24;
          const distanceBlur = typeof layout.distanceBlur === 'number' ? Math.max(0, Math.min(120, layout.distanceBlur)) : 0;
          const startIndex = Math.max(0, Math.round(Number(layout.startIndex) || 0));
          const bgColors = normalizeCategoryBgColors(layout.bgColors, [layout.bg1, layout.bg2]);
          const bgGradientType = normalizeCategoryGradientType(layout.bgGradientType, 'linear');
          const bgBlendMode = normalizeBgBlendMode(layout.bgBlendMode, 0);
          const fog = normalizeFogConfig(layout);
          const lightSettings = normalizeCategoryLightSettings(layout.lightSettings, {});
          window.__dse_gallery_layout__ = {
            cols,
            spacingX,
            initialZoom,
            initialDistance,
            distanceBlur,
            startIndex,
            bg1: bgColors[0],
            bg2: bgColors[1] || bgColors[0],
            bgColors,
            bgGradientType,
            bgBlendMode,
            lightSettings,
            ...fog,
          };

          const variantGroups = normalizeVariantGroups(merchData?.variantGroups);
          const variantStockRows = normalizeVariantStockRows(merchData?.variantStock, merchData?.stockOnHand);
          const shippingCostNokRaw = Number(merchData?.shippingCostNok);
          const shippingCostInternationalNokRaw = Number(merchData?.shippingCostInternationalNok);
          const shippingCostNok = Number.isFinite(shippingCostNokRaw) && shippingCostNokRaw >= 0
            ? Math.round(shippingCostNokRaw)
            : 75;
          const shippingCostInternationalNok = Number.isFinite(shippingCostInternationalNokRaw) && shippingCostInternationalNokRaw >= 0
            ? Math.round(shippingCostInternationalNokRaw)
            : 150;
          const totalAvailableStock = Number.isFinite(Number(merchData?.totalAvailableStock))
            ? Math.max(0, Math.round(Number(merchData.totalAvailableStock)))
            : variantStockRows.reduce((sum, row) => sum + Math.max(0, toSafeNonNegativeInteger(row.stockOnHand) - toSafeNonNegativeInteger(row.reservedCount)), 0);
          const variantDefaults = {};
          variantGroups.forEach((group) => {
            variantDefaults[group.title] = '';
          });
          setSelectedVariantValues(variantDefaults);
          setMerchandiseMeta({
            productId: String(merchData?.id || id),
            title: String(merchData?.title || '').trim(),
            description: String(merchData?.saleText || '').trim(),
            salePriceNok: Number(merchData?.salePriceNok || 0) || 0,
            shippingCostNok,
            shippingCostInternationalNok,
            stockOnHand: Math.max(0, Number(merchData?.stockOnHand || 0) || 0),
            totalAvailableStock,
            variantGroups,
            variantStockRows,
          });

          const imgs = (Array.isArray(merchData?.items) ? merchData.items : [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((it) => {
              const display = primaryImageUrl(it);
              return {
                ...it,
                kind: 'image',
                url: display || it.url,
                optimizedUrl: it.optimizedUrl || display || it.url,
                originalUrl: it.originalUrl || undefined,
                thumbUrl: it.thumbUrl || undefined,
              };
            });

          const descriptionText = String(merchData?.saleText || '').trim();
          const availabilityStatus = formatAvailabilityStatus(totalAvailableStock);
          const availabilityLines = totalAvailableStock > 0
            ? ['PÅ LAGER']
            : ['IKKE PÅ LAGER', 'LANG LEVERINGSTID'];
          const priceText = Number(merchData?.salePriceNok || 0) > 0 ? formatNok(merchData.salePriceNok) : 'Pris kommer';
          const infoCard = {
            _id: `${String(merchData?.id || id)}:merch-info-card`,
            kind: 'box',
            title: String(merchData?.title || 'Merchandise').trim() || 'Merchandise',
            description: descriptionText,
            widthSpan: 1,
            box: {
              color1: '#060606',
              color2: '#060606',
              text1: String(merchData?.title || 'MERCHANDISE').trim() || 'MERCHANDISE',
              text2: '',
              square: true,
              lines: [
                `Utsalgspris: ${priceText}`,
                ...availabilityLines,
                ...(descriptionText ? [`Beskrivelse: ${descriptionText}`] : []),
              ],
            },
          };

          const variantStatusCard = {
            _id: `${String(merchData?.id || id)}:merch-variant-status-card`,
            kind: 'box',
            title: 'Variantstatus',
            description: availabilityStatus,
            widthSpan: 1,
            box: {
              color1: '#060606',
              color2: '#060606',
              text1: 'VARIANTSTATUS',
              text2: '',
              square: true,
              table: buildMerchandiseVariantTable(variantGroups, variantStockRows),
            },
          };

          setImages([infoCard, ...imgs, variantStatusCard]);
          setVideos([]);
          setSelected(Math.min(Math.max(0, startIndex), Math.max(0, imgs.length + 1)));
          setDataLoaded(true);
          return;
        }

        setMerchandiseMeta(null);
        setSelectedVariantValues({});

        let cols = 4; let spacingX = 3; let initialZoom = 0.5; let initialDistance = 24; let distanceBlur = 0; let startIndex = 0;
        let bg1 = '#000000'; let bg2 = '#111111'; let bgColors = [bg1, bg2]; let bgGradientType = 'linear'; let bgBlendMode = 0;
        let fog = normalizeFogConfig({});
        let lightSettings = normalizeCategoryLightSettings({}, {});
        try {
          const cat = await fetchJSON(`/api/categories/${encodeURIComponent(id)}`, { cache: 'no-store' });
          if (typeof cat.cols === 'number') cols = Math.max(1, Math.min(12, cat.cols));
          if (typeof cat.spacingX === 'number') spacingX = Math.max(0.5, Math.min(40, cat.spacingX));
          if (typeof cat.initialZoom === 'number') initialZoom = Math.max(0, Math.min(1, cat.initialZoom));
          if (typeof cat.initialDistance === 'number') initialDistance = Math.max(0.5, Math.min(40, cat.initialDistance));
          if (typeof cat.distanceBlur === 'number') distanceBlur = Math.max(0, Math.min(120, cat.distanceBlur));
          startIndex = Math.max(0, Math.round(Number(cat.startIndex) || 0));
          if (typeof cat.bg1 === 'string') bg1 = normalizeHexColor(cat.bg1, bg1);
          if (typeof cat.bg2 === 'string') bg2 = normalizeHexColor(cat.bg2, bg2);
          bgColors = normalizeCategoryBgColors(cat.bgColors, [bg1, bg2]);
          bg1 = bgColors[0];
          bg2 = bgColors[1] || bgColors[0];
          bgGradientType = normalizeCategoryGradientType(cat.bgGradientType, bgGradientType);
          bgBlendMode = normalizeBgBlendMode(cat.bgBlendMode, 0);
          fog = normalizeFogConfig(cat);
          lightSettings = normalizeCategoryLightSettings(cat.lightSettings, lightSettings);
        } catch {}
        const imgData = await fetchJSON(`/api/gallery/${encodeURIComponent(id)}/images`, { cache: 'no-store' });
        let vidData = { id, title: id, items: [] };
        try {
          vidData = await fetchJSON(`/api/gallery/${encodeURIComponent(id)}/videos`, { cache: 'no-store' });
        } catch {}
        if (!active) return;
        const imgs = (imgData.items || []).slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(it => {
            if (it.kind === 'box') return it;
            const display = primaryImageUrl(it);
            return {
              ...it,
              url: display || it.url,
              optimizedUrl: it.optimizedUrl || display || it.url,
              originalUrl: it.originalUrl || undefined,
              thumbUrl: it.thumbUrl || undefined
            };
          });
        const vids = (vidData.items || [])
          .filter(v => v.gallery === id)
          .map(v => {
            const sourceUrl = v.optimizedUrl || v.storageUrl;
            const yt = v.youtubeId || youtubeIdFromUrl(sourceUrl);
            return {
              ...v,
              storageUrl: sourceUrl || v.storageUrl,
              optimizedUrl: v.optimizedUrl || sourceUrl,
              originalUrl: v.originalUrl || v.storageUrl,
              source: yt ? 'youtube' : 'asset',
              youtubeId: yt || v.youtubeId
            };
          });
        window.__dse_gallery_layout__ = { cols, spacingX, initialZoom, initialDistance, distanceBlur, startIndex, bg1, bg2, bgColors, bgGradientType, bgBlendMode, lightSettings, ...fog };
        const initialSelected = Math.min(Math.max(0, startIndex), Math.max(0, (imgs.length + vids.length) - 1));

        let nextImages = imgs;
        let nextVideos = vids;
        let nextSelected = initialSelected;

        if (embedded) {
          const requestedItemId = String(embeddedItemId || '').trim();
          const resolveEntryIds = (entry, index, kind) => {
            const ids = [
              entry?._id,
              entry?.id,
              `${id}:${kind}:${index}`,
              `${id}:${index}`,
              `${id}::${index}`,
            ];
            return ids
              .map((value) => String(value || '').trim())
              .filter(Boolean);
          };

          let selectedEntry = null;
          let selectedEntryIsVideo = false;

          if (requestedItemId) {
            const imageMatchIndex = imgs.findIndex((entry, index) => resolveEntryIds(entry, index, 'image').includes(requestedItemId));
            if (imageMatchIndex >= 0) {
              selectedEntry = imgs[imageMatchIndex];
              selectedEntryIsVideo = false;
            }

            if (!selectedEntry) {
              const videoMatchIndex = vids.findIndex((entry, index) => resolveEntryIds(entry, index, 'video').includes(requestedItemId));
              if (videoMatchIndex >= 0) {
                selectedEntry = vids[videoMatchIndex];
                selectedEntryIsVideo = true;
              }
            }
          }

          if (!selectedEntry) {
            const firstSquareBox = imgs.find((entry) => !!entry?.box?.square && !isLineBreakItem(entry));
            const firstRenderableImage = imgs.find((entry) => !isLineBreakItem(entry));
            if (firstSquareBox || firstRenderableImage) {
              selectedEntry = firstSquareBox || firstRenderableImage;
              selectedEntryIsVideo = false;
            } else if (vids[0]) {
              selectedEntry = vids[0];
              selectedEntryIsVideo = true;
            }
          }

          if (selectedEntry) {
            if (selectedEntryIsVideo) {
              nextImages = [];
              nextVideos = [selectedEntry];
            } else {
              nextImages = [selectedEntry];
              nextVideos = [];
            }
          } else {
            nextImages = [];
            nextVideos = [];
          }

          nextSelected = 0;
          window.__dse_gallery_layout__ = {
            ...(window.__dse_gallery_layout__ || {}),
            startIndex: 0,
          };
        }

        setImages(nextImages);
        setVideos(nextVideos);
        setSelected(nextSelected);
        setDataLoaded(true);
      } catch (e) {
        if (!active) return;
        try {
          const id = decodeURIComponent(galleryId);
          const res = await fetch('/galleries.json', { cache: 'no-store' });
          if (res.ok) {
            const data = await res.json();
            const entry = Array.isArray(data?.galleries) ? data.galleries.find(g => String(g.id) === id) : null;
            if (entry && Array.isArray(entry.items)) {
              const imgs = entry.items.map((u, i) => ({ _id: `${id}::${i}`, url: u, title: '', description: '', status: 'published' }));
              window.__dse_gallery_layout__ = {
                cols: 4,
                spacingX: 3,
                initialZoom: 0.5,
                initialDistance: 24,
                distanceBlur: 0,
                startIndex: 0,
                bg1: '#000000',
                bg2: '#111111',
                bgColors: ['#000000', '#111111'],
                bgGradientType: 'linear',
                bgBlendMode: 0,
                lightSettings: normalizeCategoryLightSettings({}, {}),
                ...normalizeFogConfig({}),
              };
              setImages(imgs); setVideos([]); setSelected(0); setDataLoaded(true);
              return;
            }
          }
        } catch {}
        setError(e?.message || 'Failed to load gallery');
        if (active) {
          setLoading(false);
          setFirstPaintReady(true);
          setDataLoaded(true);
        }
      }
    })();
    return () => { active = false; };
  }, [galleryId, isMerchandiseMode, embedded, embeddedItemId]);

  useEffect(() => {
    if (!embedded) return;
    if (!firstPaintReady) return;
    if (typeof onEmbeddedReady === 'function') onEmbeddedReady();
  }, [embedded, firstPaintReady, onEmbeddedReady]);

  useEffect(() => {
    setFocusedInfoVisible(false);
    setInfoItemIndex(null);
    setShowTerminalPanel(false);
    setTerminalFaqSelection('');
    setFocusedInfoTerminalFlashActive(false);
    focusedInfoActivitySequenceRef.current += 1;
    if (typeof window !== 'undefined' && Array.isArray(focusedInfoActivityTimeoutsRef.current)) {
      focusedInfoActivityTimeoutsRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      focusedInfoActivityTimeoutsRef.current = [];
    }
    if (typeof window !== 'undefined' && focusedInfoTerminalFlashTimeoutRef.current) {
      window.clearTimeout(focusedInfoTerminalFlashTimeoutRef.current);
      focusedInfoTerminalFlashTimeoutRef.current = 0;
    }

    if (typeof window !== 'undefined' && terminalFaqAnswerTimerRef.current) {
      window.clearTimeout(terminalFaqAnswerTimerRef.current);
      terminalFaqAnswerTimerRef.current = 0;
    }
  }, [galleryId]);

  useEffect(() => {
    focusedInfoVisibleRef.current = !!focusedInfoVisible;
  }, [focusedInfoVisible]);

  useEffect(() => {
    focusedInfoHideSignalRef.current = Number(focusedInfoHideSignal) || 0;
  }, [focusedInfoHideSignal]);

  useEffect(() => (
    () => {
      focusedInfoActivitySequenceRef.current += 1;
      if (typeof window !== 'undefined' && Array.isArray(focusedInfoActivityTimeoutsRef.current)) {
        focusedInfoActivityTimeoutsRef.current.forEach((timerId) => {
          window.clearTimeout(timerId);
        });
        focusedInfoActivityTimeoutsRef.current = [];
      }
      if (typeof window !== 'undefined' && focusedInfoTerminalFlashTimeoutRef.current) {
        window.clearTimeout(focusedInfoTerminalFlashTimeoutRef.current);
        focusedInfoTerminalFlashTimeoutRef.current = 0;
      }

      if (typeof window !== 'undefined' && terminalFaqAnswerTimerRef.current) {
        window.clearTimeout(terminalFaqAnswerTimerRef.current);
        terminalFaqAnswerTimerRef.current = 0;
      }
    }
  ), []);

  const selectedTerminalBot = useMemo(() => {
    if (!terminalBots.length) return null;
    return terminalBots.find((bot) => bot.id === selectedTerminalBotId) || terminalBots[0] || null;
  }, [terminalBots, selectedTerminalBotId]);

  const terminalFaqOptions = useMemo(
    () => (Array.isArray(terminalDefaulterFaqs)
      ? terminalDefaulterFaqs.filter((faq) => String(faq?.question || '').trim())
      : []),
    [terminalDefaulterFaqs]
  );

  useEffect(() => {
    const syncDefaulterFaqs = () => {
      setTerminalDefaulterFaqs(getDefaulterTerminalFaqs());
    };

    syncDefaulterFaqs();
    return subscribeDefaulterTerminalFaqs(syncDefaulterFaqs);
  }, []);

  useEffect(() => {
    if (!terminalFaqSelection) return;
    const exists = terminalFaqOptions.some((faq) => faq.question === terminalFaqSelection);
    if (!exists) setTerminalFaqSelection('');
  }, [terminalFaqOptions, terminalFaqSelection]);

  useEffect(() => {
    const node = terminalLogsRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [terminalLines, showTerminalPanel]);

  useEffect(() => {
    if (!showTerminalPanel) return undefined;
    const timer = window.setInterval(() => {
      setTerminalTypewriterNow(Date.now());
    }, 28);

    return () => {
      window.clearInterval(timer);
    };
  }, [showTerminalPanel, terminalLines.length]);

  const items = useMemo(() => [
    ...images
      .map((i) => ({
        ...i,
        kind: isLineBreakItem(i) ? 'linebreak' : (i.kind === 'box' ? 'box' : 'image')
      }))
      .filter((i) => i.kind !== 'linebreak'),
    ...videos.map(v => ({ ...v, kind: 'video' }))
  ], [images, videos]);

  const lineBreaksBeforeItem = useMemo(() => {
    const breaks = new Map();
    let renderIndex = 0;
    for (const sourceItem of images) {
      const kind = isLineBreakItem(sourceItem)
        ? 'linebreak'
        : (sourceItem?.kind === 'box' ? 'box' : 'image');
      if (kind === 'linebreak') {
        breaks.set(renderIndex, (breaks.get(renderIndex) || 0) + 1);
      } else {
        renderIndex += 1;
      }
    }
    return breaks;
  }, [images]);

  useEffect(() => {
    const next = new Map();
    items.forEach((entry, index) => {
      if (entry?.kind !== 'image') return;
      next.set(galleryImageStatusKey(entry, index), {
        tier: 'LOFI',
        progress: 0,
        visible: true,
      });
    });
    imageTierStatusRef.current = next;
    if (imageTierStatusRafRef.current) {
      window.cancelAnimationFrame(imageTierStatusRafRef.current);
      imageTierStatusRafRef.current = 0;
    }
    imageTierTerminalProgressRef.current = { key: '', tier: '', bucket: -1 };
    setImageTierUiTick((tick) => tick + 1);
  }, [items, galleryId]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (items.length === 0) {
      setFirstPaintReady(true);
      setLoading(false);
    }
  }, [dataLoaded, items.length]);

  useEffect(() => {
    if (firstPaintReady && !loading) {
      setFadePhase('idle');
      const t1 = setTimeout(() => setFadePhase('fading'), 0);
      const t2 = setTimeout(() => setFadePhase('hidden'), 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    setFadePhase('idle');
  }, [loading, firstPaintReady]);

  useEffect(() => {
    setFogIntroProgress(0);
    let raf = 0;
    let start = null;
    const durationMs = 3000;
    const tick = (timestamp) => {
      if (start == null) start = timestamp;
      const ratio = Math.max(0, Math.min(1, (timestamp - start) / durationMs));
      setFogIntroProgress(ratio);
      if (ratio < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [galleryId]);

  useEffect(() => {
    if (loading) return;
    if (assetsPending !== 0) return;
    const key = String(galleryId || '');
    if (recenterOnceRef.current === key) return;
    recenterOnceRef.current = key;
    const runtimeLayout = window.__dse_gallery_layout__ || {};
    const startIndex = Math.min(
      Math.max(0, Math.round(Number(runtimeLayout.startIndex) || 0)),
      Math.max(0, items.length - 1)
    );
    try {
      setSelected(startIndex);
      centerOnRef.current?.(startIndex, true, !isMerchandiseMode);
    } catch {}
  }, [loading, assetsPending, galleryId, isMerchandiseMode, items.length]);

  useEffect(() => {
    if (!containerRef.current || !overlayRootRef.current) return;
    if (items.length === 0) return;
    fogFocusZoomStateRef.current = { value: 0, lastEmit: 0 };
    fogStormZoomStateRef.current = { value: 0, lastEmit: 0 };
    setFogFocusZoomProgress(0);
    setFogStormZoomProgress(0);
    const mount = containerRef.current; const overlay = overlayRootRef.current;
    const layoutCfg = window.__dse_gallery_layout__ || {};
    const backgroundCss = embedded ? 'transparent' : buildCategoryBackgroundCss(layoutCfg);
    const previousMountBackground = mount.style.background;
    mount.style.background = backgroundCss;

    const scene = new THREE.Scene(); scene.background = null;
    const width = mount.clientWidth, height = mount.clientHeight;
    const baseFov = 55; const camera = new THREE.PerspectiveCamera(baseFov, width / height, 0.1, 2000); camera.position.set(0, 0, 4);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); renderer.setSize(width, height); renderer.setPixelRatio(window.devicePixelRatio); renderer.setClearColor(0x000000, 0); renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.03;
    if ('outputEncoding' in renderer && THREE.sRGBEncoding) {
      renderer.outputEncoding = THREE.sRGBEncoding;
    }
    mount.appendChild(renderer.domElement);
    const root = new THREE.Group(); scene.add(root);
    const galleryLightSettings = normalizeCategoryLightSettings(layoutCfg.lightSettings, {});
    const supportLightScale = galleryLightSettings.type === 'directional' ? 1 : 0.72;
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.24);
    const hemisphereLight = new THREE.HemisphereLight(0xe6efff, 0x171717, galleryLightSettings.type === 'hemisphere' ? 0.2 : 0.32);
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.26 * supportLightScale);
    keyLight.position.set(5.2, 4.2, 10);
    const fillLight = new THREE.DirectionalLight(0xc3d7ff, 0.24 * supportLightScale);
    fillLight.position.set(-6.2, 2.1, 7.6);
    const rimLight = new THREE.DirectionalLight(0xfff0dc, 0.18 * supportLightScale);
    rimLight.position.set(2.5, -3.2, -4.8);
    const lightTarget = new THREE.Object3D();
    scene.add(lightTarget);

    let configurableLight = null;
    if (galleryLightSettings.type === 'point') {
      configurableLight = new THREE.PointLight(
        galleryLightSettings.color,
        galleryLightSettings.intensity,
        galleryLightSettings.pointDistance,
        galleryLightSettings.pointDecay,
      );
      configurableLight.position.set(0, 5.2, 9.4);
    } else if (galleryLightSettings.type === 'spot') {
      const spot = new THREE.SpotLight(
        galleryLightSettings.color,
        galleryLightSettings.intensity,
        120,
        THREE.MathUtils.degToRad(galleryLightSettings.spotAngle),
        galleryLightSettings.spotPenumbra,
        1.2,
      );
      spot.position.set(0, 8.1, 13.2);
      spot.target = lightTarget;
      configurableLight = spot;
    } else if (galleryLightSettings.type === 'hemisphere') {
      const hemisphereGroundColor = new THREE.Color(galleryLightSettings.color)
        .lerp(new THREE.Color('#050505'), galleryLightSettings.hemisphereGroundMix);
      const hemisphere = new THREE.HemisphereLight(
        galleryLightSettings.color,
        hemisphereGroundColor,
        galleryLightSettings.intensity,
      );
      hemisphere.position.set(0, galleryLightSettings.hemisphereHeight * 10, 0);
      configurableLight = hemisphere;
    } else {
      const directional = new THREE.DirectionalLight(galleryLightSettings.color, galleryLightSettings.intensity);
      directional.position.set(6, 8, 7);
      directional.target = lightTarget;
      configurableLight = directional;
    }
    scene.add(ambientLight, hemisphereLight, keyLight, fillLight, rimLight);
    if (configurableLight) scene.add(configurableLight);
    const cols = Math.max(1, Math.min(12, layoutCfg.cols || 4));
    const initialZoom = typeof layoutCfg.initialZoom === 'number' ? layoutCfg.initialZoom : 0.5;
    const initialDistance = typeof layoutCfg.initialDistance === 'number' ? layoutCfg.initialDistance : null;
    const configuredStartIndex = Math.min(
      Math.max(0, Math.round(Number(layoutCfg.startIndex) || 0)),
      Math.max(0, items.length - 1)
    );
    const useCircularLayout = false;
    const planeH = 2.2, spacingX = Math.max(0.5, Math.min(40, layoutCfg.spacingX || 2.1)), marginY = 0.6, depthZ = -20;
    const distanceBlurStrength = clampRange(layoutCfg.distanceBlur, 0, 0, 120);
    const loader = new THREE.TextureLoader(); loader.setCrossOrigin?.('anonymous');
    const planes = []; const iframes = new Array(items.length).fill(null); const localVideos = new Array(items.length).fill(null);
    let activeIndex = configuredStartIndex;
    const imageRuntimeByIndex = new Map();
    let focusedUpgradeSequence = 0;
    let focusedUpgradeRunning = false;
    let focusedUpgradeQueued = false;
    let effectDisposed = false;
    const isEffectDisposed = () => effectDisposed;

    const queueImageTierUiRefresh = () => {
      if (imageTierStatusRafRef.current) return;
      imageTierStatusRafRef.current = window.requestAnimationFrame(() => {
        imageTierStatusRafRef.current = 0;
        setImageTierUiTick((tick) => tick + 1);
      });
    };

    const setImageTierStatus = (key, updates = {}) => {
      if (!key) return;
      const current = imageTierStatusRef.current.get(key) || { tier: 'LOFI', progress: 0, visible: true };
      const next = {
        tier: updates.tier || current.tier || 'LOFI',
        progress: clampRange(
          updates.progress,
          Number.isFinite(Number(current.progress)) ? Number(current.progress) : 0,
          0,
          1
        ),
        visible: updates.visible == null ? (current.visible !== false) : !!updates.visible,
      };
      if (
        current.tier === next.tier
        && Math.abs((Number(current.progress) || 0) - (Number(next.progress) || 0)) < 0.0001
        && current.visible === next.visible
      ) {
        return;
      }
      imageTierStatusRef.current.set(key, next);
      queueImageTierUiRefresh();
    };

    const hideImageTierStatusSoon = (runtime, delayMs = 620) => {
      if (!runtime) return;
      if (runtime.hideStatusTimer) {
        window.clearTimeout(runtime.hideStatusTimer);
        runtime.hideStatusTimer = 0;
      }
      runtime.hideStatusTimer = window.setTimeout(() => {
        runtime.hideStatusTimer = 0;
        setImageTierStatus(runtime.statusKey, { tier: 'HIFI', progress: 1, visible: false });
      }, Math.max(0, Math.round(Number(delayMs) || 0)));
    };

    const clearImageTierStatusTimer = (runtime) => {
      if (!runtime || !runtime.hideStatusTimer) return;
      window.clearTimeout(runtime.hideStatusTimer);
      runtime.hideStatusTimer = 0;
    };

    const loadTextureAsync = (url) => new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error('missing_texture_url'));
        return;
      }
      loader.load(url, resolve, undefined, reject);
    });

    const downgradeImageRuntimeToLofi = (runtime) => {
      if (!runtime || !runtime.mesh || !runtime.lowTexture) return;
      if (runtime.currentTier === 'LOFI') {
        runtime.activeTexture = runtime.lowTexture;
        setImageTierStatus(runtime.statusKey, { tier: 'LOFI', progress: 0.36, visible: true });
        return;
      }
      applyMediaSurfaceMaterials(runtime.mesh, runtime.lowTexture, runtime.mediaBoxOptions);
      runtime.currentTier = 'LOFI';
      runtime.activeTexture = runtime.lowTexture;
      clearImageTierStatusTimer(runtime);
      setImageTierStatus(runtime.statusKey, { tier: 'LOFI', progress: 0.36, visible: true });
    };

    const applyImageTierTexture = (runtime, texture, tier) => {
      if (!runtime || !runtime.mesh || !texture) return;
      const textureChanged = runtime.activeTexture !== texture;
      const tierChanged = runtime.currentTier !== tier;

      runtime.mesh.visible = tier === 'HIFI';

      if (tier === 'LOFI') {
        if (textureChanged || tierChanged) {
          applyMediaSurfaceMaterials(runtime.mesh, texture, runtime.mediaBoxOptions);
        }
      } else if (textureChanged) {
        applyUpgradeFlash(runtime.mesh, runtime.mesh.material, texture);
      }

      runtime.currentTier = tier;
      runtime.activeTexture = texture;
      if (tier === 'WEB') {
        clearImageTierStatusTimer(runtime);
        setImageTierStatus(runtime.statusKey, { tier: 'WEB', progress: 0.78, visible: true });
      } else if (tier === 'HIFI') {
        runtime.mesh.visible = true;
        setImageTierStatus(runtime.statusKey, { tier: 'HIFI', progress: 1, visible: true });
        hideImageTierStatusSoon(runtime, 700);
      } else {
        clearImageTierStatusTimer(runtime);
        setImageTierStatus(runtime.statusKey, { tier: 'LOFI', progress: 0.36, visible: true });
      }
    };

    const upgradeFocusedImageRuntime = async (runtime, sequenceId) => {
      if (!runtime || !runtime.lowTexture || effectDisposed || sequenceId !== focusedUpgradeSequence) return;

      if (
        runtime.currentTier === 'HIFI'
        && runtime.hiTexture
        && runtime.activeTexture === runtime.hiTexture
      ) {
        setImageTierStatus(runtime.statusKey, { tier: 'HIFI', progress: 1, visible: false });
        return;
      }

      if (!runtime.webTexture) {
        if (runtime.webUrl && runtime.webUrl !== runtime.lowUrl) {
          setImageTierStatus(runtime.statusKey, { tier: 'WEB', progress: 0.56, visible: true });
          try {
            const webTexture = await loadTextureAsync(runtime.webUrl);
            if (effectDisposed || sequenceId !== focusedUpgradeSequence || activeIndex !== runtime.index) {
              try { webTexture.dispose?.(); } catch {}
              return;
            }
            prepareImageTexture(webTexture, renderer);
            runtime.webTexture = webTexture;
          } catch {
            runtime.webTexture = runtime.lowTexture;
          }
        } else {
          runtime.webTexture = runtime.lowTexture;
        }
      }

      if (effectDisposed || sequenceId !== focusedUpgradeSequence || activeIndex !== runtime.index) return;

      if (runtime.currentTier === 'LOFI' && runtime.webTexture) {
        applyImageTierTexture(runtime, runtime.webTexture, 'WEB');
      }

      if (runtime.hiTexture) {
        if (runtime.currentTier !== 'HIFI' || runtime.activeTexture !== runtime.hiTexture) {
          applyImageTierTexture(runtime, runtime.hiTexture, 'HIFI');
        } else {
          setImageTierStatus(runtime.statusKey, { tier: 'HIFI', progress: 1, visible: false });
        }
        return;
      }

      const resolvedWebUrl = runtime.webUrl || runtime.lowUrl;
      const hiUrl = runtime.hiUrl;
      if (!hiUrl || hiUrl === resolvedWebUrl || hiUrl === runtime.lowUrl) {
        runtime.hiTexture = runtime.webTexture || runtime.lowTexture;
        if (runtime.hiTexture && (runtime.currentTier !== 'HIFI' || runtime.activeTexture !== runtime.hiTexture)) {
          applyImageTierTexture(runtime, runtime.hiTexture, 'HIFI');
        }
        return;
      }

      setImageTierStatus(runtime.statusKey, { tier: 'HIFI', progress: 0.9, visible: true });
      try {
        const hiTexture = await loadTextureAsync(hiUrl);
        if (effectDisposed || sequenceId !== focusedUpgradeSequence || activeIndex !== runtime.index) {
          try { hiTexture.dispose?.(); } catch {}
          return;
        }
        prepareImageTexture(hiTexture, renderer);
        runtime.hiTexture = hiTexture;
        applyImageTierTexture(runtime, hiTexture, 'HIFI');
      } catch {
        if (runtime.webTexture) {
          applyImageTierTexture(runtime, runtime.webTexture, 'WEB');
        }
      }
    };

    const processFocusedUpgrade = () => {
      if (focusedUpgradeRunning) {
        focusedUpgradeQueued = true;
        return;
      }
      const runtime = imageRuntimeByIndex.get(activeIndex);
      if (!runtime || !runtime.lowTexture) return;
      if (
        runtime.currentTier === 'HIFI'
        && runtime.hiTexture
        && runtime.activeTexture === runtime.hiTexture
      ) {
        setImageTierStatus(runtime.statusKey, { tier: 'HIFI', progress: 1, visible: false });
        return;
      }

      focusedUpgradeRunning = true;
      focusedUpgradeQueued = false;
      focusedUpgradeSequence += 1;
      const sequenceId = focusedUpgradeSequence;

      upgradeFocusedImageRuntime(runtime, sequenceId)
        .catch(() => {})
        .finally(() => {
          focusedUpgradeRunning = false;
          if (focusedUpgradeQueued && !effectDisposed) {
            processFocusedUpgrade();
          }
        });
    };

    const syncImageTierForFocus = () => {
      imageRuntimeByIndex.forEach((runtime, runtimeIndex) => {
        if (!runtime) return;
        if (runtimeIndex === activeIndex) return;
        downgradeImageRuntimeToLofi(runtime);
      });
      processFocusedUpgrade();
    };

    const animatedBoxPainters = [];
    const squareIconImageCache = new Map();
    const focusedInfoEnabled = () => (!isMerchandiseMode && focusedInfoVisibleRef.current);
    const focusedInfoGroup = new THREE.Group();
    const focusedInfoFaceCameraMeshes = [];
    const focusedInfoRows = [];
    const focusedInfoRuntime = {
      signature: '',
      index: -1,
      startedAt: 0,
      typingStartsAt: 0,
      revealedChars: -1,
      totalChars: 0,
      hideActive: false,
      hideStartedAt: 0,
      hideDurationMs: FOCUSED_INFO_HIDE_TRANSITION_MS,
      lastHideSignal: Number(focusedInfoHideSignalRef.current) || 0,
    };
    scene.add(focusedInfoGroup);
    const squareClickStateMap = squareClickStateRef.current;
    const currentSquareKeys = new Set();
    for (let index = 0; index < items.length; index += 1) {
      if (!items[index]?.box?.square) continue;
      currentSquareKeys.add(getSquareCanvasStateKey(items[index], index, galleryId));
    }
    for (const existingKey of Array.from(squareClickStateMap.keys())) {
      if (!currentSquareKeys.has(existingKey)) {
        squareClickStateMap.delete(existingKey);
      }
    }

    const resolveSquareStateForEntry = (entry, index) => {
      const key = getSquareCanvasStateKey(entry, index, galleryId);
      let state = squareClickStateMap.get(key);
      if (!state) {
        state = { activeFrameIndex: -1, transition: null };
        squareClickStateMap.set(key, state);
      }
      return state;
    };

    const resolveSquareFrameForState = (extras, state, timestampMs = performance.now()) => {
      const baseFrame = createSquareVisualFrameFromExtras(extras);
      if (!state || typeof state !== 'object') return baseFrame;

      const availableFrames = Array.isArray(extras.clickAnimations) ? extras.clickAnimations : [];
      if (!availableFrames.length) {
        state.activeFrameIndex = -1;
        state.transition = null;
        return baseFrame;
      }

      if (state.activeFrameIndex >= availableFrames.length) {
        state.activeFrameIndex = availableFrames.length - 1;
      }

      let effectiveFrame = state.activeFrameIndex >= 0
        ? normalizeSquareClickAnimationFrame(availableFrames[state.activeFrameIndex], baseFrame)
        : baseFrame;

      if (state.transition?.to) {
        const fromFrame = normalizeSquareClickAnimationFrame(state.transition.from || effectiveFrame, effectiveFrame);
        const toFrame = normalizeSquareClickAnimationFrame(state.transition.to, fromFrame);
        const durationMs = Math.max(50, normalizeSquareClickAnimationTransitionMs(state.transition.durationMs, toFrame.transitionMs));
        const progress = clampRange((timestampMs - Number(state.transition.startMs || 0)) / durationMs, 1, 0, 1);
        effectiveFrame = interpolateSquareVisualFrames(fromFrame, toFrame, progress);
        if (progress >= 0.999) {
          state.transition = null;
          effectiveFrame = toFrame;
        }
      }

      return effectiveFrame;
    };

    const advanceSquareClickAnimation = (index, timestampMs = performance.now()) => {
      const entry = items[index];
      if (!entry?.box?.square) return false;

      const extras = normalizeSquareBoxExtras(entry.box || {}, entry.box || {});
      const clickFrames = Array.isArray(extras.clickAnimations) ? extras.clickAnimations : [];
      if (!clickFrames.length) return false;

      const state = resolveSquareStateForEntry(entry, index);
      const currentFrame = resolveSquareFrameForState(extras, state, timestampMs);
      const nextIndex = state.activeFrameIndex < 0
        ? 0
        : ((state.activeFrameIndex + 1) % clickFrames.length);
      const targetFrame = normalizeSquareClickAnimationFrame(clickFrames[nextIndex], createSquareVisualFrameFromExtras(extras));

      state.activeFrameIndex = nextIndex;
      state.transition = {
        from: currentFrame,
        to: targetFrame,
        startMs: timestampMs,
        durationMs: normalizeSquareClickAnimationTransitionMs(targetFrame.transitionMs, 650),
      };
      return true;
    };

    const cursorFx = document.createElement('div');
    const cursorFxRing = document.createElement('span');
    const cursorFxCore = document.createElement('span');
    const cursorFxGlyph = document.createElement('span');

    Object.assign(cursorFx.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      width: '34px',
      height: '34px',
      transform: 'translate3d(-50%, -50%, 0) scale(0.62) rotate(-14deg)',
      transformOrigin: '50% 50%',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '9',
      borderRadius: '999px',
      filter: 'blur(1.7px) saturate(1.1) hue-rotate(0deg)',
      transition: 'opacity 200ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms ease',
      willChange: 'transform, opacity, filter',
      mixBlendMode: 'screen',
      display: 'none',
    });

    Object.assign(cursorFxRing.style, {
      position: 'absolute',
      inset: '-6px',
      borderRadius: '999px',
      background: 'conic-gradient(from 0deg, rgba(154,244,255,0.54), rgba(255,168,236,0.34), rgba(183,255,190,0.36), rgba(154,244,255,0.54))',
      opacity: '0.42',
      filter: 'blur(3px)',
      animation: 'dse-gallery-cursor-spin 6.4s linear infinite',
    });

    Object.assign(cursorFxCore.style, {
      position: 'absolute',
      inset: '5px',
      borderRadius: '999px',
      border: '1px solid rgba(255,255,255,0.62)',
      background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.24), rgba(120,220,255,0.06) 62%, rgba(0,0,0,0) 100%)',
      boxShadow: '0 0 14px rgba(118,208,255,0.22)',
      animation: 'dse-gallery-cursor-breathe 2.7s ease-in-out infinite',
    });

    Object.assign(cursorFxGlyph.style, {
      position: 'absolute',
      inset: '0',
      display: 'grid',
      placeItems: 'center',
      color: 'rgba(238,247,255,0.9)',
      fontSize: '17px',
      lineHeight: '1',
      textShadow: '0 0 10px rgba(175,226,255,0.44)',
      userSelect: 'none',
      animation: 'dse-gallery-cursor-breathe 1.9s ease-in-out infinite alternate',
    });
    cursorFxGlyph.textContent = '✥';

    cursorFx.appendChild(cursorFxRing);
    cursorFx.appendChild(cursorFxCore);
    cursorFx.appendChild(cursorFxGlyph);
    overlay.appendChild(cursorFx);
    const gridPos = (i) => {
      const itemZOffset = normalizeGalleryItemZOffset(items[i]?.zOffset, 0);
      if (!useCircularLayout) {
        return new THREE.Vector3((i % cols - (cols - 1) / 2) * spacingX, -Math.floor(i / cols) * (planeH + marginY), depthZ + itemZOffset);
      }
      const total = Math.max(1, items.length);
      const angle = (i / total) * Math.PI * 2;
      const radius = Math.max(6, spacingX * (Math.max(cols, 5) * 0.75));
      const y = Math.sin(angle * 2) * 1.35;
      const z = depthZ + Math.sin(angle) * (radius * 0.55) + itemZOffset;
      return new THREE.Vector3(Math.cos(angle) * radius, y, z);
    };

    const minGapX = 0.3;
    function layoutColumns() {
      if (useCircularLayout) {
        const total = Math.max(1, planes.length);
        const radius = Math.max(6, spacingX * (Math.max(cols, 5) * 0.75));
        for (let i = 0; i < planes.length; i++) {
          const p = planes[i]; if (!p) continue;
          const angle = (i / total) * Math.PI * 2;
          const itemZOffset = normalizeGalleryItemZOffset(items[i]?.zOffset, 0);
          p.position.x = Math.cos(angle) * radius;
          p.position.y = Math.sin(angle * 2) * 1.35;
          p.position.z = depthZ + Math.sin(angle) * (radius * 0.55) + itemZOffset;
        }
        return;
      }
      const colWidths = new Array(cols).fill(1);
      let layoutCursor = 0;
      for (let i = 0; i < planes.length; i++) {
        const p = planes[i];
        if (!p) continue;
        const breakCount = Math.max(0, Math.round(Number(lineBreaksBeforeItem.get(i) || 0)));
        if (breakCount > 0) {
          layoutCursor = 0;
        }
        const colIndex = (layoutCursor % cols);
        const { width: w } = getMeshCardDimensions(p, planeH);
        if (w > (colWidths[colIndex] || 1)) colWidths[colIndex] = w;
        layoutCursor += 1;
      }

      const colCenters = new Array(cols).fill(0);
      const spacingControl = Math.max(0.5, Math.min(40, Number(spacingX) || 2.1));
      const pairGap = Math.max(0.05, minGapX * (spacingControl / 2.1));
      for (let col = 1; col < cols; col += 1) {
        const prevHalf = (colWidths[col - 1] || 1) / 2;
        const currHalf = (colWidths[col] || 1) / 2;
        colCenters[col] = colCenters[col - 1] + prevHalf + currHalf + pairGap;
      }
      if (cols > 0) {
        const leftEdge = colCenters[0] - ((colWidths[0] || 1) / 2);
        const rightEdge = colCenters[cols - 1] + ((colWidths[cols - 1] || 1) / 2);
        const shift = -((leftEdge + rightEdge) / 2);
        for (let col = 0; col < cols; col += 1) colCenters[col] += shift;
      }

      const columnTops = new Array(cols).fill(planeH / 2);
      layoutCursor = 0;
      for (let i = 0; i < planes.length; i++) {
        const p = planes[i]; if (!p) continue;
        const breakCount = Math.max(0, Math.round(Number(lineBreaksBeforeItem.get(i) || 0)));
        if (breakCount > 0) {
          const deepestTop = Math.min(...columnTops);
          const extraBlankRows = Math.max(0, breakCount - 1);
          const nextTop = deepestTop - marginY - (extraBlankRows * (planeH + marginY));
          for (let col = 0; col < cols; col += 1) {
            columnTops[col] = nextTop;
          }
          layoutCursor = 0;
        }
        const colIndex = (layoutCursor % cols);
        const { height: h } = getMeshCardDimensions(p, planeH);
        const top = columnTops[colIndex] ?? (planeH / 2);
        const x = colCenters[colIndex] ?? ((colIndex - (cols - 1) / 2) * spacingX);
        const itemZOffset = normalizeGalleryItemZOffset(items[i]?.zOffset, 0);
        p.position.x = x;
        p.position.y = top - h / 2;
        p.position.z = depthZ + itemZOffset;
        columnTops[colIndex] = top - h - marginY;
        layoutCursor += 1;
      }
    }

    const lowResTarget = items.filter(e => e.kind === 'image' && primaryImageUrl(e)).length;
    const loadedRef = { current: 0 };
    if (lowResTarget === 0) {
      setFirstPaintReady(true); setLoading(false);
    }
    function markLowResLoaded() {
      if (lowResTarget === 0) return;
      loadedRef.current += 1;
      if (loadedRef.current === lowResTarget) {
        setFirstPaintReady(true);
        setLoading(false);
      }
    }

    function resolveSquareIconImage(icon = {}) {
      if (normalizeSquareIconSourceType(icon?.sourceType, 'symbol') !== 'image') return null;
      const raw = selectSquareIconImageUrl(icon);
      if (!raw) return null;
      const src = assetUrl(raw);
      if (!src) return null;

      let record = squareIconImageCache.get(src);
      if (!record) {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        record = { image, loaded: false, failed: false };
        image.onload = () => {
          record.loaded = true;
        };
        image.onerror = () => {
          record.failed = true;
        };
        image.src = src;
        squareIconImageCache.set(src, record);
      }
      return record;
    }

    function clearFocusedInfoMeshes() {
      while (focusedInfoRows.length) {
        const row = focusedInfoRows.pop();
        try { row.texture?.dispose?.(); } catch {}
        try {
          (Array.isArray(row.textExtrusionMeshes) ? row.textExtrusionMeshes : []).forEach((mesh) => {
            try { mesh?.geometry?.dispose?.(); } catch {}
            try { mesh?.material?.dispose?.(); } catch {}
          });
        } catch {}
        try { row.textMesh?.geometry?.dispose?.(); } catch {}
        try { row.textMesh?.material?.dispose?.(); } catch {}
        try { row.backgroundBorderMesh?.geometry?.dispose?.(); } catch {}
        try { row.backgroundBorderMesh?.material?.dispose?.(); } catch {}
        try { row.backgroundMesh?.geometry?.dispose?.(); } catch {}
        try { row.backgroundMesh?.material?.dispose?.(); } catch {}
      }
      focusedInfoFaceCameraMeshes.length = 0;
      try { focusedInfoGroup.clear(); } catch {}
      focusedInfoRuntime.signature = '';
      focusedInfoRuntime.index = -1;
      focusedInfoRuntime.startedAt = 0;
      focusedInfoRuntime.typingStartsAt = 0;
      focusedInfoRuntime.revealedChars = -1;
      focusedInfoRuntime.totalChars = 0;
      focusedInfoRuntime.hideActive = false;
      focusedInfoRuntime.hideStartedAt = 0;
      focusedInfoRuntime.hideDurationMs = FOCUSED_INFO_HIDE_TRANSITION_MS;
      focusedInfoRuntime.lastHideSignal = Number(focusedInfoHideSignalRef.current) || 0;
      focusedInfoGroup.visible = false;
    }

    function measureSpacedTextWidth(ctx, text, spacingPx) {
      const value = String(text || '');
      if (!value) return 0;
      let width = 0;
      for (let index = 0; index < value.length; index += 1) {
        width += ctx.measureText(value[index]).width;
        if (index < value.length - 1) width += spacingPx;
      }
      return width;
    }

    function drawSpacedText(ctx, text, x, y, spacingPx) {
      const value = String(text || '');
      let drawX = x;
      for (let index = 0; index < value.length; index += 1) {
        const glyph = value[index];
        ctx.fillText(glyph, drawX, y);
        drawX += ctx.measureText(glyph).width;
        if (index < value.length - 1) drawX += spacingPx;
      }
    }

    function colorToRgbaString(colorValue, alpha = 1) {
      const safeHex = normalizeHexColor(colorValue, '#ffffff');
      const color = new THREE.Color(safeHex);
      const safeAlpha = clampRange(alpha, 1, 0, 1);
      return `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, ${safeAlpha})`;
    }

    function drawFocusedInfoRow(row, text, colorHex, alpha = 1) {
      if (!row?.ctx || !row?.texture) return;
      row.ctx.setTransform(row.canvasScale || 1, 0, 0, row.canvasScale || 1, 0, 0);
      row.ctx.clearRect(0, 0, row.drawWidth || row.canvas?.width || 0, row.drawHeight || row.canvas?.height || 0);
      row.ctx.font = row.font;
      row.ctx.textAlign = 'left';
      row.ctx.textBaseline = 'alphabetic';
      row.ctx.fillStyle = colorToRgbaString(colorHex, alpha);
      const linesRaw = String(text || '').split('\n');
      const lines = linesRaw.length ? linesRaw : [''];
      const effectiveFontSize = Math.max(1, Math.round(row.effectiveFontSize || 1));
      const lineAdvancePx = Math.max(1, Math.round(row.lineAdvancePx || effectiveFontSize));
      const drawWidth = Math.max(1, Number(row.drawWidth) || 1);
      const drawHeight = Math.max(1, Number(row.drawHeight) || 1);
      const padX = Math.max(0, Number(row.padX) || 0);
      const padY = Math.max(0, Number(row.padY) || 0);
      const textBlockHeight = effectiveFontSize + (Math.max(0, lines.length - 1) * lineAdvancePx);
      let blockTop = row.verticalAlign === 'middle'
        ? Math.round((drawHeight - textBlockHeight) * 0.5)
        : row.verticalAlign === 'bottom'
          ? Math.round(drawHeight - padY - textBlockHeight)
          : padY;
      const maxTop = Math.max(padY, drawHeight - textBlockHeight - padY);
      blockTop = clampRange(blockTop, padY, padY, maxTop);

      lines.forEach((line, lineIndex) => {
        const lineText = String(line || '');
        const lineWidth = Math.max(0, Math.ceil(measureSpacedTextWidth(row.ctx, lineText, row.spacingPx)));
        let lineStartX = padX;
        if (row.horizontalAlign === 'middle') {
          lineStartX = Math.round((drawWidth - lineWidth) * 0.5);
        } else if (row.horizontalAlign === 'right') {
          lineStartX = Math.round(drawWidth - padX - lineWidth);
        }
        const maxX = Math.max(padX, drawWidth - lineWidth - padX);
        lineStartX = clampRange(lineStartX, padX, padX, maxX);
        const baselineY = blockTop + effectiveFontSize + (lineIndex * lineAdvancePx);
        if (lineText) {
          drawSpacedText(row.ctx, lineText, lineStartX, baselineY, row.spacingPx);
        }
      });
      row.texture.needsUpdate = true;
    }

    function buildFocusedInfoVisibleTexts(revealChars) {
      const totalVisibleChars = Math.max(0, Math.floor(Number(revealChars) || 0));
      let remainingChars = totalVisibleChars;
      return focusedInfoRows.map((row) => {
        const visibleChars = Math.max(0, Math.min(row.fullText.length, remainingChars));
        remainingChars -= row.fullText.length;
        return row.fullText.slice(0, visibleChars);
      });
    }

    function rebuildFocusedInfoMeshes(index, nowMs) {
      if (!focusedInfoEnabled()) {
        clearFocusedInfoMeshes();
        return;
      }
      const activeEntry = items[index];
      const activePlane = planes[index];
      if (!activeEntry || !activePlane) {
        clearFocusedInfoMeshes();
        return;
      }

      const infoSettings = normalizeInfoTextSettings(activeEntry?.box?.infoTextSettings, {});
      const infoLinesRaw = buildFocusedInfoTextLines(activeEntry, infoSettings);
      const fullText = infoLinesRaw.join('\n').trim().slice(0, 7000);
      const signature = `${index}|${JSON.stringify(infoSettings)}|${fullText}`;
      if (signature === focusedInfoRuntime.signature && focusedInfoRuntime.index === index) {
        return;
      }

      clearFocusedInfoMeshes();
      focusedInfoRuntime.signature = signature;
      focusedInfoRuntime.index = index;
      focusedInfoRuntime.startedAt = nowMs;
      focusedInfoRuntime.typingStartsAt = nowMs + FOCUSED_INFO_SHOW_PRE_TYPE_MS;
      focusedInfoRuntime.revealedChars = -1;
      focusedInfoRuntime.totalChars = 0;
      focusedInfoRuntime.hideActive = false;
      focusedInfoRuntime.hideStartedAt = 0;
      focusedInfoGroup.visible = true;

      const center = new THREE.Vector3();
      activePlane.getWorldPosition(center);
      const cardSize = getMeshCardDimensions(activePlane, planeH);
      const referenceCardHeight = Math.max(0.001, planeH);
      const referenceCardWidth = referenceCardHeight * (16 / 9);
      const focusFontSizeScale = clampRange(
        Math.max(
          cardSize.height / referenceCardHeight,
          cardSize.width / Math.max(0.001, referenceCardWidth)
        ),
        1,
        0.65,
        3.6
      );
      const devicePixelRatio = clampRange(
        ((typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1),
        1,
        1,
        3
      );
      const targetTextResolutionScale = clampRange(
        (2.2 + (Math.max(0, focusFontSizeScale - 1) * 0.55)) * Math.sqrt(devicePixelRatio),
        2.8,
        2,
        6
      );
      const maxTextTextureSize = Math.max(
        2048,
        Math.min(16384, Math.round(Number(renderer?.capabilities?.maxTextureSize || 8192)))
      );

      if (!fullText) return;
      focusedInfoRuntime.totalChars = fullText.length;

      const canvas = document.createElement('canvas');
      const measureCtx = canvas.getContext('2d');
      if (!measureCtx) return;

      const fontFamily = resolveSquareContentFontFamily(infoSettings.fontType, 'system');
      const effectiveFontSize = clampRange(infoSettings.fontSize * focusFontSizeScale, infoSettings.fontSize, 12, 240);
      const font = `600 ${Math.round(effectiveFontSize)}px ${fontFamily}`;
      const spacingPx = infoSettings.letterSpacing;
      const lineAdvancePx = Math.max(1, Math.ceil(effectiveFontSize * infoSettings.lineHeight));
      const textLines = fullText.split('\n');

      measureCtx.font = font;
      const textWidthPx = Math.max(
        24,
        ...textLines.map((line) => Math.ceil(measureSpacedTextWidth(measureCtx, line, spacingPx)))
      );
      const textHeightPx = Math.max(
        18,
        Math.ceil(effectiveFontSize + (Math.max(0, textLines.length - 1) * lineAdvancePx))
      );

      const sizeOffsetPx = Math.round(clampRange(infoSettings.backgroundSizeOffset, 0, 0, 240));
      const padX = Math.max(2, 6 + sizeOffsetPx);
      const padY = Math.max(2, 6 + sizeOffsetPx);
      const drawWidth = Math.min(6144, textWidthPx + (padX * 2));
      const drawHeight = Math.min(6144, Math.max(64, textHeightPx + (padY * 2)));
      const widthScaleLimit = maxTextTextureSize / Math.max(1, drawWidth);
      const heightScaleLimit = maxTextTextureSize / Math.max(1, drawHeight);
      const canvasScale = clampRange(
        Math.min(targetTextResolutionScale, widthScaleLimit, heightScaleLimit),
        1,
        1,
        targetTextResolutionScale
      );
      canvas.width = Math.max(2, Math.round(drawWidth * canvasScale));
      canvas.height = Math.max(2, Math.round(drawHeight * canvasScale));

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(canvasScale, 0, 0, canvasScale, 0, 0);
      if ('imageSmoothingEnabled' in ctx) ctx.imageSmoothingEnabled = true;
      if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
      ctx.font = font;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';

      const explicitTextColor = normalizeHexColor(infoSettings.textColor, '');
      const textColor = explicitTextColor || (
        getHexRelativeLuminance(infoSettings.backgroundColor, 0.5) >= 0.52
          ? '#0a0a0a'
          : '#f5f5f5'
      );
      const horizontalAlign = normalizeInfoTextHorizontalAlign(infoSettings.horizontalAlign, 'left');
      const verticalAlign = normalizeInfoTextVerticalAlign(infoSettings.verticalAlign, 'top');

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.anisotropy = Math.max(1, renderer?.capabilities?.getMaxAnisotropy?.() || 8);
      applyTextureColorSpace(texture);

      const worldScaleCap = Math.max(1.1, 1.1 * focusFontSizeScale);
      const worldHeight = Math.max(0.24, Math.min(cardSize.height * 3.2, (drawHeight / 176) * worldScaleCap));
      const worldWidth = Math.max(0.72, Math.min(cardSize.width * 4.4, worldHeight * (drawWidth / Math.max(1, drawHeight))));
      const rowX = center.x;
      const rowY = center.y;
      const rowZ = center.z + Math.max(0.42, cardSize.width * 0.25);

      const textMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(worldWidth, worldHeight),
        new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false })
      );
      const textFrontZ = rowZ + 0.002 + clampRange(infoSettings.textZIndex, 0, 0, 2);
      textMesh.position.set(rowX, rowY, textFrontZ);

      const textExtrusionDepth = clampRange(infoSettings.textExtrusion, 0, 0, 8) * 0.008;
      const textExtrusionLayerCount = textExtrusionDepth > 0.0005
        ? Math.min(8, Math.max(1, Math.round(textExtrusionDepth / 0.01)))
        : 0;
      const textExtrusionColor = getFocusedInfoTextExtrusionColor(textColor);
      const textExtrusionMeshes = [];

      for (let layerIndex = 0; layerIndex < textExtrusionLayerCount; layerIndex += 1) {
        const depthStep = ((layerIndex + 1) / textExtrusionLayerCount) * textExtrusionDepth;
        const layerMesh = new THREE.Mesh(
          new THREE.PlaneGeometry(worldWidth, worldHeight),
          new THREE.MeshBasicMaterial({
            map: texture,
            color: textExtrusionColor,
            transparent: true,
            opacity: Math.max(0.14, 0.64 - (layerIndex * 0.08)),
            depthTest: false,
            depthWrite: false,
          })
        );
        layerMesh.position.set(rowX, rowY, textFrontZ - depthStep);
        textExtrusionMeshes.push(layerMesh);
      }

      const backgroundDepth = resolveFocusedInfoBackgroundDepth(infoSettings.backgroundExtrusion, 100);
      const borderWidthPx = clampRange(infoSettings.backgroundBorderWidth, 0, 0, 240);
      const borderWidthWorld = (borderWidthPx / Math.max(1, drawHeight)) * worldHeight;
      const borderDepth = resolveFocusedInfoBackgroundDepth(infoSettings.backgroundBorderDepth, 200);
      const borderOpacity = clampRange(infoSettings.backgroundBorderOpacity, 1, 0, 1);
      const borderColor = normalizeHexColor(infoSettings.backgroundBorderColor, '#ffffff');
      const borderZOffset = clampRange(infoSettings.backgroundBorderZOffset, 0, -2, 2);
      const backgroundGeometry = createFocusedInfoBackgroundGeometry(
        worldWidth,
        worldHeight,
        infoSettings.backgroundCornerRoundness,
        backgroundDepth
      );

      const backgroundMesh = new THREE.Mesh(
        backgroundGeometry,
        new THREE.MeshStandardMaterial({
          color: infoSettings.backgroundColor,
          transparent: true,
          opacity: infoSettings.backgroundOpacity,
          roughness: 0.92,
          metalness: 0.02,
          side: THREE.DoubleSide,
          depthTest: false,
          depthWrite: false,
        })
      );
      backgroundMesh.position.set(
        rowX,
        rowY,
        rowZ - infoSettings.backgroundZIndex - (backgroundDepth * 0.5) - 0.001
      );

      let backgroundBorderMesh = null;
      if (borderWidthWorld > 0.0002 && borderOpacity > 0.0001) {
        const borderGeometry = createFocusedInfoBorderGeometry(
          worldWidth,
          worldHeight,
          borderWidthWorld,
          infoSettings.backgroundCornerRoundness,
          borderDepth
        );
        if (borderGeometry) {
          backgroundBorderMesh = new THREE.Mesh(
            borderGeometry,
            new THREE.MeshStandardMaterial({
              color: borderColor,
              transparent: true,
              opacity: borderOpacity,
              roughness: 0.9,
              metalness: 0.03,
              side: THREE.DoubleSide,
              depthTest: false,
              depthWrite: false,
            })
          );
          backgroundBorderMesh.position.set(
            rowX,
            rowY,
            rowZ - infoSettings.backgroundZIndex - (borderDepth * 0.5) - 0.0008 + borderZOffset
          );
        }
      }

      focusedInfoGroup.add(backgroundMesh);
      if (backgroundBorderMesh) focusedInfoGroup.add(backgroundBorderMesh);
      textExtrusionMeshes.forEach((mesh) => focusedInfoGroup.add(mesh));
      focusedInfoGroup.add(textMesh);
      focusedInfoFaceCameraMeshes.push(backgroundMesh, ...(backgroundBorderMesh ? [backgroundBorderMesh] : []), ...textExtrusionMeshes, textMesh);
      focusedInfoRows.push({
        fullText,
        hideText: '',
        canvas,
        ctx,
        texture,
        font,
        spacingPx,
        textColor,
        lineAdvancePx,
        effectiveFontSize,
        horizontalAlign,
        verticalAlign,
        padX,
        padY,
        drawWidth,
        drawHeight,
        canvasScale,
        textBaseZ: textFrontZ,
        textExtrusionFrontZ: textExtrusionMeshes[0]?.position?.z ?? textFrontZ,
        textExtrusionBaseZs: textExtrusionMeshes.map((mesh) => mesh?.position?.z ?? textFrontZ),
        textExtrusionBaseOpacities: textExtrusionMeshes.map((mesh) => clampRange(mesh?.material?.opacity, 1, 0, 1)),
        worldPixelDepth: Math.max(0.0005, worldHeight / Math.max(1, drawHeight)),
        backgroundBaseZ: backgroundMesh.position.z,
        backgroundStartOpacity: clampRange(infoSettings.backgroundOpacity, 0.65, 0, 1),
        backgroundBorderBaseZ: backgroundBorderMesh?.position?.z ?? backgroundMesh.position.z,
        backgroundBorderZDelta: (backgroundBorderMesh?.position?.z ?? backgroundMesh.position.z) - backgroundMesh.position.z,
        backgroundBorderStartOpacity: borderOpacity,
        textColorVector: new THREE.Color(textColor),
        backgroundColorVector: new THREE.Color(infoSettings.backgroundColor),
        backgroundBorderColorVector: new THREE.Color(borderColor),
        textMesh,
        textExtrusionMeshes,
        backgroundBorderMesh,
        backgroundMesh,
      });
    }

    function updateFocusedInfoMeshes(index, nowMs) {
      const hideSignal = Number(focusedInfoHideSignalRef.current) || 0;
      if (hideSignal !== focusedInfoRuntime.lastHideSignal) {
        focusedInfoRuntime.lastHideSignal = hideSignal;
        if (hideSignal > 0) {
          if (!focusedInfoRows.length) {
            clearFocusedInfoMeshes();
          } else {
            const fallbackTypingStartAt = (
              Number.isFinite(focusedInfoRuntime.typingStartsAt) && focusedInfoRuntime.typingStartsAt > 0
            )
              ? focusedInfoRuntime.typingStartsAt
              : (focusedInfoRuntime.startedAt + FOCUSED_INFO_SHOW_PRE_TYPE_MS);
            const revealCharsAtHide = clampRange(
              focusedInfoRuntime.revealedChars >= 0
                ? focusedInfoRuntime.revealedChars
                : Math.min(
                  focusedInfoRuntime.totalChars,
                  Math.floor(Math.max(0, nowMs - fallbackTypingStartAt) / 24)
                ),
              0,
              0,
              focusedInfoRuntime.totalChars
            );
            const frozenVisibleTexts = buildFocusedInfoVisibleTexts(revealCharsAtHide);
            focusedInfoRows.forEach((row, rowIndex) => {
              row.hideText = frozenVisibleTexts[rowIndex] || '';
            });
            focusedInfoRuntime.hideActive = true;
            focusedInfoRuntime.hideStartedAt = nowMs;
            focusedInfoRuntime.hideDurationMs = FOCUSED_INFO_HIDE_TRANSITION_MS;
            focusedInfoGroup.visible = true;
          }
        }
      }

      if (focusedInfoRuntime.hideActive) {
        const durationMs = Math.max(1, focusedInfoRuntime.hideDurationMs || FOCUSED_INFO_HIDE_TRANSITION_MS);
        const progress = clampRange((nowMs - focusedInfoRuntime.hideStartedAt) / durationMs, 1, 0, 1);
        const alignPhase = clampRange(progress / 0.25, 1, 0, 1);
        const glidePhase = clampRange((progress - 0.25) / 0.5, 0, 0, 1);
        const collapsePhase = clampRange((progress - 0.75) / 0.25, 0, 0, 1);
        const collapseScale = Math.max(0.0001, 1 - collapsePhase);
        const colorMerged = progress >= 0.75;

        focusedInfoRows.forEach((row) => {
          if (row.backgroundMesh) row.backgroundMesh.visible = true;
          if (row.backgroundBorderMesh) row.backgroundBorderMesh.visible = true;
          if (row.textMesh) row.textMesh.visible = true;
          const textColorVector = colorMerged
            ? (row.backgroundColorVector || new THREE.Color('#050505'))
            : (row.textColorVector || new THREE.Color(row.textColor || '#ffffff'));
          const textColorHex = `#${textColorVector.getHexString()}`;
          const backgroundOpacity = clampRange(row.backgroundStartOpacity, 0.65, 0, 1);
          const textAlpha = backgroundOpacity * collapseScale;
          drawFocusedInfoRow(row, row.hideText || '', textColorHex, textAlpha);

          const extrusionFrontZ = Number.isFinite(row.textExtrusionFrontZ)
            ? row.textExtrusionFrontZ
            : row.textBaseZ;
          const onePixelDepth = clampRange(row.worldPixelDepth, 0.001, 0.0002, 0.02);
          const nearFrontZ = row.textBaseZ - onePixelDepth;

          const alignedBackgroundZ = THREE.MathUtils.lerp(row.backgroundBaseZ, extrusionFrontZ, alignPhase);
          const glidedBackgroundZ = THREE.MathUtils.lerp(extrusionFrontZ, nearFrontZ, glidePhase);
          const backgroundZ = progress < 0.25 ? alignedBackgroundZ : glidedBackgroundZ;

          if (row.backgroundMesh?.position) {
            row.backgroundMesh.position.z = backgroundZ;
          }
          if (row.backgroundMesh?.scale) {
            row.backgroundMesh.scale.y = collapseScale;
          }
          if (row.backgroundBorderMesh?.position) {
            row.backgroundBorderMesh.position.z = backgroundZ + clampRange(row.backgroundBorderZDelta, 0, -2.5, 2.5);
          }
          if (row.backgroundBorderMesh?.scale) {
            row.backgroundBorderMesh.scale.y = collapseScale;
          }

          if (row.textMesh?.position) {
            row.textMesh.position.z = row.textBaseZ;
          }
          if (row.textMesh?.scale) {
            row.textMesh.scale.y = collapseScale;
          }

          const extrusionMeshes = Array.isArray(row.textExtrusionMeshes) ? row.textExtrusionMeshes : [];
          extrusionMeshes.forEach((mesh, layerIndex) => {
            if (!mesh) return;
            mesh.visible = true;
            if (mesh.position) {
              mesh.position.z = row.textExtrusionBaseZs?.[layerIndex] ?? row.textBaseZ;
            }
            if (mesh.scale) {
              mesh.scale.y = collapseScale;
            }
            const layerMaterial = mesh.material;
            if (layerMaterial?.color?.set) {
              layerMaterial.color.set(textColorHex);
            }
            if (typeof layerMaterial?.opacity === 'number') {
              const baseLayerOpacity = clampRange(row.textExtrusionBaseOpacities?.[layerIndex], 0.45, 0, 1);
              layerMaterial.opacity = baseLayerOpacity * backgroundOpacity * collapseScale;
            }
          });

          const backgroundMaterial = row.backgroundMesh?.material;
          if (typeof backgroundMaterial?.opacity === 'number') {
            backgroundMaterial.opacity = backgroundOpacity * collapseScale;
          }
          const backgroundBorderMaterial = row.backgroundBorderMesh?.material;
          if (typeof backgroundBorderMaterial?.opacity === 'number') {
            const baseBorderOpacity = clampRange(row.backgroundBorderStartOpacity, 1, 0, 1);
            backgroundBorderMaterial.opacity = baseBorderOpacity * collapseScale;
          }
        });

        if (progress >= 0.999) {
          clearFocusedInfoMeshes();
        }
        return;
      }

      if (!focusedInfoEnabled()) {
        if (focusedInfoRows.length) clearFocusedInfoMeshes();
        return;
      }

      rebuildFocusedInfoMeshes(index, nowMs);
      if (!focusedInfoRows.length) return;

      const showElapsed = Math.max(0, nowMs - focusedInfoRuntime.startedAt);
      const hasSignalReachedPlane = showElapsed >= FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS;
      const widthGrowPhase = clampRange(
        (showElapsed - FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS) / FOCUSED_INFO_SHOW_WIDTH_GROW_MS,
        0,
        0,
        1
      );
      const heightGrowUnfoldPhase = clampRange(
        (showElapsed - FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS - FOCUSED_INFO_SHOW_WIDTH_GROW_MS) / FOCUSED_INFO_SHOW_HEIGHT_UNFOLD_MS,
        0,
        0,
        1
      );
      const showReadyForTypewriter = showElapsed >= FOCUSED_INFO_SHOW_PRE_TYPE_MS;
      const tinyScale = 0.006;
      const introWhiteMix = hasSignalReachedPlane && !showReadyForTypewriter
        ? clampRange(1 - heightGrowUnfoldPhase, 1, 0, 1)
        : 0;

      focusedInfoRows.forEach((row) => {
        const backgroundScaleX = hasSignalReachedPlane
          ? THREE.MathUtils.lerp(tinyScale, 1, widthGrowPhase)
          : tinyScale;
        const backgroundScaleY = hasSignalReachedPlane
          ? THREE.MathUtils.lerp(tinyScale, 1, heightGrowUnfoldPhase)
          : tinyScale;

        if (row.backgroundMesh?.scale) {
          row.backgroundMesh.scale.x = backgroundScaleX;
          row.backgroundMesh.scale.y = backgroundScaleY;
          row.backgroundMesh.scale.z = 1;
        }
        if (row.backgroundMesh) {
          row.backgroundMesh.visible = hasSignalReachedPlane;
        }
        if (row.backgroundMesh?.quaternion?.copy) {
          row.backgroundMesh.quaternion.copy(camera.quaternion);
        }

        if (row.backgroundBorderMesh?.scale) {
          row.backgroundBorderMesh.scale.x = backgroundScaleX;
          row.backgroundBorderMesh.scale.y = backgroundScaleY;
          row.backgroundBorderMesh.scale.z = 1;
        }
        if (row.backgroundBorderMesh) {
          row.backgroundBorderMesh.visible = hasSignalReachedPlane;
        }
        if (row.backgroundBorderMesh?.quaternion?.copy) {
          row.backgroundBorderMesh.quaternion.copy(camera.quaternion);
        }

        const backgroundMaterial = row.backgroundMesh?.material;
        if (backgroundMaterial?.color?.copy) {
          const baseBackgroundColor = row.backgroundColorVector || new THREE.Color('#050505');
          backgroundMaterial.color.copy(baseBackgroundColor);
          if (introWhiteMix > 0.0001) {
            backgroundMaterial.color.lerp(FOCUSED_INFO_INTRO_GLOW_COLOR, introWhiteMix);
          }
        }
        if (typeof backgroundMaterial?.opacity === 'number') {
          const baseOpacity = clampRange(row.backgroundStartOpacity, 0.65, 0, 1);
          backgroundMaterial.opacity = hasSignalReachedPlane
            ? THREE.MathUtils.lerp(baseOpacity, 1, introWhiteMix)
            : 0;
        }
        if (backgroundMaterial?.emissive?.copy) {
          backgroundMaterial.emissive.copy(FOCUSED_INFO_INTRO_GLOW_COLOR);
          backgroundMaterial.emissiveIntensity = hasSignalReachedPlane ? (0.92 * introWhiteMix) : 0;
        }

        const backgroundBorderMaterial = row.backgroundBorderMesh?.material;
        if (backgroundBorderMaterial?.color?.copy) {
          const baseBorderColor = row.backgroundBorderColorVector || row.backgroundColorVector || FOCUSED_INFO_INTRO_GLOW_COLOR;
          backgroundBorderMaterial.color.copy(baseBorderColor);
          if (introWhiteMix > 0.0001) {
            backgroundBorderMaterial.color.lerp(FOCUSED_INFO_INTRO_GLOW_COLOR, introWhiteMix);
          }
        }
        if (typeof backgroundBorderMaterial?.opacity === 'number') {
          const baseBorderOpacity = clampRange(row.backgroundBorderStartOpacity, 1, 0, 1);
          backgroundBorderMaterial.opacity = hasSignalReachedPlane
            ? THREE.MathUtils.lerp(baseBorderOpacity, 1, introWhiteMix)
            : 0;
        }
        if (backgroundBorderMaterial?.emissive?.copy) {
          backgroundBorderMaterial.emissive.copy(FOCUSED_INFO_INTRO_GLOW_COLOR);
          backgroundBorderMaterial.emissiveIntensity = hasSignalReachedPlane ? (1.15 * introWhiteMix) : 0;
        }

        if (row.textMesh) row.textMesh.visible = showReadyForTypewriter;
        const extrusionMeshes = Array.isArray(row.textExtrusionMeshes) ? row.textExtrusionMeshes : [];
        extrusionMeshes.forEach((mesh, layerIndex) => {
          if (!mesh) return;
          mesh.visible = showReadyForTypewriter;
          const layerMaterial = mesh.material;
          if (typeof layerMaterial?.opacity === 'number') {
            const baseLayerOpacity = clampRange(row.textExtrusionBaseOpacities?.[layerIndex], 0.45, 0, 1);
            layerMaterial.opacity = showReadyForTypewriter ? baseLayerOpacity : 0;
          }
        });
      });

      if (!showReadyForTypewriter) {
        focusedInfoRuntime.revealedChars = -1;
        return;
      }

      const typingElapsed = Math.max(0, nowMs - focusedInfoRuntime.typingStartsAt);
      const revealChars = Math.min(
        focusedInfoRuntime.totalChars,
        Math.floor(typingElapsed / 24)
      );
      if (revealChars === focusedInfoRuntime.revealedChars) return;
      focusedInfoRuntime.revealedChars = revealChars;

      let remainingChars = revealChars;
      focusedInfoRows.forEach((row) => {
        const visibleChars = Math.max(0, Math.min(row.fullText.length, remainingChars));
        remainingChars -= row.fullText.length;
        const visibleText = row.fullText.slice(0, visibleChars);
        drawFocusedInfoRow(row, visibleText, row.textColor, 1);
      });
    }

    for (let i = 0; i < items.length; i++) {
      const entry = items[i];
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, planeH), new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.64,
        metalness: 0.02,
        side: THREE.DoubleSide,
        transparent: true,
        toneMapped: true
      }));
      if (!mesh.userData) mesh.userData = {};
      mesh.userData.cardWidth = 1;
      mesh.userData.cardHeight = planeH;
      mesh.userData.isSquareGeometryCard = false;
      mesh.position.copy(gridPos(i)); mesh.lookAt(new THREE.Vector3(mesh.position.x, mesh.position.y, camera.position.z)); root.add(mesh); planes.push(mesh);

      if (entry.kind === 'box' || (entry.kind === 'image' && !entry.url && entry.box)) {
        const widthSpan = normalizeCanvasSpanValue(entry?.widthSpan, 1) || 1;
        const sizeBucket = widthSpan;
        const baseW = planeH * (16 / 9);
        const isSquareBox = !!entry?.box?.square;
        const squareWidth = planeH * widthSpan + (marginY * Math.max(0, widthSpan - 1));
        const w = isSquareBox ? squareWidth : baseW * (sizeBucket === 1 ? 1.0 : sizeBucket === 2 ? 1.1 : 1.32);
        const discoverTextGutter = mode === 'discover'
          ? (DISCOVER_TEXT_CARD_PADDING_LEFT + DISCOVER_TEXT_CARD_PADDING_RIGHT)
          : TEXT_CARD_TEXT_GUTTER;
        const autoRowSpan = estimateBoxRowSpanRef.current(entry, w, planeH, 10, { textGutter: discoverTextGutter });
        const explicitRowSpan = normalizeCanvasSpanValue(entry?.heightSpan, undefined);
        const hasExplicitRowSpan = Number.isFinite(explicitRowSpan) && explicitRowSpan > 0;
        const targetRowSpan = hasExplicitRowSpan
          ? (isSquareBox
            ? explicitRowSpan
            : (mode === 'discover' ? Math.min(explicitRowSpan, autoRowSpan) : explicitRowSpan)
          )
          : autoRowSpan;
        const rowSpan = Math.max(0.25, Math.min(10, targetRowSpan));
        const h = planeH * rowSpan + marginY * Math.max(0, rowSpan - 1);
        const box = entry.box || {};
        const squareExtras = normalizeSquareBoxExtras(box, box);
        const squareState = resolveSquareStateForEntry(entry, i);
        const initialSquareFrame = resolveSquareFrameForState(squareExtras, squareState, performance.now());
        const initialSquareVisualExtras = applySquareVisualFrameToExtras(squareExtras, initialSquareFrame);
        const cardGeometry = isSquareBox
          ? buildSquareCardGeometry(w, h, initialSquareVisualExtras)
          : new THREE.PlaneGeometry(w, h);
        mesh.userData.isSquareGeometryCard = !!isSquareBox;
        setMeshGeometryWithSize(mesh, cardGeometry, w, h);
        if (isSquareBox) {
          const squareMaterial = ensureSquareRuntimeMaterial(mesh, initialSquareVisualExtras.materialType);
          applySquareRuntimeMaterialSettings(squareMaterial, initialSquareVisualExtras);
        }
        const mat = mesh.material;
        if (mat?.color?.set) mat.color.set(0xffffff);
        if (mat?.emissive?.set) mat.emissive.set(0x111111);
        setAssetsPending(p => p + 1);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 1024;
          canvas.height = Math.round(1024 * (h / Math.max(0.1, w)));
          const ctx = canvas.getContext('2d');
          const isSquareCard = squareExtras.square;
          let squareDisplacementTexture = null;
          let squareDisplacementPattern = '';
          let squareGeometrySignature = '';

          const disposeSquareDisplacementTexture = () => {
            if (!squareDisplacementTexture) return;
            try { squareDisplacementTexture.dispose?.(); } catch {}
            squareDisplacementTexture = null;
            squareDisplacementPattern = '';
          };

          const ensureSquareDisplacementTexture = (activeExtras) => {
            if (!isSquareCard) return null;
            const strength = normalizeSquareTextureDisplacement(activeExtras.textureDisplacement, 0);
            if (strength <= 0.0001) {
              disposeSquareDisplacementTexture();
              return null;
            }

            const pattern = normalizeSquareTexturePattern(activeExtras.texturePattern, 'none');
            const displacementPattern = pattern === 'none' ? 'noise' : pattern;
            if (!squareDisplacementTexture || squareDisplacementPattern !== displacementPattern) {
              disposeSquareDisplacementTexture();
              squareDisplacementTexture = new THREE.CanvasTexture(buildSquareDisplacementCanvas(256, displacementPattern));
              if ('encoding' in squareDisplacementTexture && THREE.LinearEncoding) {
                squareDisplacementTexture.encoding = THREE.LinearEncoding;
              }
              squareDisplacementTexture.wrapS = THREE.RepeatWrapping;
              squareDisplacementTexture.wrapT = THREE.RepeatWrapping;
              squareDisplacementTexture.repeat.set(2.6, 2.6);
              squareDisplacementTexture.needsUpdate = true;
              squareDisplacementPattern = displacementPattern;
            }

            return squareDisplacementTexture;
          };

          const geometrySignatureFromExtras = (activeExtras) => [
            normalizeSquareGeometryType(activeExtras.geometryType, 'plane'),
            Math.round(normalizeSquareEdgeSmoothness(activeExtras.edgeSmoothness, 24) * 1000) / 1000,
            normalizeSquarePlaneResolution(activeExtras.planeResolution, 24),
            Math.round(normalizeSquareFoldDegrees(activeExtras.planeFoldX, 0) * 1000) / 1000,
            Math.round(normalizeSquareFoldDegrees(activeExtras.planeFoldY, 0) * 1000) / 1000,
            Math.round(normalizeSquareGeometryRotateY(activeExtras.geometryRotateY, 0) * 1000) / 1000,
            Math.round(normalizeSquareGeometryDepth(activeExtras.geometryDepth, 1) * 1000) / 1000,
            Math.round(w * 1000) / 1000,
            Math.round(h * 1000) / 1000,
          ].join('|');

          const applySquareSurfaceSettings = (activeExtras, texture = null, timestampMs = performance.now()) => {
            if (!isSquareCard) return;
            const nextGeometrySignature = geometrySignatureFromExtras(activeExtras);
            if (nextGeometrySignature !== squareGeometrySignature) {
              const nextGeometry = buildSquareCardGeometry(w, h, activeExtras);
              setMeshGeometryWithSize(mesh, nextGeometry, w, h);
              squareGeometrySignature = nextGeometrySignature;
            }

            const squareMaterial = ensureSquareRuntimeMaterial(mesh, activeExtras.materialType);
            if (!squareMaterial) return;
            if (texture) squareMaterial.map = texture;
            applySquareRuntimeMaterialSettings(squareMaterial, activeExtras);

            const strength = normalizeSquareTextureDisplacement(activeExtras.textureDisplacement, 0);
            const displacementTexture = ensureSquareDisplacementTexture(activeExtras);
            if (displacementTexture && strength > 0.0001) {
              const velocity = normalizeSquareTextureVelocity(activeExtras.textureVelocity, 0);
              const travel = ((((timestampMs * velocity * 0.00008) % 1) + 1) % 1);
              displacementTexture.offset.set(travel, -travel * 0.72);
              displacementTexture.needsUpdate = true;
              squareMaterial.displacementMap = displacementTexture;
              squareMaterial.displacementScale = strength * Math.max(0.12, Math.min(w, h) * 0.28);
              squareMaterial.displacementBias = -squareMaterial.displacementScale * 0.5;
            } else {
              squareMaterial.displacementMap = null;
              squareMaterial.displacementScale = 0;
              squareMaterial.displacementBias = 0;
            }

            squareMaterial.needsUpdate = true;
          };

          if (isSquareCard) {
            squareGeometrySignature = geometrySignatureFromExtras(initialSquareVisualExtras);
          }

          const textFormat = normalizeBoxTextFormat(box);
          const textColor = box?.textColor || 'rgba(255,255,255,0.92)';
          const c1 = box?.color1 || '#0b0b0b';
          const hasBackgroundColor = /^#([0-9a-f]{6})$/i.test(String(box?.backgroundColor || '').trim());
          const backgroundColor = normalizeHexColor(box?.backgroundColor, c1);
          const backgroundOpacity = clampOpacity(box?.backgroundOpacity, 1);
          const initialPaintExtras = isSquareCard ? initialSquareVisualExtras : squareExtras;
          const buildGradientFill = (activeSquareExtras) => {
            const gradientStops = buildGradientRgbaStops(
              activeSquareExtras.gradientColors,
              activeSquareExtras.gradientColorAlphas
            );
            if (activeSquareExtras.gradientShape === 'radial') {
              const radial = ctx.createRadialGradient(
                canvas.width * 0.5,
                canvas.height * 0.5,
                0,
                canvas.width * 0.5,
                canvas.height * 0.5,
                Math.max(canvas.width, canvas.height) * 0.6
              );
              gradientStops.forEach((color, index) => {
                const stop = gradientStops.length <= 1 ? 0 : index / Math.max(1, gradientStops.length - 1);
                radial.addColorStop(stop, color);
              });
              return radial;
            }

            if (activeSquareExtras.gradientShape === 'conic' && typeof ctx.createConicGradient === 'function') {
              const conic = ctx.createConicGradient(
                (activeSquareExtras.gradientAngle * Math.PI) / 180,
                canvas.width * 0.5,
                canvas.height * 0.5
              );
              gradientStops.forEach((color, index) => {
                const stop = gradientStops.length <= 1 ? 0 : index / Math.max(1, gradientStops.length - 1);
                conic.addColorStop(stop, color);
              });
              return conic;
            }

            const angle = (activeSquareExtras.gradientAngle * Math.PI) / 180;
            const half = Math.max(canvas.width, canvas.height) * 0.7;
            const cx = canvas.width * 0.5;
            const cy = canvas.height * 0.5;
            const x1 = cx - Math.cos(angle) * half;
            const y1 = cy - Math.sin(angle) * half;
            const x2 = cx + Math.cos(angle) * half;
            const y2 = cy + Math.sin(angle) * half;
            const linear = ctx.createLinearGradient(x1, y1, x2, y2);
            gradientStops.forEach((color, index) => {
              const stop = gradientStops.length <= 1 ? 0 : index / Math.max(1, gradientStops.length - 1);
              linear.addColorStop(stop, color);
            });
            return linear;
          };
          ctx.globalAlpha = clampOpacity(initialPaintExtras.backgroundOpacity, backgroundOpacity);
          if (hasBackgroundColor && !isSquareCard) {
            ctx.fillStyle = backgroundColor;
          } else {
            const g = buildGradientFill(initialPaintExtras);
            ctx.fillStyle = g;
          }
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.globalAlpha = 1;
          ctx.font = 'bold 72px system-ui, sans-serif';
          ctx.fillStyle = textColor;
          ctx.textAlign = textFormat.textAlign;
          ctx.textBaseline = 'top';
          const t1 = (box?.text1 || entry.title || '').toString();
          const t2 = (box?.text2 || entry.description || '').toString();
          const boxTextVisible = box?.textVisible !== false;
          const cx = canvas.width / 2;
          const lines = Array.isArray(box?.lines) ? box.lines.map((line) => String(line || '').trim()).filter(Boolean) : [];
          const tableHeaders = Array.isArray(box?.table?.headers)
            ? box.table.headers.map((header) => String(header || '').trim()).filter(Boolean)
            : [];
          const tableRows = Array.isArray(box?.table?.rows)
            ? box.table.rows
              .filter((row) => Array.isArray(row))
              .map((row) => row.map((cell) => String(cell || '').trim()))
              .filter((row) => row.some(Boolean))
            : [];
          const wrapTextWithFont = (text, maxWidth, font) => {
            const previousFont = ctx.font;
            if (font) ctx.font = font;
            const rows = wrapTextBlocks(ctx, text, maxWidth);
            ctx.font = previousFont;
            return rows;
          };

          const effectiveFontScale = textFormat.fontScale * TEXT_CARD_FONT_REDUCTION;
          const isDiscoverTextBox = mode === 'discover';
          const textCardPaddingLeft = isDiscoverTextBox ? DISCOVER_TEXT_CARD_PADDING_LEFT : TEXT_CARD_SIDE_PADDING;
          const textCardPaddingRight = isDiscoverTextBox ? DISCOVER_TEXT_CARD_PADDING_RIGHT : TEXT_CARD_SIDE_PADDING;
          const textCardTextGutter = textCardPaddingLeft + textCardPaddingRight;
          const titleFontSize = Math.max(13, Math.round(58 * effectiveFontScale));
          const bodyFontSize = Math.max(11, Math.round(48 * effectiveFontScale));
          const lineFontSize = Math.max(10, Math.round(36 * effectiveFontScale));
          const titleStep = Math.max(24, Math.round(68 * textFormat.lineSpacing));
          const bodyStep = Math.max(20, Math.round(56 * textFormat.lineSpacing));
          const lineStep = Math.max(18, Math.round(52 * textFormat.lineSpacing));
          const titleFont = `700 ${titleFontSize}px system-ui, sans-serif`;
          const subtitleFont = `600 ${bodyFontSize}px system-ui, sans-serif`;
          const lineFont = `500 ${lineFontSize}px system-ui, sans-serif`;

          const resolveTextX = (drawWidth) => {
            if (textFormat.textAlign === 'left') return textCardPaddingLeft;
            if (textFormat.textAlign === 'right') return drawWidth - textCardPaddingRight;
            return drawWidth / 2 + ((textCardPaddingLeft - textCardPaddingRight) / 2);
          };

          const resolveTextY = (drawHeight, totalHeight) => {
            if (textFormat.verticalAlign === 'top') return 80;
            if (textFormat.verticalAlign === 'bottom') return Math.max(80, drawHeight - totalHeight - 70);
            return Math.max(80, Math.round((drawHeight - totalHeight) / 2));
          };

          const drawWithOrientation = (drawFn) => {
            if (textFormat.orientation !== 'vertical') {
              drawFn(canvas.width, canvas.height);
              return;
            }
            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.translate(-canvas.height / 2, -canvas.width / 2);
            drawFn(canvas.height, canvas.width);
            ctx.restore();
          };

          const drawTextEllipsis = (text, x, y, maxWidth, font, color = 'rgba(255,255,255,0.92)') => {
            ctx.font = font;
            ctx.fillStyle = color;
            const raw = String(text || '').trim();
            if (!raw) return;
            if (ctx.measureText(raw).width <= maxWidth) {
              ctx.fillText(raw, x, y, maxWidth);
              return;
            }
            const suffix = '...';
            let trimmed = raw;
            while (trimmed.length > 1 && ctx.measureText(`${trimmed}${suffix}`).width > maxWidth) {
              trimmed = trimmed.slice(0, -1).trim();
            }
            ctx.fillText(`${trimmed}${suffix}`, x, y, maxWidth);
          };

          const paintSquareFrame = (timestampMs = performance.now()) => {
            const liveSquareExtras = normalizeSquareBoxExtras(box, box);
            const liveSquareState = resolveSquareStateForEntry(entry, i);
            const liveSquareFrame = resolveSquareFrameForState(liveSquareExtras, liveSquareState, timestampMs);
            const runtimeSquareExtras = applySquareVisualFrameToExtras(liveSquareExtras, liveSquareFrame);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = clampOpacity(runtimeSquareExtras.backgroundOpacity, backgroundOpacity);
            ctx.fillStyle = buildGradientFill(runtimeSquareExtras);
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = 1;
            paintSquareTexturePatternLayer(ctx, canvas.width, canvas.height, runtimeSquareExtras, timestampMs);

            const anim = interpolateSquareAnimation({ ...(box || {}), ...runtimeSquareExtras }, timestampMs);
            const surfaceScaleToCanvas = Math.sqrt((canvas.width * canvas.height) / (1024 * 1024));

            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.translate((anim.offsetX * 0.005) * canvas.width, (anim.offsetY * 0.005) * canvas.height);
            ctx.rotate((anim.rotate * Math.PI) / 180);
            ctx.scale(anim.scale, anim.scale);
            ctx.globalAlpha = clampOpacity(anim.opacity, 1);

            runtimeSquareExtras.icons.forEach((icon) => {
              const iconAnim = interpolateSquareIconAnimation(
                icon,
                timestampMs,
                runtimeSquareExtras.animationDuration,
                runtimeSquareExtras.animationMode,
                runtimeSquareExtras.animationDirection
              );
              const iconX = ((icon.x + iconAnim.offsetX) * 0.005) * canvas.width;
              const iconY = ((icon.y + iconAnim.offsetY) * 0.005) * canvas.height;
              const symbolSize = Math.max(12, icon.size * iconAnim.scale * surfaceScaleToCanvas);
              const shapeSize = Math.max(10, symbolSize * 0.72);
              const iconOpacity = clampOpacity(icon.opacity * iconAnim.opacity, 1);
              const iconRotation = icon.rotation + iconAnim.rotate;
              const iconBlur = clampRange(icon.blur + iconAnim.blur, 0, 0, 24);
              const blurPx = iconBlur * Math.max(0.35, surfaceScaleToCanvas * 0.8);
              const iconSourceType = normalizeSquareIconSourceType(icon.sourceType, 'symbol');
              const iconImage = iconSourceType === 'image' ? resolveSquareIconImage(icon) : null;
              const drawImageIcon = !!iconImage?.loaded && !!iconImage?.image;

              if (icon.shape !== 'none' && !drawImageIcon) {
                ctx.save();
                ctx.translate(iconX, iconY);
                ctx.rotate((iconRotation * Math.PI) / 180);
                ctx.filter = blurPx > 0 ? `blur(${blurPx.toFixed(2)}px)` : 'none';
                ctx.fillStyle = icon.color;
                ctx.globalAlpha = clampOpacity(iconOpacity * 0.24, 0.24);
                if (icon.shape === 'circle') {
                  ctx.beginPath();
                  ctx.arc(0, 0, shapeSize * 0.5, 0, Math.PI * 2);
                  ctx.fill();
                } else if (icon.shape === 'diamond') {
                  ctx.beginPath();
                  ctx.moveTo(0, -shapeSize * 0.5);
                  ctx.lineTo(shapeSize * 0.5, 0);
                  ctx.lineTo(0, shapeSize * 0.5);
                  ctx.lineTo(-shapeSize * 0.5, 0);
                  ctx.closePath();
                  ctx.fill();
                } else if (icon.shape === 'star') {
                  const spikes = 5;
                  const outer = shapeSize * 0.5;
                  const inner = outer * 0.48;
                  let rot = -Math.PI / 2;
                  const step = Math.PI / spikes;
                  ctx.beginPath();
                  ctx.moveTo(0, -outer);
                  for (let s = 0; s < spikes; s += 1) {
                    ctx.lineTo(Math.cos(rot) * outer, Math.sin(rot) * outer);
                    rot += step;
                    ctx.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner);
                    rot += step;
                  }
                  ctx.closePath();
                  ctx.fill();
                } else {
                  ctx.fillRect(-shapeSize / 2, -shapeSize / 2, shapeSize, shapeSize);
                }
                ctx.restore();
              }

              ctx.save();
              ctx.translate(iconX, iconY);
              ctx.rotate((iconRotation * Math.PI) / 180);
              ctx.filter = blurPx > 0 ? `blur(${blurPx.toFixed(2)}px)` : 'none';
              ctx.font = `700 ${Math.round(symbolSize)}px "Segoe UI Symbol", "Apple Color Emoji", sans-serif`;
              ctx.fillStyle = icon.color;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.globalAlpha = iconOpacity;
              if (drawImageIcon) {
                const sizePx = Math.max(14, symbolSize);
                ctx.drawImage(iconImage.image, -sizePx / 2, -sizePx / 2, sizePx, sizePx);
              } else {
                ctx.fillText(icon.symbol, 0, 0);
              }
              ctx.restore();
            });

            ctx.restore();

            const titleText = String(t1 || '').trim();
            const subtitleText = String(t2 || '').trim();
            const squareTextVisible = runtimeSquareExtras.textVisible !== false;

            if (squareTextVisible) {
              const contentBlocks = runtimeSquareExtras.contentBlocks.length
                ? runtimeSquareExtras.contentBlocks
                : normalizeSquareContentBlocks([], subtitleText);

              drawWithOrientation((drawWidth, drawHeight) => {
                const paddingLeft = 64;
                const paddingRight = 64;
                const contentWidth = Math.max(120, drawWidth - paddingLeft - paddingRight);
                const baseTitleSize = Math.max(20, Math.round(46 * Math.max(0.8, textFormat.fontScale)));
                const baseTitleStep = Math.max(24, Math.round(baseTitleSize * Math.max(1.1, textFormat.lineSpacing)));
                const titleRows = titleText
                  ? wrapTextWithFont(titleText, contentWidth, `700 ${baseTitleSize}px system-ui, sans-serif`)
                  : [];

                const blockLayouts = contentBlocks.map((block) => {
                  const fontSize = Math.max(12, Math.round(block.size));
                  const step = Math.max(16, Math.round(fontSize * block.lineHeight));
                  const fontFamily = resolveSquareContentFontFamily(block.fontFamily, 'system');
                  const font = `${block.italic ? 'italic ' : ''}${block.weight} ${fontSize}px ${fontFamily}`;
                  const rows = wrapTextWithFont(block.text, contentWidth, font);
                  return { block, fontSize, step, font, rows: rows.length ? rows : [''] };
                });

                const titleGap = titleRows.length && blockLayouts.length ? Math.max(10, Math.round(16 * textFormat.lineSpacing)) : 0;
                const blockGap = 6;
                let totalHeight = 0;
                if (titleRows.length) totalHeight += titleRows.length * baseTitleStep;
                totalHeight += titleGap;
                blockLayouts.forEach((layout, index) => {
                  totalHeight += layout.rows.length * layout.step;
                  if (index < blockLayouts.length - 1) totalHeight += blockGap;
                });

                let y = Math.max(68, Math.round((drawHeight - totalHeight) / 2));
                const titleX = drawWidth / 2;

                if (titleRows.length) {
                  ctx.font = `700 ${baseTitleSize}px system-ui, sans-serif`;
                  ctx.fillStyle = textColor;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'top';
                  for (const row of titleRows) {
                    ctx.globalAlpha = 1;
                    ctx.fillText(row, titleX, y, contentWidth);
                    y += baseTitleStep;
                  }
                }

                y += titleGap;

                blockLayouts.forEach((layout, index) => {
                  const align = normalizeSquareContentAlign(layout.block.align, 'center');
                  const x = align === 'left'
                    ? paddingLeft
                    : (align === 'right' ? drawWidth - paddingRight : drawWidth / 2);
                  ctx.font = layout.font;
                  ctx.fillStyle = layout.block.color || textColor;
                  ctx.textAlign = align;
                  ctx.textBaseline = 'top';
                  ctx.globalAlpha = clampOpacity(layout.block.opacity, 1);

                  for (const row of layout.rows) {
                    if (row) ctx.fillText(row, x, y, contentWidth);
                    y += layout.step;
                  }

                  if (index < blockLayouts.length - 1) y += blockGap;
                });

                ctx.globalAlpha = 1;
              });
            }

            return runtimeSquareExtras;
          };

          let latestSquareRuntimeExtras = initialPaintExtras;
          if (isSquareCard) {
            latestSquareRuntimeExtras = paintSquareFrame(performance.now());
            applySquareSurfaceSettings(latestSquareRuntimeExtras, null, performance.now());
          } else if (tableHeaders.length && tableRows.length) {
            if (boxTextVisible && t1) {
              const tableTitleFont = Math.max(13, Math.round(58 * effectiveFontScale));
              ctx.font = `700 ${tableTitleFont}px system-ui, sans-serif`;
              ctx.fillStyle = 'rgba(255,255,255,0.96)';
              ctx.fillText(t1, cx, 110, canvas.width - textCardTextGutter);
            }

            const left = 56;
            const right = canvas.width - 56;
            const top = t1 ? 170 : 110;
            const bottom = canvas.height - 58;
            const tableWidth = Math.max(120, right - left);
            const tableHeight = Math.max(120, bottom - top);
            const columnCount = tableHeaders.length;
            const statusColumnIndex = columnCount - 1;
            const maxBodyRows = Math.max(1, Math.floor((tableHeight - 42) / 36));
            const visibleRows = tableRows.slice(0, maxBodyRows);
            const hiddenRows = Math.max(0, tableRows.length - visibleRows.length);
            const rowCount = visibleRows.length + 1;
            const rowHeight = Math.max(34, Math.floor(tableHeight / Math.max(2, rowCount)));
            const colWidth = tableWidth / Math.max(1, columnCount);
            const headerFontSize = Math.max(11, Math.round((26 - columnCount * 2) * effectiveFontScale));
            const bodyFontSize = Math.max(10, Math.round((22 - columnCount) * effectiveFontScale));

            ctx.fillStyle = 'rgba(255,255,255,0.08)';
            ctx.fillRect(left, top, tableWidth, rowHeight);

            for (let rowIndex = 0; rowIndex < visibleRows.length; rowIndex += 1) {
              const rowTop = top + rowHeight * (rowIndex + 1);
              ctx.fillStyle = rowIndex % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.01)';
              ctx.fillRect(left, rowTop, tableWidth, rowHeight);
            }

            ctx.strokeStyle = 'rgba(255,255,255,0.24)';
            ctx.lineWidth = 2;
            ctx.strokeRect(left, top, tableWidth, rowHeight * rowCount);

            ctx.beginPath();
            for (let c = 1; c < columnCount; c += 1) {
              const x = left + colWidth * c;
              ctx.moveTo(x, top);
              ctx.lineTo(x, top + rowHeight * rowCount);
            }
            for (let r = 1; r < rowCount; r += 1) {
              const y = top + rowHeight * r;
              ctx.moveTo(left, y);
              ctx.lineTo(left + tableWidth, y);
            }
            ctx.strokeStyle = 'rgba(255,255,255,0.16)';
            ctx.lineWidth = 1;
            ctx.stroke();

            if (boxTextVisible) {
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              for (let c = 0; c < columnCount; c += 1) {
                const x = left + colWidth * c + colWidth / 2;
                const y = top + rowHeight / 2;
                drawTextEllipsis(tableHeaders[c], x, y, colWidth - 12, `700 ${headerFontSize}px system-ui, sans-serif`);
              }

              for (let rowIndex = 0; rowIndex < visibleRows.length; rowIndex += 1) {
                const row = visibleRows[rowIndex];
                const y = top + rowHeight * (rowIndex + 1) + rowHeight / 2;
                for (let c = 0; c < columnCount; c += 1) {
                  const x = left + colWidth * c + colWidth / 2;
                  const value = String(row[c] || '').trim() || '-';
                  let color = 'rgba(255,255,255,0.90)';
                  if (c === statusColumnIndex) {
                    const upper = value.toUpperCase();
                    if (upper.includes('IKKE')) {
                      color = '#ffb3b3';
                    } else if (upper.includes('PÅ LAGER') || upper.includes('PAA LAGER')) {
                      color = '#96ffd0';
                    }
                  }
                  drawTextEllipsis(value, x, y, colWidth - 12, `600 ${bodyFontSize}px system-ui, sans-serif`, color);
                }
              }

              if (hiddenRows > 0) {
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                const hiddenRowsFont = Math.max(10, Math.round(22 * effectiveFontScale));
                ctx.font = `500 ${hiddenRowsFont}px system-ui, sans-serif`;
                ctx.fillStyle = 'rgba(255,255,255,0.74)';
                ctx.fillText(`+${hiddenRows} flere varianter`, cx, Math.min(canvas.height - 24, top + rowHeight * rowCount + 28), canvas.width - textCardTextGutter);
              }
            }
          } else if (lines.length && boxTextVisible) {
            drawWithOrientation((drawWidth, drawHeight) => {
              const maxWidth = drawWidth - textCardTextGutter;
              const titleRows = t1 ? wrapTextWithFont(t1, maxWidth, titleFont) : [];
              const bodyRows = [];
              lines.forEach((line, index) => {
                const wrapped = wrapTextWithFont(line, maxWidth, lineFont);
                if (wrapped.length) bodyRows.push(...wrapped);
                if (index < lines.length - 1) bodyRows.push('');
              });

              let totalHeight = 0;
              if (titleRows.length) totalHeight += titleRows.length * titleStep;
              if (titleRows.length && bodyRows.length) totalHeight += 20;
              totalHeight += bodyRows.length * lineStep;

              const x = resolveTextX(drawWidth);
              let y = resolveTextY(drawHeight, totalHeight);

              if (titleRows.length) {
                ctx.font = titleFont;
                ctx.fillStyle = textColor;
                ctx.textAlign = textFormat.textAlign;
                ctx.textBaseline = 'top';
                for (const row of titleRows) {
                  ctx.fillText(row, x, y, maxWidth);
                  y += titleStep;
                }
              }

              if (titleRows.length && bodyRows.length) y += 20;
              ctx.font = lineFont;
              ctx.fillStyle = textColor;
              for (const row of bodyRows) {
                if (row) ctx.fillText(row, x, y, maxWidth);
                y += lineStep;
                if (y > drawHeight - 56) break;
              }
            });
          } else if (boxTextVisible) {
            drawWithOrientation((drawWidth, drawHeight) => {
              const maxWidth = drawWidth - textCardTextGutter;
              const titleRows = t1 ? wrapTextWithFont(t1, maxWidth, titleFont) : [];
              const subtitleRows = t2 ? wrapTextWithFont(t2, maxWidth, subtitleFont) : [];

              let totalHeight = 0;
              if (titleRows.length) totalHeight += titleRows.length * titleStep;
              if (titleRows.length && subtitleRows.length) totalHeight += 20;
              if (subtitleRows.length) totalHeight += subtitleRows.length * bodyStep;

              const x = resolveTextX(drawWidth);
              let y = resolveTextY(drawHeight, totalHeight);

              if (titleRows.length) {
                ctx.font = titleFont;
                ctx.fillStyle = textColor;
                ctx.textAlign = textFormat.textAlign;
                ctx.textBaseline = 'top';
                for (const row of titleRows) {
                  ctx.fillText(row, x, y, maxWidth);
                  y += titleStep;
                }
              }

              if (titleRows.length && subtitleRows.length) y += 20;
              if (subtitleRows.length) {
                ctx.font = subtitleFont;
                ctx.fillStyle = textColor;
                for (const row of subtitleRows) {
                  ctx.fillText(row, x, y, maxWidth);
                  y += bodyStep;
                  if (y > drawHeight - 56) break;
                }
              }
            });
          }
          const tex = prepareImageTexture(new THREE.CanvasTexture(canvas), renderer);
          if (isSquareCard) {
            const squareMaterial = ensureSquareRuntimeMaterial(mesh, latestSquareRuntimeExtras.materialType);
            if (squareMaterial) {
              squareMaterial.map = tex;
              applySquareRuntimeMaterialSettings(squareMaterial, latestSquareRuntimeExtras);
            }
            applySquareSurfaceSettings(latestSquareRuntimeExtras, tex, performance.now());
          } else {
            const m = mesh.material;
            applyMediaMapToMaterial(m, tex);
            if ('displacementMap' in m) {
              m.displacementMap = null;
              m.displacementScale = 0;
              m.displacementBias = 0;
            }
            m.needsUpdate = true;
          }
          if (isSquareCard) {
            animatedBoxPainters.push((timestampMs) => {
              latestSquareRuntimeExtras = paintSquareFrame(timestampMs);
              applySquareSurfaceSettings(latestSquareRuntimeExtras, tex, timestampMs);
              tex.needsUpdate = true;
            });
          }
        } finally {
          setAssetsPending(p => p - 1);
        }
      } else if (entry.kind === 'image') {
        const mediaBoxOptions = normalizeMediaCardBoxOptions(entry?.box || {}, entry?.box || {});
        const statusKey = galleryImageStatusKey(entry, i);
        const lowSource = entry.optimizedUrl || entry.url || entry.originalUrl || entry.thumbUrl;
        const webSource = lowSource;
        const hiSource = lowSource;
        const lowUrl = assetUrl(lowSource || '');
        const webUrl = assetUrl(webSource || '');
        const hiUrl = assetUrl(hiSource || '');

        const runtime = {
          index: i,
          mesh,
          mediaBoxOptions,
          statusKey,
          lowUrl,
          webUrl,
          hiUrl,
          lowTexture: null,
          webTexture: null,
          hiTexture: null,
          activeTexture: null,
          currentTier: 'LOFI',
          hideStatusTimer: 0,
        };
        imageRuntimeByIndex.set(i, runtime);
        setImageTierStatus(statusKey, { tier: 'LOFI', progress: 0.08, visible: true });

        setAssetsPending(p => p + 1);
        if (!lowUrl) {
          markLowResLoaded();
          setImageTierStatus(statusKey, { tier: 'LOFI', progress: 0.15, visible: true });
          setAssetsPending(p => p - 1);
          continue;
        }

        let done = false;
        const timeout = window.setTimeout(() => {
          if (done) return;
          done = true;
          setImageTierStatus(statusKey, { tier: 'LOFI', progress: 0.22, visible: true });
          markLowResLoaded();
          setAssetsPending(p => p - 1);
        }, 8000);

        const finish = (loaded = false) => {
          if (done) return;
          done = true;
          window.clearTimeout(timeout);
          markLowResLoaded();
          setAssetsPending(p => p - 1);
          if (loaded) {
            processFocusedUpgrade();
          }
        };

        loader.load(lowUrl, tex => {
          if (isEffectDisposed()) {
            try { tex.dispose?.(); } catch {}
            finish(false);
            return;
          }
          prepareImageTexture(tex, renderer);
          const img = tex.image || {};
          const aspect = img.width && img.height ? img.width / img.height : 1.5;
          const { width: w, height: h } = resolveMediaSpanDimensions(aspect, entry?.widthSpan, planeH, marginY);
          const mediaGeometry = buildSquareCardGeometry(w, h, {
            geometryType: 'plane',
            edgeSmoothness: mediaBoxOptions.edgeSmoothness,
            planeResolution: mediaBoxOptions.planeResolution,
            planeFoldX: mediaBoxOptions.planeFoldX,
            planeFoldY: mediaBoxOptions.planeFoldY,
            geometryDepth: mediaBoxOptions.geometryDepth,
          });
          setMeshGeometryWithSize(mesh, mediaGeometry, w, h);
          applyMediaBorderFrame(mesh, w, h, mediaBoxOptions, animatedBoxPainters);
          applyMediaSurfaceMaterials(mesh, tex, mediaBoxOptions);
          runtime.lowTexture = tex;
          if (!runtime.webUrl || runtime.webUrl === runtime.lowUrl) runtime.webTexture = tex;
          if (!runtime.hiUrl || runtime.hiUrl === runtime.lowUrl) runtime.hiTexture = tex;
          runtime.currentTier = 'LOFI';
          runtime.activeTexture = tex;
          setImageTierStatus(statusKey, { tier: 'LOFI', progress: 0.36, visible: true });
          layoutColumns();
          fitAll();
          finish(true);
        }, undefined, () => {
          const fallback = prepareImageTexture(new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat), renderer);
          const { width: fallbackW, height: fallbackH } = resolveMediaSpanDimensions(1.5, entry?.widthSpan, planeH, marginY);
          const mediaGeometry = buildSquareCardGeometry(fallbackW, fallbackH, {
            geometryType: 'plane',
            edgeSmoothness: mediaBoxOptions.edgeSmoothness,
            planeResolution: mediaBoxOptions.planeResolution,
            planeFoldX: mediaBoxOptions.planeFoldX,
            planeFoldY: mediaBoxOptions.planeFoldY,
            geometryDepth: mediaBoxOptions.geometryDepth,
          });
          setMeshGeometryWithSize(mesh, mediaGeometry, fallbackW, fallbackH);
          applyMediaBorderFrame(mesh, fallbackW, fallbackH, mediaBoxOptions, animatedBoxPainters);
          applyMediaSurfaceMaterials(mesh, fallback, mediaBoxOptions);
          runtime.lowTexture = fallback;
          if (!runtime.webUrl || runtime.webUrl === runtime.lowUrl) runtime.webTexture = fallback;
          if (!runtime.hiUrl || runtime.hiUrl === runtime.lowUrl) runtime.hiTexture = fallback;
          runtime.currentTier = 'LOFI';
          runtime.activeTexture = fallback;
          setImageTierStatus(statusKey, { tier: 'LOFI', progress: 0.34, visible: true });
          layoutColumns();
          fitAll();
          finish(true);
        });
      } else {
        const mediaBoxOptions = normalizeMediaCardBoxOptions(entry?.box || {}, entry?.box || {});
        const yt = entry.youtubeId || youtubeIdFromUrl(entry.storageUrl || '');
        if (yt) {
          const { width: w, height: h } = resolveMediaSpanDimensions(16 / 9, entry?.widthSpan, planeH, marginY);
          const mediaGeometry = buildSquareCardGeometry(w, h, {
            geometryType: 'plane',
            edgeSmoothness: mediaBoxOptions.edgeSmoothness,
            planeResolution: mediaBoxOptions.planeResolution,
            planeFoldX: mediaBoxOptions.planeFoldX,
            planeFoldY: mediaBoxOptions.planeFoldY,
            geometryDepth: mediaBoxOptions.geometryDepth,
          });
          setMeshGeometryWithSize(mesh, mediaGeometry, w, h);
          applyMediaBorderFrame(mesh, w, h, mediaBoxOptions, animatedBoxPainters);
          const mat = mesh.material; mat.color.set(0xffffff); mat.emissive.set(0x111111);
          const thumb = entry.thumbUrl || `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
          setAssetsPending(p => p + 1);
          loader.load(assetUrl(thumb), t => {
            prepareImageTexture(t, renderer);
            applyMediaSurfaceMaterials(mesh, t, mediaBoxOptions);
            layoutColumns();
            fitAll();
            setAssetsPending(p => p - 1);
          });
          const ifr = document.createElement('iframe');
          ifr.src = `https://www.youtube.com/embed/${yt}?rel=0&playsinline=1&enablejsapi=1&mute=1`;
          ifr.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
          ifr.setAttribute('allowfullscreen', 'true'); ifr.style.position = 'absolute'; ifr.style.border = '0'; ifr.style.display = 'none';
          ifr.style.pointerEvents = 'none';
          overlay.appendChild(ifr); iframes[i] = ifr;
        } else {
          const source = entry.optimizedUrl || entry.storageUrl || entry.originalUrl;
          if (!source) { continue; }
          const v = document.createElement('video');
          v.src = assetUrl(source);
          v.crossOrigin = 'anonymous'; v.muted = true; v.loop = true; v.playsInline = true; v.preload = 'metadata';
          if (entry.thumbUrl) v.poster = assetUrl(entry.thumbUrl);
          localVideos[i] = v;
          v.addEventListener('loadedmetadata', () => {
            const aspect = v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : 16 / 9;
            const { width: finalW, height: finalH } = resolveMediaSpanDimensions(aspect, entry?.widthSpan, planeH, marginY);
            const mediaGeometry = buildSquareCardGeometry(finalW, finalH, {
              geometryType: 'plane',
              edgeSmoothness: mediaBoxOptions.edgeSmoothness,
              planeResolution: mediaBoxOptions.planeResolution,
              planeFoldX: mediaBoxOptions.planeFoldX,
              planeFoldY: mediaBoxOptions.planeFoldY,
              geometryDepth: mediaBoxOptions.geometryDepth,
            });
            setMeshGeometryWithSize(mesh, mediaGeometry, finalW, finalH);
            applyMediaBorderFrame(mesh, finalW, finalH, mediaBoxOptions, animatedBoxPainters);
            const vt = prepareVideoTexture(new THREE.VideoTexture(v));
            applyMediaSurfaceMaterials(mesh, vt, mediaBoxOptions);
            try { v.play().catch(() => {}); } catch {}
            layoutColumns();
            fitAll();
            setAssetsPending(p => p - 1);
          }, { once: true });
          setAssetsPending(p => p + 1);
          setTimeout(() => { try { v.play().catch(() => {}); } catch {} }, 300);
        }
      }
    }

    layoutColumns();

    const baseCam = new THREE.Vector3(); const lookBase = new THREE.Vector3(0, 0, depthZ); const focus = new THREE.Vector3(); let farBaseZ = depthZ + 40; let dollyZ = depthZ + 20; const zWideRef = { z: depthZ + 20 };
    const targetFocus = focus.clone();
    const galleryBounds = { minX: -Infinity, maxX: Infinity, minY: -Infinity, maxY: Infinity };
    let orbitEnabled = false; let orbitYaw = 0; let orbitPitch = 0; const orbitRadiusRef = { r: 40 };
    const CAMERA_DRAG_INERTIA_MS = 500;
    const ORBIT_DRAG_INERTIA_MULTIPLIER = 9;
    const ORBIT_DRAG_SENSITIVITY_MULTIPLIER = 2;
    const ORBIT_VERTICAL_DRAG_SENSITIVITY_MULTIPLIER = 5;
    const inertiaRef = { active: false, start: 0, duration: CAMERA_DRAG_INERTIA_MS, vx: 0, vy: 0, yaw: 0, pitch: 0 };
    activeIndex = configuredStartIndex;
    let keepZoomOnNextCenter = false;
    let wheelZoomLockUntil = 0;
    const focusedBoxScroll = { index: -1, offset: 0, overflowPx: 0 };
    const activePlaneProbe = new THREE.Vector3();
    const boundsProbe = new THREE.Vector3();
    const cameraFrontPadding = 0.1;

    function getPlaneFocusDepthZ(idx) {
      const p = planes[idx];
      if (!p) return depthZ;
      p.getWorldPosition(activePlaneProbe);
      return Number.isFinite(activePlaneProbe.z) ? activePlaneProbe.z : depthZ;
    }

    function getMinCameraZForFocusDepth(focusDepthZ) {
      return focusDepthZ + cameraFrontPadding;
    }

    function getActiveZoomBounds() {
      const focusDepthZ = getPlaneFocusDepthZ(activeIndex);
      const minZ = getMinCameraZForFocusDepth(focusDepthZ);
      const maxZ = Math.max(minZ + 0.4, farBaseZ + 80);
      return { minZ, maxZ };
    }

    function isOutsideFogView() {
      const zoomBounds = getActiveZoomBounds();
      const span = Math.max(0.6, zoomBounds.maxZ - zoomBounds.minZ);
      const outsideThreshold = Math.max(0.8, span * 0.045);
      return dollyZ >= (zoomBounds.maxZ - outsideThreshold);
    }

    thrusterZoomOutRef.current = (durationMs = THRUSTER_TRANSITION_MS) => {
      const zoomBounds = getActiveZoomBounds();
      const outsideFog = isOutsideFogView();
      const preserveOrbitOnZoomIn = outsideFog && orbitEnabled;
      const targetZ = outsideFog
        ? getFocusedFrameCameraZ(activeIndex)
        : zoomBounds.maxZ;
      transitionRef.current = {
        active: true,
        mode: 'direct',
        start: performance.now(),
        duration: Math.max(200, Number(durationMs) || THRUSTER_TRANSITION_MS),
        from: baseCam.clone(),
        to: baseCam.clone(),
        fromLook: lookBase.clone(),
        toLook: lookBase.clone(),
        fromZ: dollyZ,
        toZ: THREE.MathUtils.clamp(targetZ, zoomBounds.minZ, zoomBounds.maxZ),
      };
      orbitEnabled = preserveOrbitOnZoomIn;
      return outsideFog ? 'zoom-in' : 'zoom-out';
    };

    function getFocusedFrameCameraZ(idx) {
      const p = planes[idx];
      const focusDepthZ = getPlaneFocusDepthZ(idx);
      if (!p) return getMinCameraZForFocusDepth(focusDepthZ);

      const focusInfoTextSettings = normalizeInfoTextSettings(items[idx]?.box?.infoTextSettings, {});
      const focusTextZoomOffset = clampRange(focusInfoTextSettings.textZIndex, 0, 0, 2);

      const { width: w, height: h } = getMeshCardDimensions(p, planeH);
      const focusFrameH = isTextBoxIndex(idx) ? planeH : h;
      const vFov = THREE.MathUtils.degToRad(baseFov);
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
      const dH = (focusFrameH / 2) / Math.max(0.0001, Math.tan(vFov / 2));
      const dW = (w / 2) / Math.max(0.0001, Math.tan(hFov / 2));
      const fittedZ = focusDepthZ + Math.max(dH, dW) + 0.2 + focusTextZoomOffset;

      return Math.max(getMinCameraZForFocusDepth(focusDepthZ), fittedZ);
    }

    function getSafeTransitCameraZ() {
      let maxItemZ = depthZ;
      let maxCardW = 1;
      let maxCardH = planeH;
      for (let i = 0; i < planes.length; i += 1) {
        const plane = planes[i];
        if (!plane) continue;
        plane.getWorldPosition(boundsProbe);
        if (Number.isFinite(boundsProbe.z)) maxItemZ = Math.max(maxItemZ, boundsProbe.z);
        const { width: w, height: h } = getMeshCardDimensions(plane, planeH);
        if (Number.isFinite(w) && w > 0) maxCardW = Math.max(maxCardW, w);
        if (Number.isFinite(h) && h > 0) maxCardH = Math.max(maxCardH, h);
      }

      const vFov = THREE.MathUtils.degToRad(baseFov);
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
      const frameDistH = (maxCardH / 2) / Math.max(0.0001, Math.tan(vFov / 2));
      const frameDistW = (maxCardW / 2) / Math.max(0.0001, Math.tan(hFov / 2));
      const frameDistance = Math.max(frameDistH, frameDistW);
      const safetyPadding = Math.max(1.4, frameDistance * 0.28);
      const safeFrontZ = maxItemZ + frameDistance + safetyPadding;
      return Math.max(zWideRef.z || safeFrontZ, safeFrontZ);
    }

    const isSquareCanvasEntry = (entry) => !!entry?.box?.square;
    const isTextBoxEntry = (entry) => isTextBoxGalleryItem(entry);
    const isTextBoxIndex = (idx) => isTextBoxEntry(items[idx]);
    function clampTargetToBounds() {
      const padX = 0.6, padY = 0.6;
      targetFocus.x = THREE.MathUtils.clamp(targetFocus.x, galleryBounds.minX + padX, galleryBounds.maxX - padX);
      targetFocus.y = THREE.MathUtils.clamp(targetFocus.y, galleryBounds.minY + padY, galleryBounds.maxY - padY);
    }
    function fitAll() {
      if (planes.length === 0) return; let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity; const tmp = new THREE.Vector3();
      let maxItemW = 1;
      for (const p of planes) {
        p.getWorldPosition(tmp);
        const { width: w, height: h } = getMeshCardDimensions(p, planeH);
        maxItemW = Math.max(maxItemW, w);
        minX = Math.min(minX, tmp.x - w / 2); maxX = Math.max(maxX, tmp.x + w / 2);
        minY = Math.min(minY, tmp.y - h / 2); maxY = Math.max(maxY, tmp.y + h / 2);
      }
      const margin = 0.8; const vHalf = (maxY - minY) / 2 + margin; const hHalf = (maxX - minX) / 2 + margin; const centerY = (minY + maxY) / 2; const yOffset = 0.6;
      const vFov = THREE.MathUtils.degToRad(baseFov); const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
      const dV = vHalf / Math.tan(vFov / 2); const dH = hHalf / Math.tan(hFov / 2);
      const distWide = Math.max(dV, dH);
      const zWide = depthZ + distWide + 1;
      zWideRef.z = zWide;
      farBaseZ = zWide; baseCam.set(0, centerY + yOffset, zWide); lookBase.set(0, centerY + yOffset, depthZ);
      galleryBounds.minX = minX; galleryBounds.maxX = maxX; galleryBounds.minY = minY; galleryBounds.maxY = maxY;
      const dV_item = (planeH / 2) / Math.tan(vFov / 2);
      const dH_item = (maxItemW / 2) / Math.tan(hFov / 2);
      const distItem = Math.max(dV_item, dH_item);
      const zItem = depthZ + distItem + 0.2;
      const zoomBlend = THREE.MathUtils.clamp(initialZoom, 0, 1);
      const blended = zWide + (zItem - zWide) * zoomBlend;
      dollyZ = Math.max(depthZ + 0.5, blended);
      focus.set(0, centerY + yOffset, 0); targetFocus.set(0, centerY + yOffset, 0); clampTargetToBounds();
      camera.position.set(baseCam.x, baseCam.y, dollyZ); camera.lookAt(lookBase);
    }
    function applyFocusedTextBoxOffset(idx, offset) {
      const p = planes[idx];
      if (!p) return { hasScrollable: false, travel: 0, offset: 0 };
      const c = new THREE.Vector3();
      p.getWorldPosition(c);
      const { height: h } = getMeshCardDimensions(p, planeH);
      const travel = Math.max(0, h - planeH);
      const clampedOffset = THREE.MathUtils.clamp(offset, 0, travel);
      const focusY = c.y + (travel / 2) - clampedOffset;

      focusedBoxScroll.index = idx;
      focusedBoxScroll.offset = clampedOffset;

      targetFocus.x = c.x;
      targetFocus.y = focusY;
      clampTargetToBounds();

      return { hasScrollable: travel > 0.001, travel, offset: clampedOffset };
    }

    function scrollFocusedTextBoxByStep(direction, stepWorld, navigateOnOverflow) {
      const idx = activeIndex;
      if (!isTextBoxIndex(idx)) return false;
      const p = planes[idx];
      if (!p) return false;

      const { height: h } = getMeshCardDimensions(p, planeH);
      const travel = Math.max(0, h - planeH);
      if (travel <= 0.001) return false;

      const sign = direction >= 0 ? 1 : -1;
      const current = focusedBoxScroll.index === idx ? focusedBoxScroll.offset : 0;
      const rawNext = current + (sign * Math.max(0.01, stepWorld));
      const clampedNext = THREE.MathUtils.clamp(rawNext, 0, travel);
      applyFocusedTextBoxOffset(idx, clampedNext);
      focusedBoxScroll.overflowPx = 0;

      if (navigateOnOverflow && sign > 0 && rawNext > travel + 0.02) {
        const nextIdx = Math.min(items.length - 1, idx + 1);
        if (nextIdx !== idx) {
          keepZoomOnNextCenter = true;
          wheelZoomLockUntil = performance.now() + 420;
          setSelected(nextIdx);
          centerOn(nextIdx, false);
        }
      }

      return true;
    }

    function scrollFocusedTextBoxByWheel(wheelDelta, worldPerPixelY) {
      const idx = activeIndex;
      if (!isTextBoxIndex(idx)) return false;
      const p = planes[idx];
      if (!p) return false;

      const { height: h } = getMeshCardDimensions(p, planeH);
      const travel = Math.max(0, h - planeH);
      if (travel <= 0.001) return false;

      const current = focusedBoxScroll.index === idx ? focusedBoxScroll.offset : 0;
      const dampedWheelDelta = wheelDelta * 0.55;
      const deltaWorld = THREE.MathUtils.clamp(
        dampedWheelDelta * worldPerPixelY,
        -planeH * 0.32,
        planeH * 0.32
      );
      const rawNext = current + deltaWorld;
      const clampedNext = THREE.MathUtils.clamp(rawNext, 0, travel);
      applyFocusedTextBoxOffset(idx, clampedNext);

      const movingDown = wheelDelta > 0;
      const atBottom = clampedNext >= travel - 0.001;
      const consumedWorld = clampedNext - current;
      const residualWorld = deltaWorld - consumedWorld;

      if (movingDown && atBottom && residualWorld > 0) {
        const residualPx = worldPerPixelY > 1e-6 ? residualWorld / worldPerPixelY : 0;
        focusedBoxScroll.overflowPx = Math.max(0, focusedBoxScroll.overflowPx + residualPx);
        if (focusedBoxScroll.overflowPx > 160) {
          const nextIdx = Math.min(items.length - 1, idx + 1);
          if (nextIdx !== idx) {
            keepZoomOnNextCenter = true;
            wheelZoomLockUntil = performance.now() + 520;
            setSelected(nextIdx);
            centerOn(nextIdx, false);
          }
          focusedBoxScroll.overflowPx = 0;
        }
      } else {
        focusedBoxScroll.overflowPx = 0;
      }

      return true;
    }

    scrollFocusedTextBoxRef.current = (direction = 1) => {
      if (direction > 0) return scrollFocusedTextBoxByStep(1, planeH * 0.22, true);
      if (direction < 0) return scrollFocusedTextBoxByStep(-1, planeH * 0.22, false);
      return false;
    };

    function centerOn(idx, instant = false, half = false, focusPoint = null) {
      const p = planes[idx];
      if (!p) return;
      const c = new THREE.Vector3();
      p.getWorldPosition(c);
      const focusAnchor = focusPoint && Number.isFinite(focusPoint.x) && Number.isFinite(focusPoint.y) && Number.isFinite(focusPoint.z)
        ? focusPoint
        : c;
      const focusDepthZ = Number.isFinite(c.z) ? c.z : depthZ;
      const { width: w, height: h } = getMeshCardDimensions(p, planeH);
      const isFocusedTextBox = isTextBoxIndex(idx);
      const focusFrameH = isFocusedTextBox ? planeH : h;
      const focusInfoTextSettings = normalizeInfoTextSettings(items[idx]?.box?.infoTextSettings, {});
      const focusTextZoomOffset = clampRange(focusInfoTextSettings.textZIndex, 0, 0, 2);
      const vFov = THREE.MathUtils.degToRad(baseFov); const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect); const dH = (focusFrameH / 2) / Math.tan(vFov / 2); const dW = (w / 2) / Math.tan(hFov / 2);
      const dist = Math.max(dH, dW);
      let targetZ = focusDepthZ + dist + 0.2 + focusTextZoomOffset;
      const previousActiveIndex = activeIndex;
      const preserveOrbitView = orbitEnabled && !instant;
      activeIndex = idx;
      
      // Open object viewer for canvas and text box objects
      const selectedItem = items[idx];
      const isCanvasObject = selectedItem && selectedItem.kind === 'box' && selectedItem.box?.square;
      const isTextBoxObject = isTextBoxGalleryItem(selectedItem);
      if (isCanvasObject || isTextBoxObject) {
        setViewedObjectIndex(idx);
        setShowObjectViewer(true);
      } else {
        setShowObjectViewer(false);
      }
      
      if (instant && Number.isFinite(initialDistance) && initialDistance > 0) {
        targetZ = focusDepthZ + initialDistance + focusTextZoomOffset;
      }
      if (half) {
        const zw = zWideRef.z || targetZ;
        targetZ = zw + (targetZ - zw) * 0.5;
      }
      if (keepZoomOnNextCenter && !instant) {
        targetZ = dollyZ;
        keepZoomOnNextCenter = false;
      } else if (isFocusedTextBox) {
        const prevEntry = items[previousActiveIndex];
        const switchedFromMedia = !!prevEntry
          && !isTextBoxEntry(prevEntry)
          && (prevEntry.kind === 'image' || prevEntry.kind === 'video');
        if (switchedFromMedia) targetZ = dollyZ;
      }
      if (preserveOrbitView) {
        targetZ = dollyZ;
      }
      targetZ = Math.max(getMinCameraZForFocusDepth(focusDepthZ), targetZ);
      let focusY = c.y;
      if (isFocusedTextBox) {
        const travel = Math.max(0, Number(h) - planeH);
        const currentOffset = focusedBoxScroll.index === idx ? focusedBoxScroll.offset : 0;
        const clampedOffset = THREE.MathUtils.clamp(currentOffset, 0, travel);
        focusY = c.y + (travel / 2) - clampedOffset;
        focusedBoxScroll.index = idx;
        focusedBoxScroll.offset = clampedOffset;
        focusedBoxScroll.overflowPx = 0;
      } else {
        focusedBoxScroll.index = -1;
        focusedBoxScroll.offset = 0;
        focusedBoxScroll.overflowPx = 0;
      }
      const targetFocusY = isFocusedTextBox ? focusY : focusAnchor.y;
      const targetPos = new THREE.Vector3(focusAnchor.x, targetFocusY, targetZ); const targetLook = new THREE.Vector3(focusAnchor.x, targetFocusY, focusDepthZ);
      targetFocus.set(focusAnchor.x, targetFocusY, focusAnchor.z); clampTargetToBounds();
      inertiaRef.active = false;
      if (preserveOrbitView) {
        if (transitionRef.current) transitionRef.current.active = false;
      } else if (instant || !transitionRef.current) {
        baseCam.copy(targetPos); lookBase.copy(targetLook); dollyZ = targetZ; transitionRef.current.active = false; focus.copy(targetFocus);
      } else {
        const selectionChanged = idx !== previousActiveIndex;
        const transitionDurationMs = selectionChanged ? Math.round(1000 / 0.3) : 1000;
        const safeTransitZ = getSafeTransitCameraZ();
        const movingOutwardToTarget = targetZ > dollyZ + 0.04;
        const requiresSafeTransit = selectionChanged
          && movingOutwardToTarget
          && safeTransitZ > Math.max(dollyZ, targetZ) + 0.04;
        if (requiresSafeTransit) {
          transitionRef.current = {
            active: true,
            mode: 'zoom-out-pan-zoom',
            zoomOutRatio: 0.24,
            panRatio: 0.52,
            start: performance.now(),
            duration: transitionDurationMs,
            from: baseCam.clone(),
            to: targetPos.clone(),
            fromLook: lookBase.clone(),
            toLook: targetLook.clone(),
            fromZ: dollyZ,
            viaZ: safeTransitZ,
            toZ: targetZ
          };
        } else {
          transitionRef.current = {
            active: true,
            mode: 'pan-then-zoom',
            panRatio: 0.58,
            start: performance.now(),
            duration: transitionDurationMs,
            from: baseCam.clone(),
            to: targetPos.clone(),
            fromLook: lookBase.clone(),
            toLook: targetLook.clone(),
            fromZ: dollyZ,
            toZ: targetZ
          };
        }
      }
      orbitEnabled = preserveOrbitView;
      syncImageTierForFocus();
    }
    fitAll();
    centerOn(configuredStartIndex, true, true);
    centerOnRef.current = (i, instant, half) => centerOn(i, instant, !!half);

    const raycaster = new THREE.Raycaster(); const pointer = new THREE.Vector2();
    const isTouchDevice = (() => {
      if (typeof window === 'undefined') return false;
      if (window.matchMedia?.('(pointer: coarse)').matches) return true;
      return navigator.maxTouchPoints > 0;
    })();
    const EDGE_SPIN_TRIGGER_PX = isTouchDevice ? 34 : 22;
    const EDGE_SPIN_MIN_YAW = isTouchDevice ? 0.24 : 0.18;
    const EDGE_SPIN_MIN_PITCH = isTouchDevice ? 0.9 : 0.65;
    let dragging = false, lastX = 0, lastY = 0, downX = 0, downY = 0, moved = false;
    let orbitDragLagRemaining = 0;
    let activePointerId = null;
    let hoverPlaneIndex = -1;
    let dragMode = 'camera';

    const resolvePointerEdgeSpin = (ev) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = Number(ev?.clientX);
      const y = Number(ev?.clientY);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return { hit: false, yaw: 0, pitch: 0 };
      }

      const left = x <= (rect.left + EDGE_SPIN_TRIGGER_PX);
      const right = x >= (rect.right - EDGE_SPIN_TRIGGER_PX);
      const top = y <= (rect.top + EDGE_SPIN_TRIGGER_PX);
      const bottom = y >= (rect.bottom - EDGE_SPIN_TRIGGER_PX);

      let yaw = 0;
      let pitch = 0;
      if (left && !right) yaw = 1;
      else if (right && !left) yaw = -1;
      if (top && !bottom) pitch = 1;
      else if (bottom && !top) pitch = -1;

      return { hit: yaw !== 0 || pitch !== 0, yaw, pitch };
    };

    const setHoverCursorState = (isHovering) => {
      renderer.domElement.style.cursor = isHovering ? 'all-scroll' : 'default';
      if (isHovering) {
        cursorFx.style.display = 'block';
        cursorFx.style.opacity = '1';
        cursorFx.style.transform = 'translate3d(-50%, -50%, 0) scale(1) rotate(0deg)';
        cursorFx.style.filter = 'blur(0px) saturate(1.18) hue-rotate(10deg)';
      } else {
        cursorFx.style.opacity = '0';
        cursorFx.style.transform = 'translate3d(-50%, -50%, 0) scale(0.62) rotate(-14deg)';
        cursorFx.style.filter = 'blur(1.7px) saturate(1.1) hue-rotate(0deg)';
      }
    };

    const updateHoverFromPointer = (ev) => {
      if (ev.pointerType === 'touch') {
        hoverPlaneIndex = -1;
        setHoverCursorState(false);
        return;
      }

      const rect = renderer.domElement.getBoundingClientRect();
      const relX = ev.clientX - rect.left;
      const relY = ev.clientY - rect.top;
      if (relX < 0 || relY < 0 || relX > rect.width || relY > rect.height) {
        hoverPlaneIndex = -1;
        setHoverCursorState(false);
        return;
      }

      cursorFx.style.left = `${relX}px`;
      cursorFx.style.top = `${relY}px`;
      pointer.x = (relX / rect.width) * 2 - 1;
      pointer.y = -((relY / rect.height) * 2 - 1);
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(planes, false);
      hoverPlaneIndex = hits.length ? planes.indexOf(hits[0].object) : -1;
      setHoverCursorState(hoverPlaneIndex >= 0);
    };

    renderer.domElement.style.touchAction = 'none';
    renderer.domElement.style.userSelect = 'none';
    function onPointerDown(ev) {
      if (activePointerId != null && ev.pointerId !== activePointerId) return;
      activePointerId = ev.pointerId;
      dragging = true; lastX = downX = ev.clientX; lastY = downY = ev.clientY; moved = false;
      orbitDragLagRemaining = isTouchDevice ? 44 : 30;
      updateHoverFromPointer(ev);
      dragMode = 'camera';
      if (ev.pointerType === 'touch') ev.preventDefault();
      try { renderer.domElement.setPointerCapture?.(ev.pointerId); } catch {}
      if (transitionRef.current) transitionRef.current.active = false;
    }
    function onPointerUp(ev) {
      if (activePointerId != null && ev.pointerId !== activePointerId) return;
      activePointerId = null;
      const dx = Math.abs(ev.clientX - downX); const dy = Math.abs(ev.clientY - downY);
      const clickThreshold = isTouchDevice ? 8 : 5;
      const clickish = dx < clickThreshold && dy < clickThreshold && !moved;
      if (clickish) {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
        raycaster.setFromCamera(pointer, camera);
        const hits = raycaster.intersectObjects(planes, false);
        if (hits.length) {
          const idx = planes.indexOf(hits[0].object);
          if (idx >= 0) {
            const clickedEntry = items[idx];
            const clickedFocusedSquare = idx === activeIndex && isSquareCanvasEntry(clickedEntry);
            setSelected(idx);
            if (clickedFocusedSquare) {
              const played = advanceSquareClickAnimation(idx, performance.now());
              if (!played || isOutsideFogView()) centerOn(idx, false, false, hits[0].point);
            } else {
              centerOn(idx, false, false, hits[0].point);
            }
            if (isMerchandiseMode && !clickedFocusedSquare) {
              setInfoItemIndex(null);
            }
          }
        }
      }
      if (moved) {
        const draggingOrbit = dragMode === 'orbit';
        const edgeSpin = resolvePointerEdgeSpin(ev);
        if (draggingOrbit && edgeSpin.hit) {
          if (edgeSpin.yaw !== 0) {
            inertiaRef.yaw = edgeSpin.yaw * Math.max(Math.abs(inertiaRef.yaw), EDGE_SPIN_MIN_YAW);
          }
          if (edgeSpin.pitch !== 0) {
            inertiaRef.pitch = edgeSpin.pitch * Math.max(Math.abs(inertiaRef.pitch), EDGE_SPIN_MIN_PITCH);
          }
        }

        const hasOrbitSpin = draggingOrbit
          && (Math.abs(inertiaRef.yaw) > 0.00001 || Math.abs(inertiaRef.pitch) > 0.00001);
        const hasCameraPanInertia = !draggingOrbit
          && (Math.abs(inertiaRef.vx) > 0.00001 || Math.abs(inertiaRef.vy) > 0.00001);
        if (hasOrbitSpin || hasCameraPanInertia) {
          inertiaRef.duration = hasOrbitSpin
            ? (CAMERA_DRAG_INERTIA_MS * ORBIT_DRAG_INERTIA_MULTIPLIER)
            : CAMERA_DRAG_INERTIA_MS;
          inertiaRef.active = true;
          inertiaRef.start = performance.now();
        } else {
          inertiaRef.duration = CAMERA_DRAG_INERTIA_MS;
          inertiaRef.active = false;
          inertiaRef.vx = 0;
          inertiaRef.vy = 0;
          inertiaRef.yaw = 0;
          inertiaRef.pitch = 0;
        }
      } else {
        inertiaRef.duration = CAMERA_DRAG_INERTIA_MS;
        inertiaRef.active = false;
        inertiaRef.vx = 0;
        inertiaRef.vy = 0;
        inertiaRef.yaw = 0;
        inertiaRef.pitch = 0;
      }
      dragging = false;
      orbitDragLagRemaining = 0;
      dragMode = 'camera';
      updateHoverFromPointer(ev);
      try { renderer.domElement.releasePointerCapture?.(ev.pointerId); } catch {}
    }
    function onPointerMove(ev) {
      if (activePointerId != null && ev.pointerId !== activePointerId) return;
      updateHoverFromPointer(ev);
      if (!dragging) return; const dx = ev.clientX - lastX; const dy = ev.clientY - lastY; lastX = ev.clientX; lastY = ev.clientY; if (Math.abs(ev.clientX - downX) > 4 || Math.abs(ev.clientY - downY) > 4) moved = true;
      if (ev.pointerType === 'touch') ev.preventDefault();

      const syncOrbitPoseFromCamera = () => {
        const cx = focus.x;
        const cy = focus.y;
        const cz = depthZ;
        const vecX = camera.position.x - cx;
        const vecZ = camera.position.z - cz;
        const radiusXZ = Math.hypot(vecX, vecZ);
        const fallbackRadius = Math.max(0.001, dollyZ - depthZ, orbitRadiusRef.r);
        const radius = Math.max(0.001, radiusXZ > 0.0001 ? radiusXZ : fallbackRadius);
        orbitRadiusRef.r = radius;
        orbitYaw = Math.atan2(vecZ, vecX);

        const pitchDenom = Math.max(0.001, radius * 0.6);
        const pitchRatio = THREE.MathUtils.clamp((camera.position.y - cy) / pitchDenom, -1, 1);
        orbitPitch = THREE.MathUtils.clamp(Math.asin(pitchRatio), -Math.PI / 5, Math.PI / 5);
      };

      const activeFocusDepth = getPlaneFocusDepthZ(activeIndex);
      const dist = Math.max(0.001, camera.position.z - activeFocusDepth); const vFov = THREE.MathUtils.degToRad(baseFov);
      const worldPerPixelY = (2 * Math.tan(vFov / 2) * dist) / renderer.domElement.clientHeight; const worldPerPixelX = worldPerPixelY * camera.aspect;
      const activeZoomBounds = getActiveZoomBounds();
      const zoomSpan = Math.max(0.6, activeZoomBounds.maxZ - activeZoomBounds.minZ);
      const zoomFactor = THREE.MathUtils.clamp((dollyZ - activeZoomBounds.minZ) / zoomSpan, 0, 1);
      const orbitThreshold = 0.7;
      const dragScale = isTouchDevice ? 1.2 : 0.5;
      if (zoomFactor >= orbitThreshold) {
        if (!orbitEnabled) syncOrbitPoseFromCamera();
        orbitEnabled = true;
        dragMode = 'orbit';
        let orbitDx = dx;
        let orbitDy = dy;
        const dragStep = Math.hypot(dx, dy);
        if (orbitDragLagRemaining > 0 && dragStep > 0) {
          if (dragStep <= orbitDragLagRemaining) {
            orbitDragLagRemaining -= dragStep;
            inertiaRef.vx = 0; inertiaRef.vy = 0; inertiaRef.yaw = 0; inertiaRef.pitch = 0;
            return;
          }
          const usableRatio = (dragStep - orbitDragLagRemaining) / dragStep;
          orbitDx *= usableRatio;
          orbitDy *= usableRatio;
          orbitDragLagRemaining = 0;
        }

        const orbitSpeed = ((0.0020 / 20) * ORBIT_DRAG_SENSITIVITY_MULTIPLIER) * (1 + (zoomFactor - orbitThreshold) * 1.2) * dragScale;
        const yawDelta = orbitDx * orbitSpeed;
        const pitchDelta = orbitDy * orbitSpeed * ORBIT_VERTICAL_DRAG_SENSITIVITY_MULTIPLIER;
        orbitYaw += yawDelta;
        orbitPitch = THREE.MathUtils.clamp(orbitPitch + pitchDelta, -Math.PI / 5, Math.PI / 5);
        inertiaRef.vx = 0; inertiaRef.vy = 0; inertiaRef.yaw = yawDelta * 60; inertiaRef.pitch = pitchDelta * 60;
      } else {
        dragMode = 'camera';
        orbitDragLagRemaining = 0;
        const responsiveness = dragScale * (0.75 + (1 - zoomFactor) * 0.25);
        const moveX = -dx * worldPerPixelX * responsiveness;
        const moveY = dy * worldPerPixelY * responsiveness;
        targetFocus.x += moveX;
        targetFocus.y += moveY;
        clampTargetToBounds();
        inertiaRef.vx = moveX * 60;
        inertiaRef.vy = moveY * 60;
        inertiaRef.yaw = 0; inertiaRef.pitch = 0;
      }
    }
    const onPointerLeave = (ev) => {
      onPointerUp(ev);
      hoverPlaneIndex = -1;
      setHoverCursorState(false);
    };
    renderer.domElement.addEventListener('pointerdown', onPointerDown); renderer.domElement.addEventListener('pointerup', onPointerUp); renderer.domElement.addEventListener('pointerleave', onPointerLeave); renderer.domElement.addEventListener('pointermove', onPointerMove);

    function onWheel(ev) {
      ev.preventDefault();
      const unit = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? 100 : 1;
      const wheelDelta = ev.deltaY * unit;

      if (isTextBoxIndex(activeIndex)) {
        wheelZoomLockUntil = performance.now() + 520;
        const activeFocusDepth = getPlaneFocusDepthZ(activeIndex);
        const dist = Math.max(0.001, camera.position.z - activeFocusDepth);
        const vFov = THREE.MathUtils.degToRad(baseFov);
        const worldPerPixelY = (2 * Math.tan(vFov / 2) * dist) / Math.max(1, renderer.domElement.clientHeight);
        if (scrollFocusedTextBoxByWheel(wheelDelta, worldPerPixelY)) return;
        return;
      }

      if (performance.now() < wheelZoomLockUntil) {
        return;
      }

      const zoomBounds = getActiveZoomBounds();
      const zoomSpan = Math.max(0.6, zoomBounds.maxZ - zoomBounds.minZ);
      const zoomOutRatio = THREE.MathUtils.clamp((dollyZ - zoomBounds.minZ) / zoomSpan, 0, 1);
      let speed = 0.0039 + (0.0026 * zoomOutRatio);
      if (ev.shiftKey) speed *= 0.33;
      dollyZ += wheelDelta * speed;
      dollyZ = THREE.MathUtils.clamp(dollyZ, zoomBounds.minZ, zoomBounds.maxZ);
    }
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    function onResize() { if (!mount) return; const w = mount.clientWidth, h = mount.clientHeight; camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h); layoutColumns(); fitAll(); }
    window.addEventListener('resize', onResize);

    function updateIframes() {
      const hostRect = overlay.getBoundingClientRect(); const rect = renderer.domElement.getBoundingClientRect(); const w = rect.width, h = rect.height;
      for (let i = 0; i < iframes.length; i++) { const ifr = iframes[i]; if (!ifr) continue; const p = planes[i]; if (!p) continue; const dims = getMeshCardDimensions(p, planeH); const halfW = dims.width / 2; const halfH = dims.height / 2;
        const corners = [new THREE.Vector3(-halfW, halfH, 0), new THREE.Vector3(halfW, halfH, 0), new THREE.Vector3(halfW, -halfH, 0), new THREE.Vector3(-halfW, -halfH, 0)];
        for (const v of corners) v.applyMatrix4(p.matrixWorld);
        const toScreen = (v) => { const ndc = v.clone().project(camera); return { x: (ndc.x + 1) / 2 * w + rect.left, y: (1 - (ndc.y + 1) / 2) * h + rect.top }; };
        const pts = corners.map(toScreen); const minX = Math.min(...pts.map(p => p.x)), maxX = Math.max(...pts.map(p => p.x)), minY = Math.min(...pts.map(p => p.y)), maxY = Math.max(...pts.map(p => p.y));
        const left = minX - hostRect.left, top = minY - hostRect.top; const widthPx = Math.max(0, maxX - minX), heightPx = Math.max(0, maxY - minY);
        ifr.style.left = `${left}px`; ifr.style.top = `${top}px`; ifr.style.width = `${widthPx}px`; ifr.style.height = `${heightPx}px`; ifr.style.display = widthPx > 12 && heightPx > 12 ? 'block' : 'none';
      }

    }

    window.__dse_gallery_iframes__ = iframes;
    window.__dse_gallery_localVideos__ = localVideos;

    const publishFogZoomProgress = (value, nowMs = performance.now()) => {
      const target = THREE.MathUtils.clamp(value, 0, 1);
      const state = fogFocusZoomStateRef.current;
      if (!state) return;
      const previous = Number.isFinite(state.value) ? state.value : target;
      const enteringFog = target < previous;
      const smoothing = enteringFog ? 0.045 : 0.075;
      const smoothed = previous + ((target - previous) * smoothing);
      const changedEnough = Math.abs(smoothed - previous) >= 0.0004;
      const staleEnough = (nowMs - state.lastEmit) >= 16;
      if (!changedEnough && !staleEnough) return;
      state.value = smoothed;
      state.lastEmit = nowMs;
      setFogFocusZoomProgress(smoothed);
    };

    const publishFogStormProgress = (value, nowMs = performance.now()) => {
      const target = THREE.MathUtils.clamp(value, 0, 1);
      const state = fogStormZoomStateRef.current;
      if (!state) return;
      const previous = Number.isFinite(state.value) ? state.value : target;
      const smoothing = target > previous ? 0.18 : 0.11;
      const smoothed = previous + ((target - previous) * smoothing);
      const changedEnough = Math.abs(smoothed - previous) >= 0.0006;
      const staleEnough = (nowMs - state.lastEmit) >= 18;
      if (!changedEnough && !staleEnough) return;
      state.value = smoothed;
      state.lastEmit = nowMs;
      setFogStormZoomProgress(smoothed);
    };

    let running = true; function animate() {
      if (!running) return; requestAnimationFrame(animate);
      const nowMs = performance.now();
      for (const paint of animatedBoxPainters) {
        try {
          paint(nowMs);
        } catch {}
      }
      if (transitionRef.current.active) {
        const now = nowMs;
        const t = (now - transitionRef.current.start) / transitionRef.current.duration;
        const clamped = Math.min(1, Math.max(0, t));
        const mode = transitionRef.current.mode || 'direct';
        if (mode === 'zoom-out-pan-zoom') {
          const zoomOutRatio = THREE.MathUtils.clamp(Number(transitionRef.current.zoomOutRatio) || 0.24, 0.08, 0.45);
          const panRatio = THREE.MathUtils.clamp(Number(transitionRef.current.panRatio) || 0.52, 0.2, 0.75);
          const zoomInStart = Math.min(0.95, zoomOutRatio + panRatio);
          const transitZ = Number.isFinite(transitionRef.current.viaZ)
            ? transitionRef.current.viaZ
            : Math.max(transitionRef.current.fromZ, transitionRef.current.toZ);

          if (clamped < zoomOutRatio) {
            const zoomOutT = clamped / zoomOutRatio;
            const zoomOutEase = zoomOutT < 0.5 ? 4 * zoomOutT * zoomOutT * zoomOutT : 1 - Math.pow(-2 * zoomOutT + 2, 3) / 2;
            baseCam.copy(transitionRef.current.from);
            lookBase.copy(transitionRef.current.fromLook);
            dollyZ = transitionRef.current.fromZ + (transitZ - transitionRef.current.fromZ) * zoomOutEase;
          } else if (clamped < zoomInStart) {
            const panT = (clamped - zoomOutRatio) / Math.max(0.0001, panRatio);
            const panEase = panT < 0.5 ? 4 * panT * panT * panT : 1 - Math.pow(-2 * panT + 2, 3) / 2;
            baseCam.lerpVectors(transitionRef.current.from, transitionRef.current.to, panEase);
            lookBase.lerpVectors(transitionRef.current.fromLook, transitionRef.current.toLook, panEase);
            dollyZ = transitZ;
          } else {
            const zoomInT = (clamped - zoomInStart) / Math.max(0.0001, 1 - zoomInStart);
            const zoomInEase = zoomInT < 0.5 ? 4 * zoomInT * zoomInT * zoomInT : 1 - Math.pow(-2 * zoomInT + 2, 3) / 2;
            baseCam.copy(transitionRef.current.to);
            lookBase.copy(transitionRef.current.toLook);
            dollyZ = transitZ + (transitionRef.current.toZ - transitZ) * zoomInEase;
          }
        } else if (mode === 'pan-then-zoom') {
          const panRatio = THREE.MathUtils.clamp(Number(transitionRef.current.panRatio) || 0.58, 0.2, 0.9);
          if (clamped < panRatio) {
            const panT = clamped / panRatio;
            const panEase = panT < 0.5 ? 4 * panT * panT * panT : 1 - Math.pow(-2 * panT + 2, 3) / 2;
            baseCam.lerpVectors(transitionRef.current.from, transitionRef.current.to, panEase);
            lookBase.lerpVectors(transitionRef.current.fromLook, transitionRef.current.toLook, panEase);
            dollyZ = transitionRef.current.fromZ;
          } else {
            const zoomT = (clamped - panRatio) / (1 - panRatio);
            const zoomEase = zoomT < 0.5 ? 4 * zoomT * zoomT * zoomT : 1 - Math.pow(-2 * zoomT + 2, 3) / 2;
            baseCam.copy(transitionRef.current.to);
            lookBase.copy(transitionRef.current.toLook);
            dollyZ = transitionRef.current.fromZ + (transitionRef.current.toZ - transitionRef.current.fromZ) * zoomEase;
          }
        } else {
          const ease = clamped < 0.5 ? 4 * clamped * clamped * clamped : 1 - Math.pow(-2 * clamped + 2, 3) / 2;
          baseCam.lerpVectors(transitionRef.current.from, transitionRef.current.to, ease);
          lookBase.lerpVectors(transitionRef.current.fromLook, transitionRef.current.toLook, ease);
          dollyZ = transitionRef.current.fromZ + (transitionRef.current.toZ - transitionRef.current.fromZ) * ease;
        }
        if (clamped >= 1) transitionRef.current.active = false;
      }
      if (inertiaRef.active) {
        const t = (performance.now() - inertiaRef.start);
        const k = THREE.MathUtils.clamp(t / inertiaRef.duration, 0, 1);
        const decay = 1 - k;
        if (decay > 0) {
          targetFocus.x += (inertiaRef.vx * decay) / 3600;
          targetFocus.y += (inertiaRef.vy * decay) / 3600;
          orbitYaw += (inertiaRef.yaw * decay) / 3600;
          orbitPitch = THREE.MathUtils.clamp(orbitPitch + (inertiaRef.pitch * decay) / 3600, -Math.PI / 5, Math.PI / 5);
        } else {
          inertiaRef.active = false;
        }
      }
      const fogZoomBounds = getActiveZoomBounds();
      const fogFocusZ = getFocusedFrameCameraZ(activeIndex);
      const fogZoomDenominator = Math.max(0.5, fogZoomBounds.maxZ - fogFocusZ);
      const fogZoomProgressValue = THREE.MathUtils.clamp((fogZoomBounds.maxZ - dollyZ) / fogZoomDenominator, 0, 1);
      publishFogZoomProgress(fogZoomProgressValue, nowMs);
      const fogStormRange = 80;
      const zoomOutFromFocus = 1 - fogZoomProgressValue;
      const overshootStorm = THREE.MathUtils.clamp((dollyZ - farBaseZ) / fogStormRange, 0, 1);
      const earlyStormFromZoomOut = Math.pow(zoomOutFromFocus, 0.34);
      const earlyStormFromOvershoot = Math.pow(overshootStorm, 0.55);
      const fogStormProgressValue = THREE.MathUtils.clamp(
        (earlyStormFromZoomOut * 0.9) + (earlyStormFromOvershoot * 0.78) + (zoomOutFromFocus * 0.12),
        0,
        1
      );
      publishFogStormProgress(fogStormProgressValue, nowMs);
      const zoomFactor = THREE.MathUtils.clamp((dollyZ - depthZ) / 60, 0, 1);
      const followLerp = orbitEnabled ? 0.06 : (0.12 - zoomFactor * 0.05);
      if (!transitionRef.current.active) {
        focus.lerp(targetFocus, followLerp);
        clampTargetToBounds();
        baseCam.x = focus.x; baseCam.y = focus.y; lookBase.x = focus.x; lookBase.y = focus.y;
      } else {
        focus.lerp(targetFocus, Math.min(0.10, followLerp));
      }

      if (orbitEnabled) {
        const radiusTarget = Math.max(0.001, dollyZ - depthZ);
        orbitRadiusRef.r += (radiusTarget - orbitRadiusRef.r) * 0.08;
        const cx = focus.x; const cy = focus.y; const cz = depthZ;
        const orbitX = cx + Math.cos(orbitYaw) * orbitRadiusRef.r;
        const orbitZ = cz + Math.sin(orbitYaw) * orbitRadiusRef.r;
        const orbitY = cy + orbitRadiusRef.r * Math.sin(orbitPitch) * 0.6;
        const targetPos = new THREE.Vector3(orbitX, orbitY, orbitZ);
        camera.position.lerp(targetPos, 0.10);
        camera.lookAt(new THREE.Vector3(cx, cy, cz));
      } else {
        camera.position.lerp(new THREE.Vector3(baseCam.x, baseCam.y, dollyZ), 0.09);
        camera.lookAt(lookBase);
      }
      keyLight.position.lerp(new THREE.Vector3(camera.position.x + 3.8, camera.position.y + 2.7, camera.position.z + 6.6), 0.1);
      fillLight.position.lerp(new THREE.Vector3(camera.position.x - 5.6, camera.position.y + 1.4, camera.position.z + 3.9), 0.1);
      rimLight.position.lerp(new THREE.Vector3(focus.x + 2.2, focus.y - 2.4, depthZ - 3.5), 0.08);

      const lightFocusTarget = new THREE.Vector3(focus.x, focus.y, depthZ);
      if (configurableLight) {
        configurableLight.color.set(galleryLightSettings.color);
        configurableLight.intensity = galleryLightSettings.intensity;

        if (galleryLightSettings.type === 'directional' && configurableLight instanceof THREE.DirectionalLight) {
          const azimuthRad = THREE.MathUtils.degToRad(galleryLightSettings.directionalAzimuth);
          const elevationRad = THREE.MathUtils.degToRad(galleryLightSettings.directionalElevation);
          const directionalRadius = 11.5;
          const directionalPos = new THREE.Vector3(
            focus.x + (Math.cos(azimuthRad) * directionalRadius),
            focus.y + (Math.sin(elevationRad) * directionalRadius),
            depthZ + 6.2 + (Math.sin(azimuthRad) * directionalRadius * 0.7),
          );
          configurableLight.position.lerp(directionalPos, 0.08);
          lightTarget.position.lerp(lightFocusTarget, 0.14);
        } else if (galleryLightSettings.type === 'point' && configurableLight instanceof THREE.PointLight) {
          configurableLight.distance = galleryLightSettings.pointDistance;
          configurableLight.decay = galleryLightSettings.pointDecay;
          const orbitPhase = nowMs * 0.00022;
          const pointPos = new THREE.Vector3(
            focus.x + (Math.cos(orbitPhase) * 1.4),
            focus.y + 2.3 + (galleryLightSettings.pointDistance * 0.08),
            depthZ + 4.2 + (galleryLightSettings.pointDistance * 0.14),
          );
          configurableLight.position.lerp(pointPos, 0.08);
        } else if (galleryLightSettings.type === 'spot' && configurableLight instanceof THREE.SpotLight) {
          configurableLight.angle = THREE.MathUtils.degToRad(galleryLightSettings.spotAngle);
          configurableLight.penumbra = galleryLightSettings.spotPenumbra;
          const spotPos = new THREE.Vector3(camera.position.x, camera.position.y + 4.6, camera.position.z + 3.8);
          configurableLight.position.lerp(spotPos, 0.1);
          lightTarget.position.lerp(lightFocusTarget, 0.14);
        } else if (galleryLightSettings.type === 'hemisphere' && configurableLight instanceof THREE.HemisphereLight) {
          const groundColor = new THREE.Color(galleryLightSettings.color)
            .lerp(new THREE.Color('#050505'), galleryLightSettings.hemisphereGroundMix);
          configurableLight.groundColor.copy(groundColor);
          configurableLight.position.set(0, galleryLightSettings.hemisphereHeight * 10, 0);
        }
      }

      const camQuat = camera.quaternion;
      const activePlane = planes[activeIndex] || null;
      const blurAnchor = activePlane?.position || focus;
      const blurDistanceScale = Math.max(6, spacingX * Math.max(1, cols) * 0.45);
      planes.forEach((p, planeIndex) => {
        p.quaternion.copy(camQuat);
        const planeEntry = items[planeIndex] || null;
        if (!planeEntry || planeEntry.kind !== 'image') {
          applyDistanceBlurToMediaMesh(p, 0, nowMs);
          return;
        }
        if (distanceBlurStrength <= 0.0001) {
          applyDistanceBlurToMediaMesh(p, 0, nowMs);
          return;
        }
        const distanceFromFocus = Math.hypot(
          p.position.x - blurAnchor.x,
          p.position.y - blurAnchor.y,
          (p.position.z - blurAnchor.z) * 0.5,
        );
        const normalizedDistance = clampRange(distanceFromFocus / blurDistanceScale, 0, 0, 1);
        const blurAmount = planeIndex === activeIndex
          ? 0
          : distanceBlurStrength * Math.pow(normalizedDistance, 1.2);
        applyDistanceBlurToMediaMesh(p, blurAmount, nowMs);
      });
      focusedInfoFaceCameraMeshes.forEach((mesh) => {
        try {
          mesh.quaternion.copy(camQuat);
        } catch {}
      });
      updateFocusedInfoMeshes(activeIndex, nowMs);
      clampTargetToBounds();
      updateIframes(); renderer.render(scene, camera);
    } animate();

    return () => {
      running = false;
      effectDisposed = true;
      focusedUpgradeSequence += 1;
      const loadedImageTextures = new Set();
      imageRuntimeByIndex.forEach((runtime) => {
        clearImageTierStatusTimer(runtime);
        if (runtime?.lowTexture) loadedImageTextures.add(runtime.lowTexture);
        if (runtime?.webTexture) loadedImageTextures.add(runtime.webTexture);
        if (runtime?.hiTexture) loadedImageTextures.add(runtime.hiTexture);
      });
      loadedImageTextures.forEach((texture) => {
        if (!texture || texture instanceof THREE.VideoTexture) return;
        try { texture.dispose?.(); } catch {}
      });
      if (imageTierStatusRafRef.current) {
        window.cancelAnimationFrame(imageTierStatusRafRef.current);
        imageTierStatusRafRef.current = 0;
      }
      renderer.domElement.removeEventListener('pointerdown', onPointerDown); renderer.domElement.removeEventListener('pointerup', onPointerUp); renderer.domElement.removeEventListener('pointerleave', onPointerLeave); renderer.domElement.removeEventListener('pointermove', onPointerMove); renderer.domElement.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
      renderer.domElement.style.cursor = 'default';
      try { mount.removeChild(renderer.domElement); } catch {}
      mount.style.background = previousMountBackground;
      try { if (cursorFx && cursorFx.parentNode) cursorFx.parentNode.removeChild(cursorFx); } catch {}
      try { for (const ifr of iframes) if (ifr && ifr.parentNode) ifr.parentNode.removeChild(ifr); } catch {}
      try { clearFocusedInfoMeshes(); } catch {}
      localVideos.forEach(v => { try { v?.pause(); v?.removeAttribute('src'); } catch {} });
      scrollFocusedTextBoxRef.current = () => false;
      thrusterZoomOutRef.current = () => '';
      renderer.dispose();
    };
  }, [items, galleryId, isMerchandiseMode, lineBreaksBeforeItem, embedded, mode]);

  useEffect(() => {
    const entry = items[selected];
    if (!entry || entry.kind !== 'video') {
      setVideoInteract(false);
    }
  }, [selected, items]);

  useEffect(() => {
    const entry = items[selected];
    const iframes = window.__dse_gallery_iframes__ || [];
    if (!entry || entry.kind !== 'video') {
      iframes.forEach(f => { if (f) f.style.pointerEvents = 'none'; });
      return;
    }
    const idx = selected;
    iframes.forEach((f, i) => {
      if (!f) return;
      if (i === idx && entry.youtubeId) {
        f.style.pointerEvents = videoInteract ? 'auto' : 'none';
      } else {
        f.style.pointerEvents = 'none';
      }
    });
  }, [videoInteract, selected, items]);

  function togglePlay() {
    const entry = items[selected]; if (!entry || entry.kind !== 'video') return;
    const localVideos = window.__dse_gallery_localVideos__ || [];
    const iframes = window.__dse_gallery_iframes__ || [];
    const idx = selected;
    if (entry.youtubeId) {
      const ifr = iframes[idx]; if (!ifr) return;
      ifr.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
    } else {
      const vid = localVideos[idx]; if (!vid) return;
      if (vid.paused) { vid.play().catch(() => {}); } else { vid.pause(); }
      setVideoUiTick(t => t + 1);
    }
  }
  function togglePause() {
    const entry = items[selected]; if (!entry || entry.kind !== 'video') return;
    const localVideos = window.__dse_gallery_localVideos__ || [];
    const iframes = window.__dse_gallery_iframes__ || [];
    const idx = selected;
    if (entry.youtubeId) {
      const ifr = iframes[idx]; if (!ifr) return;
      ifr.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
    } else {
      const vid = localVideos[idx]; if (!vid) return; vid.pause(); setVideoUiTick(t => t + 1);
    }
  }
  function toggleMute() {
    const entry = items[selected]; if (!entry || entry.kind !== 'video') return;
    const localVideos = window.__dse_gallery_localVideos__ || [];
    const iframes = window.__dse_gallery_iframes__ || [];
    const idx = selected;
    if (entry.youtubeId) {
      const ifr = iframes[idx]; if (!ifr) return;
      const key = '__dse_youtube_muted_' + entry.youtubeId;
      const muted = window[key] !== false;
      if (muted) {
        ifr.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*');
        window[key] = false;
      } else {
        ifr.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'mute', args: [] }), '*');
        window[key] = true;
      }
    } else {
      const vid = localVideos[idx]; if (!vid) return; vid.muted = !vid.muted; setVideoUiTick(t => t + 1);
    }
  }
  function currentLocalVideoState() {
    const entry = items[selected]; if (!entry || entry.kind !== 'video' || entry.youtubeId) return null;
    const localVideos = window.__dse_gallery_localVideos__ || [];
    const vid = localVideos[selected]; if (!vid) return null;
    return { paused: vid.paused, muted: vid.muted };
  }

  useEffect(() => {
    if (embedded) return undefined;
    function onKey(ev) {
      if (items.length === 0) return;
      if ((ev.key === 'ArrowRight' || ev.key === 'ArrowDown') && scrollFocusedTextBoxRef.current?.(1)) {
        ev.preventDefault();
        return;
      }
      if ((ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') && scrollFocusedTextBoxRef.current?.(-1)) {
        ev.preventDefault();
        return;
      }
      const layout = window.__dse_gallery_layout__ || {};
      const cols = Math.max(1, Math.min(12, layout.cols || 4));
      let idx = selected; let used = true;
      if (ev.key === 'ArrowLeft') idx -= 1; else if (ev.key === 'ArrowRight') idx += 1; else if (ev.key === 'ArrowUp') idx -= cols; else if (ev.key === 'ArrowDown') idx += cols; else used = false;
      if (!used) return; idx = Math.max(0, Math.min(items.length - 1, idx)); setSelected(idx); centerOnRef.current?.(idx, false); ev.preventDefault();
    }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [items.length, selected, embedded]);

  const canLeft = selected > 0;
  const canRight = items.length > 1;
  const [hoverGallery, setHoverGallery] = useState(false);
  const runtimeLayout = (typeof window !== 'undefined' && window.__dse_gallery_layout__) || {};
  const galleryFog = normalizeFogConfig(runtimeLayout);
  const fogProgress = clampRange(fogIntroProgress, 0, 0, 1);
  const fogRootOpacity = clampOpacity(galleryFog.fogOpacity * fogProgress, 0);
  const fogPrimaryRgb = hexToRgbTriplet(galleryFog.fogColorPrimary, '155,196,222');
  const fogSecondaryRgb = hexToRgbTriplet(galleryFog.fogColorSecondary, '138,174,199');
  const fogDensityScale = clampRange(0.48 + (galleryFog.fogDensity * 1.08), 1.05, 0.25, 2.8);
  const fogVelocityAmount = normalizeFogVelocity(galleryFog.fogVelocity, 35);
  const fogVelocityRatio = clampRange(Math.pow(fogVelocityAmount / 1000, 0.35), 0.35, 0, 1);
  const focusedEntry = items[selected] || null;
  const focusedFogClear = isFogClearableGalleryItem(focusedEntry);
  const fogZoomInProgress = clampRange(fogFocusZoomProgress, 0, 0, 1);
  const fogZoomOutProgress = clampRange(1 - fogZoomInProgress, 0, 0, 1);
  const fogStormZoomRatio = clampRange(fogStormZoomProgress, 0, 0, 1);
  const fogEarlyZoomOut = Math.pow(fogZoomOutProgress, 0.52);
  const fogEarlyStorm = Math.pow(fogStormZoomRatio, 0.58);
  const fogWindPulseRatio = clampRange(
    0.08 + (fogVelocityRatio * 0.46) + (fogEarlyZoomOut * 0.86) + (fogEarlyStorm * 1.04),
    0,
    0,
    1
  );
  const fogStill = fogWindPulseRatio <= 0.001;
  const fogStormFactor = Math.pow(fogWindPulseRatio, 0.62);
  const fogGustFactor = Math.pow(fogStormFactor, 0.74);
  const fogDuration1 = 12 - (10.4 * fogStormFactor);
  const fogDuration2 = 16 - (13.2 * fogStormFactor);
  const fogDuration3 = 22 - (17.6 * fogStormFactor);
  const fogDuration4 = 28 - (22 * fogStormFactor);
  const fogDuration5 = 34 - (26 * fogStormFactor);
  const fogDuration6 = 9.5 - (7.2 * fogStormFactor);
  const fogDuration7 = 12.5 - (9 * fogStormFactor);
  const fogDuration8 = 16.5 - (11.5 * fogStormFactor);
  const fogGustOpacity = clampRange(0.22 + (0.78 * fogGustFactor), 0.25, 0, 1);
  const fogGustBlurPx = Math.round((62 + (88 * fogGustFactor)) * fogDensityScale);
  const fogRingProgress = Math.pow(fogZoomInProgress, 0.86);
  const fogPassiveRingExpand = Math.pow(fogRingProgress, 1.28) * 0.46;
  const fogRingExpandBase = focusedFogClear ? fogRingProgress : fogPassiveRingExpand;
  const fogStormRingSuppress = clampRange(1 - (0.42 * Math.pow(fogStormZoomRatio, 1.15)), 1, 0.5, 1);
  const fogRingMinExpand = focusedFogClear ? 0.05 : 0.042;
  const fogRingExpand = Math.max(fogRingMinExpand, fogRingExpandBase * fogStormRingSuppress);
  const fogFocusedOpacityScale = focusedFogClear ? clampRange(1 - Math.pow(fogZoomInProgress, 1.35), 1, 0, 1) : 1;
  const fogRenderOpacity = fogRootOpacity * fogFocusedOpacityScale;
  const fogHoleInnerPct = focusedFogClear ? (30 + (52 * fogRingExpand)) : (14 + (42 * fogRingExpand));
  const fogHoleFeatherPct = Math.min(
    100,
    fogHoleInnerPct + (focusedFogClear ? (22 + (20 * fogRingExpand)) : (16 + (16 * fogRingExpand)))
  );
  const fogMaskMidAlpha = focusedFogClear ? 0.78 : 0.9;
  const fogFocusMask = `radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) ${fogHoleInnerPct}%, rgba(0,0,0,${fogMaskMidAlpha}) ${fogHoleFeatherPct}%, rgba(0,0,0,1) 100%)`;
  const activeGalleryItem = items[selected] || null;
  const activeImageTierStatus = (() => {
    const _tick = imageTierUiTick;
    if (_tick < -1) return null;
    if (!activeGalleryItem || activeGalleryItem.kind !== 'image') return null;
    const key = galleryImageStatusKey(activeGalleryItem, selected);
    return imageTierStatusRef.current.get(key) || { tier: 'LOFI', progress: 0, visible: true };
  })();
  const activeImageTierRawLabel = String(activeImageTierStatus?.tier || 'LOFI').toUpperCase();
  const activeImageTierLabel = activeImageTierRawLabel === 'HIFI' ? 'ORIGINAL' : activeImageTierRawLabel;
  const activeImageTierProgress = clampRange(activeImageTierStatus?.progress, 0, 0, 1);
  const activeImageTierVisible = !!activeImageTierStatus && activeImageTierStatus.visible !== false;
  const activeImageTierColor = activeImageTierRawLabel === 'HIFI'
    ? '#68f4b6'
    : (activeImageTierRawLabel === 'WEB' ? '#ffd970' : '#84cfff');
  const activeGalleryItemTitle = activeGalleryItem?.subject || activeGalleryItem?.title || activeGalleryItem?.filename || 'Gallery item';

  useEffect(() => {
    if (!activeGalleryItem || activeGalleryItem.kind !== 'image' || !activeImageTierStatus) {
      imageTierTerminalProgressRef.current = { key: '', tier: '', bucket: -1 };
      return;
    }

    const statusKey = galleryImageStatusKey(activeGalleryItem, selected);
    const tier = String(activeImageTierStatus.tier || 'LOFI').toUpperCase();
    const tierLabel = tier === 'HIFI' ? 'ORIGINAL' : tier;
    const percent = Math.round(clampRange(activeImageTierStatus.progress, 0, 0, 1) * 100);
    const bucketSize = tier === 'HIFI' ? 2 : 5;
    const bucket = Math.max(0, Math.min(100, Math.round(percent / bucketSize) * bucketSize));

    const previous = imageTierTerminalProgressRef.current || { key: '', tier: '', bucket: -1 };
    if (previous.key === statusKey && previous.tier === tier && previous.bucket === bucket) return;

    imageTierTerminalProgressRef.current = { key: statusKey, tier, bucket };

    const safeTitle = String(activeGalleryItemTitle || 'GALLERY ITEM').trim().toUpperCase().slice(0, 52);
    const createdAt = Date.now();
    setTerminalLines((prev) => {
      const next = [
        ...prev,
        {
          id: `imgtier-${createdAt.toString(36)}-${Math.round(Math.random() * 1e7).toString(36)}`,
          speaker: 'system',
          text: `IMG_STATUS ${safeTitle}: ${tierLabel} ${bucket}%`,
          createdAt,
          botName: 'DEBOTS',
        }
      ];
      return next.slice(-MAX_GALLERY_TERMINAL_LINES);
    });
    setTerminalTypewriterNow(createdAt);
  }, [activeGalleryItem, activeGalleryItemTitle, activeImageTierStatus, selected]);

  const canShowSelectedItemInfo = !isMerchandiseMode && !!activeGalleryItem && !isLineBreakItem(activeGalleryItem);
  const infoItem = infoItemIndex != null ? (items[infoItemIndex] || null) : null;
  const selectedItemTitle = infoItem?.subject || infoItem?.title || infoItem?.filename || 'Gallery item';
  const selectedItemText = infoItem?.description || infoItem?.text || '';
  const selectedItemDescription = selectedItemText || 'No description available.';
  const selectedCreatedYear = Number.isFinite(Number(infoItem?.createdYear)) ? Math.round(Number(infoItem.createdYear)) : null;
  const selectedCreatedBy = Array.isArray(infoItem?.artCreatedBy) ? infoItem.artCreatedBy : [];
  const selectedCreatedByText = selectedCreatedBy
    .map((p) => [String(p?.name || '').trim(), String(p?.rolle || '').trim()].filter(Boolean).join(' — '))
    .filter(Boolean)
    .join(', ');
  const selectedForSale = !!infoItem?.forSale;
  const selectedSaleType = infoItem?.saleType || 'Other';
  const selectedSalePriceNok = infoItem?.salePriceNok;
  const selectedSaleSizes = Array.isArray(infoItem?.saleSizes) ? infoItem.saleSizes : [];
  const selectedSaleText = infoItem?.saleText || '';
  const selectedItemPreviewSrc = assetUrl(
    infoItem?.optimizedUrl || infoItem?.url || infoItem?.originalUrl || infoItem?.thumbUrl || infoItem?.storageUrl || ''
  );
  const selectedItemImageSrc = assetUrl(
    infoItem?.optimizedUrl || infoItem?.url || infoItem?.originalUrl || infoItem?.thumbUrl || infoItem?.storageUrl || ''
  );
  const totalCartItems = cartCount(cartItems);
  const variantGroups = useMemo(
    () => (Array.isArray(merchandiseMeta?.variantGroups) ? merchandiseMeta.variantGroups : []),
    [merchandiseMeta?.variantGroups]
  );
  const variantStockRows = useMemo(
    () => normalizeVariantStockRows(merchandiseMeta?.variantStockRows, merchandiseMeta?.stockOnHand),
    [merchandiseMeta?.variantStockRows, merchandiseMeta?.stockOnHand]
  );
  const missingVariantTitles = variantGroups
    .filter((group) => !String(selectedVariantValues[group.title] || '').trim())
    .map((group) => group.title);
  const selectedCombinationAvailableStock = useMemo(() => {
    if (!merchandiseMeta) return 0;
    if (!variantGroups.length) return Math.max(0, Number(merchandiseMeta?.totalAvailableStock || 0));
    const completeSelection = variantGroups
      .map((group) => ({ title: group.title, value: String(selectedVariantValues[group.title] || '').trim() }))
      .filter((entry) => entry.title && entry.value);
    if (completeSelection.length !== variantGroups.length) return 0;
    return variantStockRows
      .filter((row) => rowMatchesSelection(row, completeSelection))
      .reduce((sum, row) => sum + variantRowAvailableStock(row), 0);
  }, [merchandiseMeta, variantGroups, selectedVariantValues, variantStockRows]);
  const canAddConfiguredMerchToCart = isMerchandiseMode
    && !!merchandiseMeta
    && missingVariantTitles.length === 0
    && selectedCombinationAvailableStock > 0;

  const addConfiguredMerchToCart = () => {
    if (!canAddConfiguredMerchToCart) return;
    const selectedVariants = variantGroups
      .map((group) => ({ title: group.title, value: String(selectedVariantValues[group.title] || '').trim() }))
      .filter((entry) => entry.title && entry.value);
    const requestedSize = deriveRequestedSizeFromVariants(selectedVariants);
    const productId = String(merchandiseMeta?.productId || '').trim();
    const firstMerchImage = images.find((entry) => entry?.kind === 'image');
    const next = addItemToCart(cartItems, {
      productId,
      itemTitle: String(merchandiseMeta?.title || selectedItemTitle || 'Merchandise').trim(),
      itemImageSrc: firstMerchImage?.optimizedUrl || firstMerchImage?.url || firstMerchImage?.originalUrl || '',
      saleType: requestedSize ? 'T-shirt' : 'Other',
      salePriceNok: Number(merchandiseMeta?.salePriceNok || selectedSalePriceNok || 0) || 0,
      requestedSize,
      selectedVariants,
      variantGroups,
      quantity: 1,
    });
    setCartItems(next);
    saveMerchCart(next);
    setCartFeedback('Lagt i handlevogn');
  };

  const closePurchaseModal = () => {
    setShowPurchaseModal(false);
    setSendingOrder(false);
    setOrderMessage('');
    setOrderForm(initialOrderForm);
  };

  async function submitPurchaseOrder(e) {
    e.preventDefault();
    const customerFirstName = String(orderForm.customerFirstName || '').trim();
    const customerLastName = String(orderForm.customerLastName || '').trim();
    const customerEmail = String(orderForm.customerEmail || '').trim();
    const deliveryAddress = String(orderForm.deliveryAddress || '').trim();
    const deliveryPostcode = String(orderForm.deliveryPostcode || '').trim();
    const deliveryCity = String(orderForm.deliveryCity || '').trim();
    const deliveryCountry = String(orderForm.deliveryCountry || '').trim();
    const requestedSize = String(orderForm.requestedSize || '').trim().toUpperCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail);
    if (!customerFirstName || !customerLastName || !customerEmail) {
      setOrderMessage('Please enter first name, last name, and email.');
      return;
    }
    if (!emailOk) {
      setOrderMessage('Email must be a valid email address.');
      return;
    }
    if (!deliveryAddress) {
      setOrderMessage('Please enter a delivery address.');
      return;
    }
    if (!deliveryPostcode || !deliveryCity || !deliveryCountry) {
      setOrderMessage('Please enter postcode, city, and country.');
      return;
    }
    if (selectedSaleType === 'T-shirt' && !requestedSize) {
      setOrderMessage('Please select a T-shirt size.');
      return;
    }

    setSendingOrder(true);
    setOrderMessage('');
    try {
      await fetchJSON('/api/gallery/purchase-orders/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assetId: infoItem?._id || infoItem?.id || undefined,
          itemTitle: selectedItemTitle,
          itemImageSrc: selectedItemImageSrc,
          saleType: selectedSaleType,
          salePriceNok: selectedSalePriceNok,
          saleSizes: selectedSaleSizes,
          saleText: selectedSaleText,
          customerFirstName,
          customerLastName,
          customerEmail,
          customerPhone: String(orderForm.customerPhone || '').trim(),
          deliveryAddress,
          deliveryPostcode,
          deliveryCity,
          deliveryCountry,
          ...(selectedSaleType === 'T-shirt' && requestedSize ? { requestedSize } : {}),
          quantity: Number(orderForm.quantity || 1),
          message: String(orderForm.message || '').trim(),
        }),
      });
      setOrderMessage('Order sent. We will contact you soon.');
      setOrderForm(initialOrderForm);
    } catch {
      setOrderMessage('Could not submit order. Please try again.');
    } finally {
      setSendingOrder(false);
    }
  }

  const appendTerminalLine = (speaker, text, botName) => {
    const createdAt = Date.now();
    setTerminalLines((prev) => {
      const next = [
        ...prev,
        {
          id: `${createdAt.toString(36)}-${Math.round(Math.random() * 1e7).toString(36)}`,
          speaker,
          text,
          createdAt,
          botName,
        }
      ];
      return next.slice(-MAX_GALLERY_TERMINAL_LINES);
    });
    setTerminalTypewriterNow(createdAt);
  };

  const clearTerminalFaqAnswerTimer = () => {
    if (typeof window === 'undefined') return;
    if (!terminalFaqAnswerTimerRef.current) return;
    window.clearTimeout(terminalFaqAnswerTimerRef.current);
    terminalFaqAnswerTimerRef.current = 0;
  };

  const askTerminalFaq = (rawQuestion) => {
    const question = String(rawQuestion || '').trim();
    if (!question) return;

    clearTerminalFaqAnswerTimer();
    const activeBot = selectedTerminalBot || null;
    const botName = activeBot ? activeBot.name : 'DEBOT';

    appendTerminalLine('user', question);
    const answer = createGalleryTerminalFallbackAnswer(question, activeBot, terminalFaqOptions);
    const questionTypeDurationMs = Math.max(
      300,
      Math.ceil((question.length / TERMINAL_TYPEWRITER_CHARS_PER_SEC) * 1000) + 140
    );

    terminalFaqAnswerTimerRef.current = window.setTimeout(() => {
      terminalFaqAnswerTimerRef.current = 0;
      appendTerminalLine('bot', answer, botName);
    }, questionTypeDurationMs);

    setTerminalFaqSelection('');
  };

  const handleTerminalFaqSelect = (question) => {
    setTerminalFaqSelection(question);
    if (!question) {
      clearTerminalFaqAnswerTimer();
      return;
    }
    askTerminalFaq(question);
  };

  const handleTerminalThruster = () => {
    if (isMerchandiseMode) return;
    appendTerminalLine('user', 'THRUSTER');
    const mode = thrusterZoomOutRef.current?.(THRUSTER_TRANSITION_MS);
    if (!mode) {
      appendTerminalLine('bot', 'Thruster er ikke klar enda. Proev igjen om et oyeblikk.', selectedTerminalBot ? selectedTerminalBot.name : 'DEBOT');
      return;
    }

    if (mode === 'zoom-in') {
      appendTerminalLine('system', 'THRUSTER reverse engaged. Flyr tilbake inn i galleriet.', 'DEBOTS');
      return;
    }

    appendTerminalLine('system', 'THRUSTER engaged. Zoomer ut til maks paa 2 sekunder.', 'DEBOTS');
  };

  const clearFocusedInfoTerminalFlashTimeout = () => {
    if (typeof window === 'undefined') return;
    if (!focusedInfoTerminalFlashTimeoutRef.current) return;
    window.clearTimeout(focusedInfoTerminalFlashTimeoutRef.current);
    focusedInfoTerminalFlashTimeoutRef.current = 0;
  };

  const triggerFocusedInfoTerminalFlash = () => {
    if (typeof window === 'undefined') return;
    clearFocusedInfoTerminalFlashTimeout();
    setFocusedInfoTerminalFlashActive(true);
    focusedInfoTerminalFlashTimeoutRef.current = window.setTimeout(() => {
      focusedInfoTerminalFlashTimeoutRef.current = 0;
      setFocusedInfoTerminalFlashActive(false);
    }, FOCUSED_INFO_TERMINAL_FLASH_MS);
  };

  const clearFocusedInfoActivityTimeouts = () => {
    if (typeof window === 'undefined') return;
    if (!Array.isArray(focusedInfoActivityTimeoutsRef.current) || focusedInfoActivityTimeoutsRef.current.length === 0) return;
    focusedInfoActivityTimeoutsRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    focusedInfoActivityTimeoutsRef.current = [];
  };

  const scheduleFocusedInfoActivityLine = ({ sequenceId, delayMs, text, requireFocusedVisible = null }) => {
    if (typeof window === 'undefined') return;
    const normalizedDelayMs = Math.max(0, Math.round(Number(delayMs) || 0));
    const timeoutId = window.setTimeout(() => {
      focusedInfoActivityTimeoutsRef.current = focusedInfoActivityTimeoutsRef.current.filter((entry) => entry !== timeoutId);
      if (sequenceId !== focusedInfoActivitySequenceRef.current) return;
      if (requireFocusedVisible === true && !focusedInfoVisibleRef.current) return;
      if (requireFocusedVisible === false && focusedInfoVisibleRef.current) return;
      appendTerminalLine('system', text, 'DEBOTS');
    }, normalizedDelayMs);
    focusedInfoActivityTimeoutsRef.current.push(timeoutId);
  };

  const triggerFocusedInfoTerminalActivityLogSequence = (nextVisible, itemTitleRaw) => {
    const itemTitle = String(itemTitleRaw || 'Gallery item').trim().toUpperCase();
    focusedInfoActivitySequenceRef.current += 1;
    const sequenceId = focusedInfoActivitySequenceRef.current;
    clearFocusedInfoActivityTimeouts();

    if (nextVisible) {
      appendTerminalLine('user', `MER INFORMASJON OM ${itemTitle}`);
      appendTerminalLine('system', `CMD: MER_INFO ${itemTitle}`, 'DEBOTS');
      appendTerminalLine('system', 'GUI: TERMINAL_THEME_WHITE_BLUR_MAX', 'DEBOTS');
      appendTerminalLine('system', 'GUI: BACKGROUND_PLANE_DOT_INIT_WHITE', 'DEBOTS');

      scheduleFocusedInfoActivityLine({
        sequenceId,
        delayMs: FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS,
        text: 'GUI: SIGNAL_ROUTE_LOCKED_TO_INFO_PLANE',
        requireFocusedVisible: true,
      });
      scheduleFocusedInfoActivityLine({
        sequenceId,
        delayMs: FOCUSED_INFO_SHOW_SIGNAL_TRAVEL_MS + FOCUSED_INFO_SHOW_WIDTH_GROW_MS,
        text: 'GUI: BACKGROUND_PLANE_WIDTH_EXPAND_COMPLETE',
        requireFocusedVisible: true,
      });
      scheduleFocusedInfoActivityLine({
        sequenceId,
        delayMs: FOCUSED_INFO_SHOW_PRE_TYPE_MS,
        text: 'GUI: BACKGROUND_PLANE_HEIGHT_EXPAND_COMPLETE',
        requireFocusedVisible: true,
      });
      scheduleFocusedInfoActivityLine({
        sequenceId,
        delayMs: FOCUSED_INFO_SHOW_PRE_TYPE_MS + 1,
        text: 'GUI: TYPEWRITER_STREAM_START',
        requireFocusedVisible: true,
      });
      return;
    }

    appendTerminalLine('user', `SKJUL INFORMASJON OM ${itemTitle}`);
    appendTerminalLine('system', `CMD: SKJUL_INFO ${itemTitle}`, 'DEBOTS');
    appendTerminalLine('system', 'GUI: TYPEWRITER_FREEZE_CURRENT_OUTPUT', 'DEBOTS');
    appendTerminalLine('system', 'GUI: INFO_HIDE_SEQUENCE_START', 'DEBOTS');

    const hideAlignMs = Math.round(FOCUSED_INFO_HIDE_TRANSITION_MS * 0.25);
    const hideGlideMs = Math.round(FOCUSED_INFO_HIDE_TRANSITION_MS * 0.75);

    scheduleFocusedInfoActivityLine({
      sequenceId,
      delayMs: hideAlignMs,
      text: 'GUI: BACKGROUND_ALIGN_TO_TEXT_FRONT_COMPLETE',
      requireFocusedVisible: false,
    });
    scheduleFocusedInfoActivityLine({
      sequenceId,
      delayMs: hideGlideMs,
      text: 'GUI: BACKGROUND_GLIDE_TO_NEAR_TEXT_COMPLETE',
      requireFocusedVisible: false,
    });
    scheduleFocusedInfoActivityLine({
      sequenceId,
      delayMs: FOCUSED_INFO_HIDE_TRANSITION_MS,
      text: 'GUI: INFO_LAYER_COLLAPSED_AND_CLEARED',
      requireFocusedVisible: false,
    });
  };

  const handleOpenSelectedItemInfo = () => {
    if (isMerchandiseMode) return;
    const activeItem = items[selected] || null;
    if (!activeItem || isLineBreakItem(activeItem)) return;
    setInfoItemIndex(null);
    setFocusedInfoVisible((prev) => {
      if (prev) setFocusedInfoHideSignal((signal) => signal + 1);
      const nextVisible = !prev;
      if (nextVisible) {
        triggerFocusedInfoTerminalFlash();
      } else {
        clearFocusedInfoTerminalFlashTimeout();
        setFocusedInfoTerminalFlashActive(false);
      }
      triggerFocusedInfoTerminalActivityLogSequence(nextVisible, activeGalleryItemTitle);
      return nextVisible;
    });
  };

  function nudge(direction, options = {}) {
    const { allowTextScroll = true } = options;
    if (items.length === 0) return;
    const dir = direction === 'right' ? 1 : -1;
    if (allowTextScroll && scrollFocusedTextBoxRef.current?.(dir)) return;

    let idx = selected;
    if (direction === 'right') {
      idx = selected >= items.length - 1 ? 0 : selected + 1;
    } else {
      if (selected === 0) return;
      idx = selected - 1;
    }

    setSelected(idx);
    centerOnRef.current?.(idx, false);
  }

  const terminalInfoIntroFlashActive = focusedInfoTerminalFlashActive && focusedInfoVisible;
  const terminalPanelTextColor = terminalInfoIntroFlashActive ? '#111' : '#fff';
  const galleryTerminalButtonWidth = `min(${GALLERY_TERMINAL_PANEL_WIDTH}px, calc(100vw - 48px))`;
  const galleryTerminalButtonTop = `calc(100vh - ${GALLERY_TERMINAL_BUTTON_BOTTOM + GALLERY_TERMINAL_BUTTON_HEIGHT}px)`;
  const galleryTerminalPanelTop = `calc(100vh - ${GALLERY_TERMINAL_PANEL_HEIGHT + GALLERY_TERMINAL_BUTTON_BOTTOM + GALLERY_TERMINAL_BUTTON_HEIGHT}px)`;

  const requireRotation = isMobileViewport && !isLandscape;
  const navArrowBaseStyle = {
    position: 'absolute',
    width: 68,
    height: 68,
    borderRadius: '999px',
    background: 'radial-gradient(circle at 28% 24%, rgba(255,255,255,0.20), rgba(0,0,0,0.76) 62%)',
    color: '#f3f3f3',
    border: '1px solid rgba(255,255,255,0.46)',
    boxShadow: '0 14px 30px rgba(0,0,0,0.58), inset 0 1px 10px rgba(255,255,255,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    zIndex: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    transition: 'opacity 170ms ease, transform 170ms ease',
    opacity: hoverGallery ? 1 : 0,
    pointerEvents: hoverGallery ? 'auto' : 'none',
    fontSize: 42,
    fontWeight: 700,
    lineHeight: 1,
    textShadow: '0 0 14px rgba(255,255,255,0.35)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  };

  const infoItemOpen = infoItemIndex != null && infoItem;
  const infoPanelWidth = 420;
  const minGalleryWidth = 500;
  const availableWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const hasSpaceForSideBySide = availableWidth > (minGalleryWidth + infoPanelWidth + 20);
  const galleryScale = !hasSpaceForSideBySide && infoItemOpen ? Math.max(0.5, (availableWidth - infoPanelWidth - 20) / availableWidth) : 1;

  if (embedded) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', background: 'transparent', overflow: 'hidden' }}>
        {error ? (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.62)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12, padding: 24 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Could not load gallery</div>
              <pre style={{ whiteSpace: 'pre-wrap', maxWidth: 720 }}>{error}</pre>
            </div>
          </div>
        ) : null}
        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
        <div ref={overlayRootRef} id="yt-overlays-root" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }} />
        {!firstPaintReady ? (
          <div style={{ position: 'absolute', inset: 0, background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11 }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.15)', borderTopColor: '#fff', animation: 'dse-spin 0.9s linear infinite' }} />
            <style>{`@keyframes dse-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', position: 'relative', height: '100vh', marginTop: 0, background: '#000', alignItems: 'stretch' }} onMouseEnter={() => setHoverGallery(true)} onMouseLeave={() => setHoverGallery(false)}>
      <style>{`
        @keyframes dse-gallery-fog-drift-1 {
          0% { transform: translate3d(-16%, -6%, 0) scale(1.02); }
          100% { transform: translate3d(18%, 10%, 0) scale(1.22); }
        }
        @keyframes dse-gallery-fog-drift-2 {
          0% { transform: translate3d(14%, 10%, 0) scale(1.05); }
          100% { transform: translate3d(-18%, -10%, 0) scale(1.26); }
        }
        @keyframes dse-gallery-fog-drift-3 {
          0% { transform: translate3d(8%, -14%, 0) scale(1.0); }
          100% { transform: translate3d(-12%, 16%, 0) scale(1.28); }
        }
        @keyframes dse-gallery-fog-drift-4 {
          0% { transform: translate3d(-10%, 12%, 0) scale(1.02); }
          100% { transform: translate3d(14%, -16%, 0) scale(1.3); }
        }
        @keyframes dse-gallery-fog-drift-5 {
          0% { transform: translate3d(16%, -6%, 0) scale(1.04); }
          100% { transform: translate3d(-22%, 12%, 0) scale(1.34); }
        }
        @keyframes dse-gallery-fog-gust-1 {
          0% { transform: translate3d(-44%, -18%, 0) scale(0.72) rotate(-8deg); opacity: 0; }
          28% { opacity: 0.84; }
          72% { opacity: 0.62; }
          100% { transform: translate3d(52%, 18%, 0) scale(1.38) rotate(12deg); opacity: 0; }
        }
        @keyframes dse-gallery-fog-gust-2 {
          0% { transform: translate3d(48%, -22%, 0) scale(0.68) rotate(11deg); opacity: 0; }
          34% { opacity: 0.78; }
          100% { transform: translate3d(-56%, 30%, 0) scale(1.42) rotate(-13deg); opacity: 0; }
        }
        @keyframes dse-gallery-fog-gust-3 {
          0% { transform: translate3d(-26%, 42%, 0) scale(0.74) rotate(6deg); opacity: 0; }
          40% { opacity: 0.66; }
          100% { transform: translate3d(34%, -46%, 0) scale(1.36) rotate(-10deg); opacity: 0; }
        }
        @keyframes dse-gallery-cursor-spin {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.03); }
          100% { transform: rotate(360deg) scale(1); }
        }
        @keyframes dse-gallery-cursor-breathe {
          0% { opacity: 0.62; transform: scale(0.98); }
          100% { opacity: 1; transform: scale(1.03); }
        }
      `}</style>
      {requireRotation && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center',
            color: '#fff',
            background: 'rgba(0, 0, 0, 0.94)',
          }}
        >
          <div style={{ display: 'grid', gap: 12, maxWidth: 420 }}>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Rotate your phone</div>
            <div style={{ fontSize: 18, lineHeight: 1.5, opacity: 0.85 }}>Switch to landscape mode to view the gallery.</div>
          </div>
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', inset: 0, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, padding: 24 }}>
          <div><div style={{ fontWeight: 700, marginBottom: 8 }}>Could not load gallery</div><pre style={{ whiteSpace: 'pre-wrap', maxWidth: 720 }}>{error}</pre></div>
        </div>
      )}
      {!firstPaintReady && (
        <div style={{ position: 'absolute', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.15)', borderTopColor: '#fff', animation: 'dse-spin 0.9s linear infinite' }} />
          <style>{`@keyframes dse-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <div style={{ flex: 1, position: 'relative', transform: `scale(${galleryScale})`, transformOrigin: 'left center', transition: 'transform 300ms ease' }}>
        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
        {galleryFog.fogEnabled && fogRenderOpacity > 0.001 && (
        <div
          style={{
            position: 'absolute',
            inset: '-12%',
            zIndex: 3,
            pointerEvents: 'none',
            overflow: 'hidden',
            opacity: fogRenderOpacity,
            mixBlendMode: 'screen',
            transition: 'opacity 900ms cubic-bezier(0.22, 1, 0.36, 1), mask-image 900ms linear, -webkit-mask-image 900ms linear',
            ...(fogFocusMask
              ? {
                maskImage: fogFocusMask,
                WebkitMaskImage: fogFocusMask,
              }
              : {}),
          }}
        >
          <span
            style={{
              position: 'absolute',
              inset: '-18%',
              background: `radial-gradient(70% 58% at 30% 36%, rgba(${fogPrimaryRgb}, ${0.92 + (0.08 * fogStormFactor)}), rgba(${fogPrimaryRgb}, 0.2) 62%, rgba(${fogPrimaryRgb}, 0) 80%)`,
              filter: `blur(${Math.round((36 + (22 * fogStormFactor)) * fogDensityScale)}px)`,
              animation: fogStill ? 'none' : `dse-gallery-fog-drift-1 ${Math.max(1.4, fogDuration1).toFixed(2)}s ease-in-out infinite alternate`,
              transformOrigin: '40% 52%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-22%',
              background: `radial-gradient(66% 52% at 68% 62%, rgba(${fogSecondaryRgb}, ${0.86 + (0.14 * fogStormFactor)}), rgba(${fogSecondaryRgb}, 0.15) 66%, rgba(${fogSecondaryRgb}, 0) 84%)`,
              filter: `blur(${Math.round((44 + (28 * fogStormFactor)) * fogDensityScale)}px)`,
              animation: fogStill ? 'none' : `dse-gallery-fog-drift-2 ${Math.max(1.8, fogDuration2).toFixed(2)}s ease-in-out infinite alternate`,
              transformOrigin: '58% 44%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-24%',
              background: `radial-gradient(74% 64% at 50% 48%, rgba(${fogPrimaryRgb}, ${0.72 + (0.2 * fogStormFactor)}), rgba(${fogSecondaryRgb}, 0.24) 60%, rgba(${fogSecondaryRgb}, 0) 84%)`,
              filter: `blur(${Math.round((54 + (34 * fogStormFactor)) * fogDensityScale)}px)`,
              animation: fogStill ? 'none' : `dse-gallery-fog-drift-3 ${Math.max(2.1, fogDuration3).toFixed(2)}s ease-in-out infinite alternate`,
              transformOrigin: '52% 48%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-28%',
              background: `radial-gradient(62% 58% at 54% 34%, rgba(${fogSecondaryRgb}, ${0.42 + (0.36 * fogStormFactor)}), rgba(${fogPrimaryRgb}, 0.16) 48%, rgba(${fogPrimaryRgb}, 0) 78%)`,
              filter: `blur(${Math.round((64 + (38 * fogStormFactor)) * fogDensityScale)}px)`,
              animation: fogStill ? 'none' : `dse-gallery-fog-drift-4 ${Math.max(2.6, fogDuration4).toFixed(2)}s ease-in-out infinite alternate`,
              transformOrigin: '46% 56%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-30%',
              background: `radial-gradient(88% 66% at 48% 52%, rgba(${fogPrimaryRgb}, ${0.32 + (0.3 * fogStormFactor)}), rgba(${fogSecondaryRgb}, 0.12) 60%, rgba(${fogSecondaryRgb}, 0) 86%)`,
              filter: `blur(${Math.round((74 + (46 * fogStormFactor)) * fogDensityScale)}px)`,
              animation: fogStill ? 'none' : `dse-gallery-fog-drift-5 ${Math.max(2.9, fogDuration5).toFixed(2)}s ease-in-out infinite alternate`,
              transformOrigin: '50% 50%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-36%',
              background: `radial-gradient(46% 32% at 50% 50%, rgba(${fogPrimaryRgb}, ${0.22 + (0.5 * fogGustOpacity)}), rgba(${fogSecondaryRgb}, 0.12) 48%, rgba(${fogSecondaryRgb}, 0) 80%)`,
              filter: `blur(${fogGustBlurPx}px)`,
              opacity: fogGustOpacity,
              animation: fogStill ? 'none' : `dse-gallery-fog-gust-1 ${Math.max(1.25, fogDuration6).toFixed(2)}s cubic-bezier(0.22, 0.7, 0.2, 1) infinite`,
              animationDelay: '-2.1s',
              transformOrigin: '50% 50%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-40%',
              background: `radial-gradient(42% 30% at 48% 52%, rgba(${fogSecondaryRgb}, ${0.18 + (0.48 * fogGustOpacity)}), rgba(${fogPrimaryRgb}, 0.1) 44%, rgba(${fogPrimaryRgb}, 0) 78%)`,
              filter: `blur(${Math.round(fogGustBlurPx * 1.08)}px)`,
              opacity: fogGustOpacity * 0.9,
              animation: fogStill ? 'none' : `dse-gallery-fog-gust-2 ${Math.max(1.35, fogDuration7).toFixed(2)}s cubic-bezier(0.24, 0.72, 0.24, 1) infinite`,
              animationDelay: '-3.9s',
              transformOrigin: '52% 46%',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: '-44%',
              background: `radial-gradient(52% 34% at 50% 48%, rgba(${fogPrimaryRgb}, ${0.14 + (0.42 * fogGustOpacity)}), rgba(${fogSecondaryRgb}, 0.08) 40%, rgba(${fogSecondaryRgb}, 0) 76%)`,
              filter: `blur(${Math.round(fogGustBlurPx * 1.22)}px)`,
              opacity: fogGustOpacity * 0.74,
              animation: fogStill ? 'none' : `dse-gallery-fog-gust-3 ${Math.max(1.6, fogDuration8).toFixed(2)}s cubic-bezier(0.2, 0.64, 0.18, 1) infinite`,
              animationDelay: '-5.4s',
              transformOrigin: '50% 52%',
            }}
          />
        </div>
      )}
      <div ref={overlayRootRef} id="yt-overlays-root" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }} />
      {fadePhase !== 'hidden' && (
        <div style={{ position: 'absolute', inset: 0, background: '#000', zIndex: 8, opacity: fadePhase === 'fading' ? 0 : 1, transition: 'opacity 2s ease', pointerEvents: 'none' }} />
      )}
      {activeImageTierVisible && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 14,
            transform: 'translateX(-50%)',
            zIndex: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'rgba(0,0,0,0.58)',
            border: `1px solid ${activeImageTierColor}66`,
            borderRadius: 999,
            padding: '6px 12px',
            color: '#f4f4f4',
            boxShadow: `0 0 22px ${activeImageTierColor}33`,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              width: 44,
              height: 24,
              borderRadius: '50%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <span style={{ position: 'absolute', inset: 0, borderRadius: '999px', filter: 'blur(7px)', background: `radial-gradient(circle at 24% 38%, ${activeImageTierColor}cc, rgba(255,255,255,0) 60%), radial-gradient(circle at 58% 56%, rgba(255,255,255,0.76), rgba(255,255,255,0) 58%), radial-gradient(circle at 78% 42%, rgba(255,255,255,0.28), rgba(255,255,255,0) 62%)`, animation: 'dse-gallery-cursor-breathe 2.7s ease-in-out infinite' }} />
          </span>
          <span style={{ display: 'grid', lineHeight: 1.1 }}>
            <strong style={{ color: activeImageTierColor, fontSize: 11, letterSpacing: '0.1em' }}>{activeImageTierLabel}</strong>
            <span style={{ fontSize: 10, opacity: 0.82 }}>{Math.round(activeImageTierProgress * 100)}%</span>
          </span>
        </div>
      )}
      {items[selected]?.kind === 'video' && (
        <div style={{ position: 'absolute', left: '50%', top: activeImageTierVisible ? 58 : 12, transform: 'translateX(-50%)', zIndex: 8, display: 'flex', gap: 8, background: 'rgba(0,0,0,0.55)', padding: '6px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.25)', color: '#fff' }}>
          <button onClick={() => setVideoInteract(v => !v)} style={{ padding: '4px 10px' }}>
            {videoInteract ? 'Drag mode' : 'Interact'}
          </button>
          {items[selected]?.youtubeId ? (
            <>
              <button onClick={togglePlay} style={{ padding: '4px 10px' }}>Play</button>
              <button onClick={togglePause} style={{ padding: '4px 10px' }}>Pause</button>
              <button onClick={toggleMute} style={{ padding: '4px 10px' }}>Mute/Unmute</button>
            </>
          ) : (
            (() => {
              const st = currentLocalVideoState();
              return st ? (
                <>
                  <button onClick={togglePlay} style={{ padding: '4px 10px' }}>{st.paused ? 'Play' : 'Pause'}</button>
                  <button onClick={toggleMute} style={{ padding: '4px 10px' }}>{st.muted ? 'Unmute' : 'Mute'}</button>
                </>
              ) : null;
            })()
          )}
        </div>
      )}
      </div>
      {infoItemOpen && hasSpaceForSideBySide && (
        <div style={{ width: infoPanelWidth, background: 'rgba(0,0,0,0.95)', borderLeft: '1px solid rgba(255,255,255,0.2)', overflowY: 'auto', WebkitOverflowScrolling: 'touch', color: '#fff', padding: 20, display: 'grid', gap: 10, zIndex: 5, alignContent: 'start' }}>
          <button type='button' onClick={() => setInfoItemIndex(null)} style={{ justifySelf: 'end', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Close</button>
          <h3 style={{ margin: 0 }}>{selectedItemTitle}</h3>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{selectedItemDescription}</div>
          {selectedCreatedYear ? <div style={{ fontSize: 13, opacity: 0.86 }}><strong>Created year:</strong> {selectedCreatedYear}</div> : null}
          {selectedCreatedByText ? <div style={{ fontSize: 13, opacity: 0.86 }}><strong>Created by:</strong> {selectedCreatedByText}</div> : null}
          {selectedForSale ? (
            <div style={{ display: 'grid', gap: 6, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: 10, background: 'rgba(0,0,0,0.45)' }}>
              <div><strong>For sale</strong></div>
              <div style={{ fontSize: 12 }}>Type: {selectedSaleType}</div>
              {selectedSalePriceNok ? <div style={{ fontSize: 12 }}>Price: {formatNok(selectedSalePriceNok)}</div> : null}
              {selectedSaleType === 'T-shirt' && selectedSaleSizes.length ? <div style={{ fontSize: 12 }}>Sizes: {selectedSaleSizes.join(', ')}</div> : null}
              {selectedSaleText ? <div style={{ fontSize: 12 }}>{selectedSaleText}</div> : null}
              {!isMerchandiseMode ? (
                <button type="button" onClick={() => { setShowPurchaseModal(true); setOrderMessage(''); setOrderForm(initialOrderForm); }} style={{ justifySelf: 'start', border: '1px solid #fff', borderRadius: 999, background: '#000', color: '#fff', padding: '8px 12px', cursor: 'pointer', fontSize: 12 }}>Place Order</button>
              ) : null}
            </div>
          ) : (
            <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: 10, background: 'rgba(0,0,0,0.45)' }}>Not for sale</div>
          )}
        </div>
      )}
      {infoItemOpen && !hasSpaceForSideBySide && (
        <div onClick={() => setInfoItemIndex(null)} style={{ position: 'absolute', inset: 0, zIndex: 9, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(92vw, 420px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 12, background: 'rgba(0,0,0,0.82)', color: '#fff', padding: 16, display: 'grid', gap: 10, maxHeight: '80vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <h3 style={{ margin: 0 }}>{selectedItemTitle}</h3>
            <div style={{ whiteSpace: 'pre-wrap' }}>{selectedItemDescription}</div>
            {selectedCreatedYear ? <div style={{ fontSize: 13, opacity: 0.86 }}><strong>Created year:</strong> {selectedCreatedYear}</div> : null}
            {selectedCreatedByText ? <div style={{ fontSize: 13, opacity: 0.86 }}><strong>Created by:</strong> {selectedCreatedByText}</div> : null}
            {selectedForSale ? (
              <div style={{ display: 'grid', gap: 6, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: 10, background: 'rgba(0,0,0,0.45)' }}>
                <div><strong>For sale</strong></div>
                <div>Type: {selectedSaleType}</div>
                {selectedSalePriceNok ? <div>Price: {formatNok(selectedSalePriceNok)}</div> : null}
                {selectedSaleType === 'T-shirt' && selectedSaleSizes.length ? <div>Sizes: {selectedSaleSizes.join(', ')}</div> : null}
                {selectedSaleText ? <div>{selectedSaleText}</div> : null}
                {!isMerchandiseMode ? (
                  <button type="button" onClick={() => { setShowPurchaseModal(true); setOrderMessage(''); setOrderForm(initialOrderForm); }} style={{ justifySelf: 'start', border: '1px solid #fff', borderRadius: 999, background: '#000', color: '#fff', padding: '8px 12px', cursor: 'pointer' }}>Place Order</button>
                ) : null}
              </div>
            ) : (
              <div style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: 10, background: 'rgba(0,0,0,0.45)' }}>Not for sale</div>
            )}
          </div>
        </div>
      )}

      {showPurchaseModal && infoItem && (
        <div onClick={closePurchaseModal} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(1100px, 96vw)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 12, border: '1px solid rgba(255,255,255,0.24)', background: '#0d0d0d', color: '#fff', padding: 'clamp(16px, 3vw, 50px)', display: 'grid', gap: 10, boxSizing: 'border-box' }}>
            <h3 style={{ margin: 0 }}>Order {selectedItemTitle}</h3>
            {selectedItemPreviewSrc ? (
              <div style={{ display: 'grid', justifyItems: 'center', gap: 6 }}>
                <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #2e2e2e', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4 }}>
                  <img
                    src={selectedItemPreviewSrc}
                    alt={selectedItemTitle}
                    style={{ height: 120, width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                  />
                </div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>{selectedItemTitle}</div>
                <div style={{ fontSize: 12, opacity: 0.85, whiteSpace: 'pre-wrap', textAlign: 'center', maxWidth: 420 }}>{selectedItemDescription}</div>
              </div>
            ) : null}
            <form onSubmit={submitPurchaseOrder} style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                <input
                  value={orderForm.customerFirstName}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, customerFirstName: e.target.value }))}
                  placeholder="First name"
                  autoComplete="given-name"
                  required
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '6px 8px', boxSizing: 'border-box' }}
                />
                <input
                  value={orderForm.customerLastName}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, customerLastName: e.target.value }))}
                  placeholder="Last name"
                  autoComplete="family-name"
                  required
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '6px 8px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                <input
                  type="email"
                  value={orderForm.customerEmail}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, customerEmail: e.target.value }))}
                  placeholder="Email"
                  autoComplete="email"
                  required
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '6px 8px', boxSizing: 'border-box' }}
                />
                <input
                  value={orderForm.customerPhone}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, customerPhone: e.target.value }))}
                  placeholder="Phone (optional)"
                  autoComplete="tel"
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '6px 8px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                <input
                  value={orderForm.deliveryAddress}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, deliveryAddress: e.target.value }))}
                  placeholder="Delivery address"
                  autoComplete="street-address"
                  required
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
                />
                <input
                  value={orderForm.deliveryCity}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, deliveryCity: e.target.value }))}
                  placeholder="City"
                  autoComplete="address-level2"
                  required
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
                />
                <input
                  value={orderForm.deliveryCountry}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, deliveryCountry: e.target.value }))}
                  placeholder="Country"
                  autoComplete="country-name"
                  required
                  style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
                />
              </div>
              <input
                value={orderForm.deliveryPostcode}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, deliveryPostcode: e.target.value }))}
                placeholder="Postcode"
                autoComplete="postal-code"
                required
                style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
              />
              {selectedSaleType === 'T-shirt' ? (
                selectedSaleSizes.length ? (
                  <select
                    value={orderForm.requestedSize}
                    onChange={(e) => setOrderForm((prev) => ({ ...prev, requestedSize: e.target.value }))}
                    required
                    style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
                  >
                    <option value="">Select size</option>
                    {selectedSaleSizes.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={orderForm.requestedSize}
                    onChange={(e) => setOrderForm((prev) => ({ ...prev, requestedSize: e.target.value }))}
                    placeholder="Size (e.g. M)"
                    required
                    style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
                  />
                )
              ) : null}
              <input
                type="number"
                min="1"
                value={orderForm.quantity}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, quantity: e.target.value }))}
                placeholder="Quantity"
                style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
              />
              <textarea
                rows={3}
                value={orderForm.message}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, message: e.target.value }))}
                placeholder="Message (optional)"
                style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" onClick={closePurchaseModal} style={{ borderRadius: 8, border: '1px solid #444', background: '#111', color: '#fff', padding: '8px 11px', cursor: 'pointer' }}>Close</button>
                <button type="submit" disabled={sendingOrder} style={{ borderRadius: 8, border: '1px solid #fff', background: '#000', color: '#fff', padding: '8px 11px', cursor: 'pointer' }}>{sendingOrder ? 'Sending…' : 'Submit Order'}</button>
              </div>
            </form>
            {orderMessage ? <div style={{ marginTop: 4, fontSize: '0.92rem', opacity: 0.95 }}>{orderMessage}</div> : null}
          </div>
        </div>
      )}
      {isMerchandiseMode ? (
        <div style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 1200, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => {
              if (!variantGroups.length) {
                addConfiguredMerchToCart();
                return;
              }
              setShowMerchVariantModal(true);
            }}
            style={{ borderRadius: 999, border: '1px solid #fff', background: '#000', color: '#fff', padding: '10px 16px', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.45)', fontFamily: 'Philosopher, serif' }}
          >
            LEGG TIL HANDLEVOGN
          </button>
          <button
            type="button"
            onClick={() => setShowCartModal(true)}
            style={{ borderRadius: 999, border: '1px solid #fff', background: '#0a0a0a', color: '#fff', padding: '10px 16px', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.45)' }}
          >
            HANDLEVOGN ({totalCartItems})
          </button>
        </div>
      ) : null}

      {isMerchandiseMode && showMerchVariantModal && merchandiseMeta ? (
        <div
          onClick={() => setShowMerchVariantModal(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1250, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(620px, 94vw)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.26)', background: '#0d0d0d', color: '#fff', padding: 16, display: 'grid', gap: 10 }}
          >
            <h3 style={{ margin: 0 }}>{merchandiseMeta.title || 'Merchandise'}</h3>
            {variantGroups.length ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {variantGroups.map((group, groupIndex) => {
                  const previousGroups = variantGroups.slice(0, groupIndex);
                  const groupEnabled = groupIndex === 0 || previousGroups.every((entry) => String(selectedVariantValues[entry.title] || '').trim());
                  const isLastGroup = groupIndex === variantGroups.length - 1;
                  const prefixSelections = variantGroups
                    .slice(0, groupIndex)
                    .map((entry) => ({ title: entry.title, value: String(selectedVariantValues[entry.title] || '').trim() }))
                    .filter((entry) => entry.title && entry.value);
                  const canResolveLastGroupStock = isLastGroup && prefixSelections.length === groupIndex;

                  return (
                    <label key={group.title} style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontSize: 13, opacity: 0.9 }}>{group.title}</span>
                      <select
                        disabled={!groupEnabled}
                        value={selectedVariantValues[group.title] || ''}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setSelectedVariantValues((prev) => {
                            const next = { ...prev, [group.title]: nextValue };
                            variantGroups.slice(groupIndex + 1).forEach((laterGroup) => {
                              next[laterGroup.title] = '';
                            });
                            return next;
                          });
                        }}
                        style={{ width: '100%', background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 8, padding: '8px 10px', opacity: groupEnabled ? 1 : 0.65 }}
                      >
                        <option value="">Velg {group.title.toLowerCase()}</option>
                        {group.options.map((opt) => {
                          const optionStock = canResolveLastGroupStock
                            ? variantStockRows
                              .filter((row) => rowMatchesSelection(row, [...prefixSelections, { title: group.title, value: opt }]))
                              .reduce((sum, row) => sum + variantRowAvailableStock(row), 0)
                            : null;
                          const statusText = optionStock == null
                            ? ''
                            : formatAvailabilityStatus(optionStock);
                          return (
                            <option
                              key={`${group.title}-${opt}`}
                              value={opt}
                              disabled={canResolveLastGroupStock && optionStock <= 0}
                            >
                              {statusText ? `${opt} - ${statusText}` : opt}
                            </option>
                          );
                        })}
                      </select>
                      {isLastGroup && canResolveLastGroupStock && String(selectedVariantValues[group.title] || '').trim() ? (
                        <div style={{ fontSize: 12, fontWeight: 700, color: selectedCombinationAvailableStock > 0 ? '#9dffca' : '#ffb3b3' }}>
                          {formatAvailabilityStatus(selectedCombinationAvailableStock)}
                        </div>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div style={{ opacity: 0.82 }}>Ingen varianter er paakrevd for dette produktet.</div>
            )}
            {missingVariantTitles.length ? <div style={{ fontSize: 12, opacity: 0.82 }}>Velg: {missingVariantTitles.join(', ')}</div> : null}
            {!missingVariantTitles.length && selectedCombinationAvailableStock <= 0 ? (
              <div style={{ fontSize: 12, color: '#ffb3b3' }}>IKKE PÅ LAGER, LANG LEVERINGSTID</div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowMerchVariantModal(false)}
                style={{ borderRadius: 8, border: '1px solid #444', background: '#111', color: '#fff', padding: '8px 11px', cursor: 'pointer' }}
              >
                Lukk
              </button>
              <button
                type="button"
                disabled={!canAddConfiguredMerchToCart}
                onClick={() => {
                  addConfiguredMerchToCart();
                  setShowMerchVariantModal(false);
                }}
                style={{ borderRadius: 10, border: '1px solid rgba(255,255,255,0.45)', background: '#000', color: '#fff', padding: '10px 14px', fontFamily: 'Philosopher, serif', cursor: canAddConfiguredMerchToCart ? 'pointer' : 'not-allowed', opacity: canAddConfiguredMerchToCart ? 1 : 0.6 }}
              >
                LEGG TIL HANDLEVOGN
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isMerchandiseMode && cartFeedback ? (
        <div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1200, borderRadius: 999, border: '1px solid rgba(108,255,159,0.7)', background: 'rgba(6,58,24,0.88)', color: '#e4ffe8', padding: '8px 14px' }}>
          {cartFeedback}
        </div>
      ) : null}

      {isMerchandiseMode ? (
        <MerchandiseCartModal
          open={showCartModal}
          cartItems={cartItems}
          setCartItems={setCartItems}
          shippingCostNok={Number(merchandiseMeta?.shippingCostNok || 75) || 75}
          shippingCostInternationalNok={Number(merchandiseMeta?.shippingCostInternationalNok || 150) || 150}
          onClose={() => setShowCartModal(false)}
          onOrderCompleted={() => navigate('/')}
        />
      ) : null}

      {showObjectViewer && viewedObjectIndex !== null && items[viewedObjectIndex] ? (
        <GalleryObjectViewer
          item={items[viewedObjectIndex]}
          itemIndex={viewedObjectIndex}
          galleryId={galleryId}
          isSquareCanvas={items[viewedObjectIndex]?.box?.square}
          isTextBox={isTextBoxGalleryItem(items[viewedObjectIndex])}
          onClose={() => setShowObjectViewer(false)}
        />
      ) : null}

      {!isMerchandiseMode && items[selected] ? (
        <AnimatedTerminalShell
          isOpen={showTerminalPanel}
          onOpenChange={setShowTerminalPanel}
          buttonLabel="TERMINAL"
          buttonTitleOpen="Skjul terminal"
          buttonTitleClosed="Vis terminal"
          buttonAriaOpen="Skjul terminal"
          buttonAriaClosed="Vis terminal"
          buttonStyle={{
            position: 'fixed',
            left: GALLERY_TERMINAL_BUTTON_LEFT,
            bottom: GALLERY_TERMINAL_BUTTON_BOTTOM,
            width: galleryTerminalButtonWidth,
            height: GALLERY_TERMINAL_BUTTON_HEIGHT,
            fontSize: 14,
            letterSpacing: 1.2,
            borderRadius: 10,
            textTransform: 'uppercase',
          }}
          panelStyle={{
            position: 'fixed',
            left: GALLERY_TERMINAL_PANEL_LEFT,
            top: galleryTerminalPanelTop,
          }}
          collapsedPanelStyle={{
            position: 'fixed',
            left: GALLERY_TERMINAL_BUTTON_LEFT,
            top: galleryTerminalButtonTop,
          }}
          baseWidth={GALLERY_TERMINAL_PANEL_WIDTH}
          baseHeight={GALLERY_TERMINAL_PANEL_HEIGHT}
          expandScale={2}
          expandSettleMs={360}
          buttonZIndex={26}
          panelZIndex={25}
        >
          {({ isExpanded, toggleExpand, requestClose }) => (
            <div
              style={{
                borderRadius: 14,
                border: terminalInfoIntroFlashActive
                  ? '1px solid rgba(255,255,255,0.96)'
                  : '1px solid rgba(255,255,255,0.22)',
                background: terminalInfoIntroFlashActive
                  ? 'linear-gradient(180deg, rgba(255,255,255,0.99), rgba(244,244,244,0.97))'
                  : 'linear-gradient(180deg, rgba(7,7,10,0.95), rgba(3,3,4,0.95))',
                color: terminalPanelTextColor,
                padding: 12,
                display: 'grid',
                gap: 8,
                minHeight: 280,
                boxShadow: terminalInfoIntroFlashActive
                  ? '0 0 36px rgba(255,255,255,0.98), 0 0 124px rgba(255,255,255,0.94), inset 0 0 18px rgba(255,255,255,0.8)'
                  : '0 18px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
                backdropFilter: terminalInfoIntroFlashActive ? 'blur(18px)' : 'blur(4px)',
                WebkitBackdropFilter: terminalInfoIntroFlashActive ? 'blur(18px)' : 'blur(4px)',
                transition: 'border-color 0.28s ease, background 0.28s ease, box-shadow 0.28s ease, color 0.28s ease, backdrop-filter 0.28s ease',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, opacity: 0.76 }}>DEBOTS TERMINAL</div>
                  <div style={{ fontSize: 10, opacity: 0.62 }}>{formatTerminalTimestamp(Date.now())}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={toggleExpand}
                    title={isExpanded ? 'Minimer terminal' : 'Utvid terminal'}
                    aria-label={isExpanded ? 'Minimer terminal' : 'Utvid terminal'}
                    style={{
                      borderRadius: 8,
                      border: terminalInfoIntroFlashActive
                        ? '1px solid rgba(28,28,28,0.28)'
                        : '1px solid rgba(255,255,255,0.3)',
                      background: terminalInfoIntroFlashActive
                        ? 'rgba(255,255,255,0.82)'
                        : 'rgba(14,14,18,0.8)',
                      color: terminalPanelTextColor,
                      width: 34,
                      height: 30,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    {isExpanded ? (
                      <span aria-hidden style={{ display: 'inline-block', width: 14, height: 14, position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 1, right: 1, bottom: 3, height: 2, borderRadius: 2, background: 'currentColor' }} />
                      </span>
                    ) : (
                      <span aria-hidden style={{ display: 'inline-block', width: 14, height: 14, position: 'relative' }}>
                        <span
                          style={{
                            position: 'absolute',
                            right: 1,
                            top: 1,
                            width: 7,
                            height: 7,
                            borderTop: '2px solid currentColor',
                            borderRight: '2px solid currentColor',
                            boxSizing: 'border-box',
                          }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            left: 2,
                            bottom: 3,
                            width: 9,
                            height: 2,
                            borderRadius: 2,
                            background: 'currentColor',
                            transform: 'rotate(-45deg)',
                            transformOrigin: 'left center',
                          }}
                        />
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => requestClose(false)}
                    style={{
                      borderRadius: 8,
                      border: terminalInfoIntroFlashActive
                        ? '1px solid rgba(28,28,28,0.28)'
                        : '1px solid rgba(255,255,255,0.3)',
                      background: terminalInfoIntroFlashActive
                        ? 'rgba(255,255,255,0.82)'
                        : 'rgba(14,14,18,0.8)',
                      color: terminalPanelTextColor,
                      padding: '6px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    Lukk
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleOpenSelectedItemInfo}
                  disabled={!canShowSelectedItemInfo}
                  aria-pressed={!!focusedInfoVisible}
                  style={{
                    borderRadius: 8,
                    border: focusedInfoVisible ? '1px solid #0f5f2b' : '1px solid #2d8dff',
                    background: focusedInfoVisible ? 'rgba(26,126,58,0.86)' : 'rgba(26,88,206,0.58)',
                    color: '#fff',
                    padding: '8px 11px',
                    cursor: canShowSelectedItemInfo ? 'pointer' : 'not-allowed',
                    opacity: canShowSelectedItemInfo ? 1 : 0.45,
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    width: '100%',
                    transition: 'border-color 0.5s ease, background-color 0.5s ease, box-shadow 0.5s ease',
                    boxShadow: focusedInfoVisible
                      ? '0 0 0 1px rgba(16,86,43,0.35), inset 0 1px 0 rgba(255,255,255,0.2)'
                      : '0 0 0 1px rgba(37,118,214,0.28), inset 0 1px 0 rgba(255,255,255,0.24)',
                  }}
                >
                  {focusedInfoVisible
                    ? `SKJUL INFORMASJON OM ${activeGalleryItemTitle}`
                    : `MER INFORMASJON OM ${activeGalleryItemTitle}`}
                </button>
              </div>

              <div
                ref={terminalLogsRef}
                style={{
                  borderRadius: 10,
                  border: terminalInfoIntroFlashActive
                    ? '1px solid rgba(255,255,255,0.92)'
                    : '1px solid rgba(255,255,255,0.15)',
                  background: terminalInfoIntroFlashActive
                    ? 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.96), rgba(248,248,248,0.94) 60%, rgba(240,240,240,0.96))'
                    : 'radial-gradient(circle at 50% 0%, rgba(20,26,30,0.45), rgba(4,7,10,0.82) 60%, rgba(2,2,3,0.95))',
                  boxShadow: terminalInfoIntroFlashActive
                    ? '0 0 42px rgba(255,255,255,0.9), inset 0 0 20px rgba(255,255,255,0.78)'
                    : 'none',
                  minHeight: 126,
                  maxHeight: 190,
                  overflowY: 'auto',
                  padding: '10px 12px',
                  lineHeight: 1.5,
                  fontSize: 11,
                  display: 'grid',
                  gap: 4,
                }}
              >
                {terminalLines.map((line) => {
                  const stamp = formatTerminalTimestamp(line.createdAt);
                  const rawText = String(line.text || '');
                  const elapsedMs = Math.max(0, terminalTypewriterNow - Number(line.createdAt || 0));
                  const typedChars = Math.floor((elapsedMs / 1000) * TERMINAL_TYPEWRITER_CHARS_PER_SEC);
                  const animatedSource = rawText.replace(/[^a-zA-Z0-9 ]+/g, '');
                  const typedText = typedChars >= rawText.length
                    ? rawText
                    : animatedSource.slice(0, Math.min(animatedSource.length, Math.max(0, typedChars)));

                  if (line.speaker === 'user') {
                    return (
                      <div key={line.id} style={{ color: terminalPanelTextColor }}>
                        [{terminalUserLabel} | {stamp} | {typedText}]
                      </div>
                    );
                  }

                  if (line.speaker === 'bot') {
                    return (
                      <div key={line.id} style={{ color: terminalPanelTextColor }}>
                        [{line.botName || 'DEBOT'} | {stamp} |: {typedText}]
                      </div>
                    );
                  }

                  return (
                    <div key={line.id} style={{ color: terminalPanelTextColor }}>
                      [{line.botName || 'SYSTEM'} | {stamp} | {typedText}]
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                {terminalFaqOptions.length > 0 ? (
                  <select
                    value={terminalFaqSelection}
                    onChange={(event) => handleTerminalFaqSelect(event.target.value)}
                    disabled={!(selectedTerminalBot && selectedTerminalBot.online)}
                    style={{
                      borderRadius: 8,
                      border: terminalInfoIntroFlashActive
                        ? '1px solid rgba(28,28,28,0.18)'
                        : '1px solid rgba(255,255,255,0.25)',
                      background: terminalInfoIntroFlashActive ? 'rgba(255,255,255,0.96)' : '#09090b',
                      color: terminalPanelTextColor,
                      padding: '8px 9px',
                      opacity: selectedTerminalBot && selectedTerminalBot.online ? 1 : 0.65,
                    }}
                  >
                    <option value="">FAQ</option>
                    {terminalFaqOptions.map((faq) => (
                      <option key={faq.question} value={faq.question}>{faq.question}</option>
                    ))}
                  </select>
                ) : null}

                <button
                  type="button"
                  onClick={handleTerminalThruster}
                  style={{
                    borderRadius: 9,
                    border: '1px solid rgba(239,68,68,0.92)',
                    background: 'linear-gradient(160deg, rgba(16,6,6,0.95), rgba(10,3,3,0.96))',
                    color: '#fff',
                    padding: '8px 10px',
                    cursor: 'pointer',
                    fontWeight: 700,
                    letterSpacing: 0.9,
                    textTransform: 'uppercase',
                  }}
                >
                  THRUSTER
                </button>
              </div>
            </div>
          )}
        </AnimatedTerminalShell>
      ) : null}
      {canLeft ? (
        <button
          type="button"
          onClick={() => nudge('left', { allowTextScroll: false })}
          title="Previous"
          aria-label="Previous"
          style={{ ...navArrowBaseStyle, left: 22 }}
        >
          ❮
        </button>
      ) : null}
      {canRight ? (
        <button
          type="button"
          onClick={() => nudge('right', { allowTextScroll: false })}
          title={selected >= items.length - 1 ? 'Back to first item' : 'Next'}
          aria-label={selected >= items.length - 1 ? 'Back to first item' : 'Next'}
          style={{ ...navArrowBaseStyle, right: 22 }}
        >
          ❯
        </button>
      ) : null}
      {items.length > 1 && items[selected]?.kind === 'image' ? (
        <div
          style={{
            position: 'absolute',
            bottom: 22,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(0,0,0,0.58)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 8,
            padding: '8px 12px',
            color: '#f4f4f4',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.05em' }}>
            {selected + 1} / {items.length}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PhotoGallery3D;
